import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CombatMap, CombatEntity, HighlightedCell, MapBackgroundTheme, MapWeatherType } from '../../types/interfaces';
import { SPRITE_COLS, SPRITE_ROWS } from '../../utils/sprites';
import { drawSpriteFrame, type SpriteSheetCfg } from '../../utils/spriteFrame';
import { drawParallaxBackground } from './MapBackground';
import { Compass } from './Compass';
import { CanvasWeatherEffect } from './CanvasWeatherEffect';
import styles from './CombatGrid.module.css';
import {
  useAmbientMovement,
  useCharacterMovementConfig,
  drawBird,
  drawEagle,
  type AmbientNpcSpec,
} from '../../systems/movement';
import { keyOutWhite } from '../../utils/chromaKey';

// ─────────────────────────────────────────────────────────────────────────────
// Tile constants
// ─────────────────────────────────────────────────────────────────────────────

const TILE_W      = 64;
const TILE_H      = 32;
const TILE_H_HALF = TILE_H / 2;   // 16
const TILE_W_HALF = TILE_W / 2;   // 32

// Animação de caminhada casa-a-casa: parâmetros vêm de
// `public/data/movement/character_movement.json` via useCharacterMovementConfig
// (com DEFAULT_CHARACTER_MOVEMENT como reserva). Nada de constante fixa aqui.
// Zoom travado — apenas pelos botões +/- da UI (sem scroll do mouse ou pinça),
// com limites rígidos de escala para o mapa nunca ficar desproporcional.
const MIN_ZOOM  = 0.8;
const MAX_ZOOM  = 1.3;
const ZOOM_STEP = 0.1;

const COLORS = {
  walkable:        '#5fa843',
  fullObstacle:    '#2a2a2a',
  visionObstacle:  '#555533',
  tree:            '#1f4d1f',
  mountain:        '#4b4b4b',
  rock:            '#6a6a6a',
  lake:            '#2a5a8a',
  high_ground:     '#3a5e3a',
  // ── Falésias de Auto-Tiling — Montanha (elevação positiva, encosta rochosa) ──
  // Tons de pedra/rocha entalhada (cinza-acastanhado), não mais "caixa marrom
  // reta": cada face recebe gradiente + estratos na hora do desenho (ver
  // drawFrame) para dar volume tridimensional real de falésia.
  high_ground_L:   '#b39a72',   // face virada para a luz (esquerda na tela) — arenito claro
  high_ground_R:   '#a58c66',   // (legado) face norte
  high_ground_E:   '#86704f',   // face na sombra (direita na tela) — arenito escuro
  high_ground_S:   '#6a583e',   // (legado) face sul
  soil_seam:       '#4a3b22',   // faixa fina de terra/raízes onde a grama encontra a rocha
  peak:            '#a89c85',   // tingimento do topo no nível mais alto (Cume da Montanha/pedra clara)
  // ── Paredes de Auto-Tiling — Buraco/Vala (elevação negativa, parede interna) ──
  pit_wall_L:      '#221c14',
  pit_wall_R:      '#2a231a',
  pit_wall_E:      '#1a150f',
  pit_wall_S:      '#0f0c08',   // mais escura — fundo do buraco, longe da luz
  pit_floor:       '#241d15',   // tingimento do piso no fundo do buraco (terra/lama escura)
  ramp:            '#8b5a2b',   // usado como tom de terra batida no centro da trilha (ver drawRampTile)
  // ── Chão Batido (piso `floor: 'dirt'`) — ver drawDirtTop ──
  dirt_dark:       '#a86f47',
  dirt_light:      '#c98d5c',
  dirt_border:     'rgba(90, 52, 26, 0.12)',
  dirt_shade:      '#6e4526',   // sombra sob a borda ondulada de grama
  // ── Grama viva — ver drawGrassDetails ──
  grass_light:     '#7cc456',
  grass_dark:      '#4a9136',
  grass_blade:     '#2f6e2a',
  grass_tip:       '#a6dc72',
  // ── Via de Subida (Ponto A → Ponto B) — conectores caminháveis ──
  stairs_tread:    '#c08a52',   // degrau de madeira clara (escada de mão)
  stairs_riser:    '#6a4222',   // haste de madeira escura
  rope:            '#a9814a',   // corda de escalada (fibra clara)
  rope_dark:       '#5a4326',   // nó/sombra da corda
  // Linhas de grade — suavizadas para 18% de opacidade (Limpeza Visual do
  // Grid): o "xadrez" duro de antes some, mas a grade continua legível o
  // suficiente para orientação tática. O contorno forte (`borderHover`) só
  // aparece na célula sob o mouse/seleção, reforçando via destaque em vez de
  // linhas permanentes.
  border:           'rgba(18, 28, 16, 0.08)',
  borderHover:      'rgba(255, 245, 190, 0.85)',
  highlight_mov:    'rgba(80, 180, 255, 0.45)',
  highlight_spell:  'rgba(255, 120, 40, 0.45)',
  highlight_aoe:    'rgba(255, 50, 50, 0.50)',
  hover:            'rgba(255, 255, 100, 0.35)',
  player:           '#4488ff',
  monster:          '#ff4444',
  text:             '#ffffff',
  shadow:           'rgba(0,0,0,0.55)',
} as const;

const SPRITE_DRAW_H = 64;   // draw height in canvas pixels (before zoom)

// ─────────────────────────────────────────────────────────────────────────────
// Sistema de Elevação de Terreno (Plataformas Elevadas/Montanhas E Buracos/Valas)
// ─────────────────────────────────────────────────────────────────────────────

const ELEV_UNIT = 28;  // px de altura por nível — aumentado (era 16) para dar impacto visual real de profundidade/elevação, sem ficar "achatado"
const ELEV_MAX  = 2;   // Nível +2 = Cume da Montanha (mais alto)
const ELEV_MIN  = -2;  // Nível -2 = fundo de um Buraco/Vala (mais baixo)

/**
 * Retorna o nível de elevação (-2 a +2) de uma célula.
 * Compatível com mapas legados: 'High_Ground' sem `elevation` explícito vira nível 1.
 */
function getElevation(cell?: { type?: string; elevation?: number } | null): number {
  if (!cell) return 0;
  if (typeof cell.elevation === 'number') {
    return Math.max(ELEV_MIN, Math.min(ELEV_MAX, Math.round(cell.elevation)));
  }
  return cell.type === 'High_Ground' ? 1 : 0;
}

/**
 * Tipos decorativos de cenário (Montanha, Árvore, Rocha, Vegetação densa)
 * que usam SEMPRE a técnica de Billboarding — nunca entram no bloco/prisma
 * isométrico de elevação (piso alto + paredes), mesmo que a célula guarde
 * um `elevation` (ex.: Montanha herda nível 2 do pincel do admin). Ficam
 * ancorados num piso plano (nível 0 visual) com o doodad desenhado reto,
 * sem nunca esticar/achatar pela projeção isométrica do chão em nenhum
 * ângulo de câmera. Padrão a seguir para qualquer objeto grande futuro
 * (castelos, torres, estátuas): sempre billboard, nunca bloco.
 */
const BILLBOARD_PROP_TYPES = new Set(['Mountain', 'Tree', 'tree', 'Rock', 'Vision_Obstacle']);

/**
 * Elevação usada para GEOMETRIA/RENDER (piso e paredes). Igual a
 * `getElevation`, exceto para os tipos de Billboard acima, sempre tratados
 * como nível 0 no desenho — a altura "de dados" deles (colisão/alcance em
 * useCombat/MovementEngine) não muda, só deixa de esculpir a malha 3D do chão.
 */
function getVisualElevation(cell?: { type?: string; elevation?: number } | null): number {
  if (cell && BILLBOARD_PROP_TYPES.has(cell.type ?? '')) return 0;
  // Lago é sempre água no chão — altura em lago (dado antigo) é ignorada.
  if (cell && (cell.type === 'Lake' || cell.type === 'water')) return 0;
  return getElevation(cell);
}

/**
 * Mapa "plano": nenhuma célula tem elevação VISUAL nem rampa — logo
 * `buildTerrainLayer` não gera nenhuma parede. Nesse caso o piso é 100%
 * estático por ângulo/zoom e pode ser cacheado (ver drawFrame). Resultado
 * memoizado por referência do objeto `map` (que o Hub já memoiza).
 */

/**
 * Cópia do mapa (memoizada por referência) em que células da Rampa antiga
 * (`type: 'Ramp'`) viram terreno caminhável comum no mesmo nível — só para
 * DESENHO. A regra de passagem (canStepBetween / backend) continua lendo o
 * mapa original, então a rampa antiga segue liberando a subida.
 */
const _rampFreeMaps = new WeakMap<object, CombatMap>();
function withoutLegacyRamps(map: CombatMap): CombatMap {
  if (!map.cells.some((row) => row.some((c) => c?.type === 'Ramp'))) return map;
  let out = _rampFreeMaps.get(map);
  if (!out) {
    out = { ...map, cells: map.cells.map((row) => row.map((c) => (c?.type === 'Ramp' ? { ...c, type: 'Walkable' } : c))) };
    _rampFreeMaps.set(map, out);
  }
  return out;
}

/** id estável por referência do objeto `map` — muda quando o mapa é recriado
 *  (ex.: admin editou o Hub), invalidando o cache do piso. */
const _mapId = new WeakMap<object, number>();
let _mapIdSeq = 0;
function mapId(m: object): number {
  let id = _mapId.get(m);
  if (id === undefined) { id = ++_mapIdSeq; _mapId.set(m, id); }
  return id;
}

const _flatMapCache = new WeakMap<object, boolean>();
function isFlatMap(map: { cells: Array<Array<{ type?: string; elevation?: number } | null | undefined>> }): boolean {
  const cached = _flatMapCache.get(map as object);
  if (cached !== undefined) return cached;
  let flat = true;
  outer: for (const row of map.cells) {
    if (!row) continue;
    for (const cell of row) {
      if (cell && (getVisualElevation(cell) !== 0 || cell.type === 'Ramp')) { flat = false; break outer; }
    }
  }
  _flatMapCache.set(map as object, flat);
  return flat;
}

/** Mistura duas cores hex (0-1 = peso de b). */
function mixHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const ar = (pa >> 16) & 255, ag = (pa >> 8) & 255, ab = pa & 255;
  const br = (pb >> 16) & 255, bg = (pb >> 8) & 255, bb = pb & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  // Sempre #rrggbb — assim o resultado pode voltar a entrar no mixHex (encadeável).
  return '#' + ((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1);
}

/** Interpola linearmente entre dois pontos de tela (t=0 → a, t=1 → b). */
function lerpPt(a: [number, number], b: [number, number], t: number): [number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

type ClimbConnCell = { climb_connection?: { targetX: number; targetY: number; type: 'RAMP' | 'STAIRS' | 'ROPE' } | null } | null | undefined;

/**
 * Via de Subida (Ponto A → Ponto B): resolve o tipo de conector (Rampa/
 * Escada/Corda) para a fronteira entre duas células vizinhas, checando a
 * `climb_connection` de QUALQUER uma das duas pontas (gravada simetricamente
 * pela ferramenta do admin, mas checar as duas cobre dados legados/parciais).
 * Retorna `undefined` quando não há conexão — a fronteira vira muro sólido.
 */
function findClimbConnectorType(
  cell: ClimbConnCell, cx: number, cy: number,
  neighborCell: ClimbConnCell, nx: number, ny: number,
): 'RAMP' | 'STAIRS' | 'ROPE' | undefined {
  const ownConn = cell?.climb_connection;
  if (ownConn && ownConn.targetX === nx && ownConn.targetY === ny) return ownConn.type;
  const nbConn = neighborCell?.climb_connection;
  if (nbConn && nbConn.targetX === cx && nbConn.targetY === cy) return nbConn.type;
  return undefined;
}

type ScreenCorner = [number, number];

/**
 * Desenha o topo (piso) de uma célula de terreno — chão padrão (Nível 0) ou
 * plataforma elevada (Nível 1/2) — sempre 100% sólido, cobrindo toda a área
 * da célula (Camada de Superfície: elimina qualquer transparência/"buraco").
 * As paredes verticais de sustentação (Auto-Tiling) são desenhadas à parte,
 * no vetor ordenado por profundidade (ver Layer 3 em drawFrame), para que
 * personagens ocluam/sejam ocluídos corretamente por elas.
 */
/**
 * Cor do piso para um nível de elevação — rampa de cor CONTÍNUA (não mais um
 * corte binário grama/pedra), criando uma transição suave e realista entre o
 * chão base e o patamar mais alto (montanha) ou mais baixo (buraco/vala).
 */
function elevationTopColor(elevation: number): string {
  if (elevation > 0) {
    // Topo da Montanha: piso de grama IDÊNTICO ao chão base (mesma cor
    // exata de `walkable`) — a elevação em si já é comunicada pela altura,
    // sombra de borda e pelas encostas de pedra ao redor; o topo nunca vira
    // pedra/terra, fica assentado sobre a rocha da encosta como um platô
    // verde real.
    return COLORS.walkable;
  }
  if (elevation < 0) {
    const t = Math.min(1, Math.abs(elevation) / Math.abs(ELEV_MIN));
    return mixHex(COLORS.walkable, COLORS.pit_floor, t * 0.85);
  }
  return COLORS.walkable;
}

function drawTopFace(
  ctx: CanvasRenderingContext2D,
  corners: ScreenCorner[],
  elevation: number,
) {
  const H = elevation * ELEV_UNIT;
  const top: ScreenCorner[] = corners.map(([x, y]) => [x, y - H]);

  ctx.beginPath();
  ctx.moveTo(top[0][0], top[0][1]);
  ctx.lineTo(top[1][0], top[1][1]);
  ctx.lineTo(top[2][0], top[2][1]);
  ctx.lineTo(top[3][0], top[3][1]);
  ctx.closePath();
  ctx.fillStyle = elevationTopColor(elevation);
  ctx.fill();
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 0.8;
  ctx.stroke();
}

// ── Revestimento do Piso: Grama viva + Chão Batido ──────────────────────────
// Puramente visual (colisão/LOS seguem `cell.type`). Tudo é determinístico
// pela semente (gx, gy) — o mesmo tile sempre tem os mesmos tufos/pedrinhas,
// sem "piscar" entre frames; só o balanço do vento varia com `now`.

/** Semente pseudo-aleatória estável (0-1) para a célula (gx, gy) + canal k. */
function tileRand(gx: number, gy: number, k: number): number {
  const s = Math.sin(gx * 127.1 + gy * 311.7 + k * 74.7) * 43758.5453;
  return s - Math.floor(s);
}

/** Ponto de tela dentro do topo do tile — (u, v) ∈ [0,1]² sobre c0→c1 (u) e c0→c3 (v). */
function tilePoint(top: ScreenCorner[], u: number, v: number): ScreenCorner {
  return lerpPt(lerpPt(top[0], top[1], u), lerpPt(top[3], top[2], u), v);
}

function liftedCorners(corners: ScreenCorner[], elevation: number): ScreenCorner[] {
  const H = elevation * ELEV_UNIT;
  return corners.map(([x, y]) => [x, y - H]);
}

/** Tamanho de referência do tile na tela (maior diagonal) — escala com zoom/rotação. */
function tileSpan(top: ScreenCorner[]): number {
  return Math.max(
    Math.hypot(top[2][0] - top[0][0], top[2][1] - top[0][1]),
    Math.hypot(top[3][0] - top[1][0], top[3][1] - top[1][1]),
  );
}

/**
 * Acumula num path os fios de um tufo de capim em (u, v). O balanço usa a
 * posição no mapa na fase, então o vento "corre" pelo campo em ondas.
 */
function addGrassTuft(
  ctx: CanvasRenderingContext2D, top: ScreenCorner[], u: number, v: number,
  span: number, seed: number, sway: number,
) {
  const [bx, by] = tilePoint(top, u, v);
  const h = span * (0.07 + seed * 0.04);
  for (let b = -1; b <= 1; b++) {
    const lean = b * h * 0.7 + sway * h * (0.6 + seed * 0.4);
    ctx.moveTo(bx + b * span * 0.008, by);
    ctx.quadraticCurveTo(bx + b * span * 0.01, by - h * 0.5, bx + lean, by - h * (b === 0 ? 1 : 0.7));
  }
}

/**
 * Ruído de valor suave (0-1) no espaço do mapa — interpolação bilinear com
 * smoothstep entre sementes nos pontos inteiros. Em baixa frequência os
 * vizinhos têm tons quase iguais, então as manchas atravessam vários tiles
 * em vez de formar um "xadrez".
 */
function smoothNoise(x: number, y: number, k: number): number {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = x - ix, fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const a = tileRand(ix, iy, k),     b = tileRand(ix + 1, iy, k);
  const c = tileRand(ix, iy + 1, k), d = tileRand(ix + 1, iy + 1, k);
  return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
}

/** Tom da grama na célula (gx, gy) — mesma cor usada pela borda da trilha. */
function grassColorAt(gx: number, gy: number): string {
  const n = smoothNoise(gx / 5, gy / 5, 7) * 0.7 + smoothNoise(gx / 2, gy / 2, 8) * 0.3;
  return n > 0.5
    ? mixHex(COLORS.walkable, COLORS.grass_light, (n - 0.5) * 1.6)
    : mixHex(COLORS.walkable, COLORS.grass_dark, (0.5 - n) * 1.6);
}

function fillTile(ctx: CanvasRenderingContext2D, top: ScreenCorner[]) {
  ctx.beginPath();
  ctx.moveTo(top[0][0], top[0][1]);
  for (let i = 1; i < 4; i++) ctx.lineTo(top[i][0], top[i][1]);
  ctx.closePath();
}

/** Grama viva: tom contínuo pelo mapa, poucos tufos em "V" ao vento e flores raras. */
function drawGrassDetails(
  ctx: CanvasRenderingContext2D, corners: ScreenCorner[], elevation: number,
  gx: number, gy: number, now: number,
) {
  const top  = liftedCorners(corners, elevation);
  const span = tileSpan(top);

  fillTile(ctx, top);
  ctx.fillStyle = grassColorAt(gx, gy);
  ctx.fill();
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 0.8;
  ctx.stroke();

  if (span < 12) return; // muito afastado — detalhe invisível, poupa o frame

  // ~30% dos tiles ficam lisos — menos ruído visual.
  const r0 = tileRand(gx, gy, 1);
  const tufts = r0 < 0.3 ? 0 : r0 < 0.75 ? 1 : 2;
  if (tufts > 0) {
    const wind = Math.sin(now / 900 + gx * 0.55 + gy * 0.35);
    ctx.lineCap = 'round';
    // Duas passadas: haste escura, depois ponta clara um pouco menor.
    const passes: Array<[string, number, number]> = [
      [COLORS.grass_blade, 1, 0.018],
      [COLORS.grass_tip, 0.55, 0.014],
    ];
    for (const [color, scale, width] of passes) {
      ctx.beginPath();
      for (let i = 0; i < tufts; i++) {
        const u = 0.2 + tileRand(gx, gy, 10 + i) * 0.6;
        const v = 0.2 + tileRand(gx, gy, 20 + i) * 0.6;
        addGrassTuft(ctx, top, u, v, span * scale, tileRand(gx, gy, 30 + i), wind * 0.3);
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(1.2, span * width);
      ctx.stroke();
    }
  }

  // Flores raras (~8% dos tiles).
  if (tileRand(gx, gy, 2) < 0.08) {
    const palette = ['#f4e27a', '#ffffff', '#e89ab8'];
    const color = palette[Math.floor(tileRand(gx, gy, 3) * palette.length)];
    const n = 2 + Math.floor(tileRand(gx, gy, 4) * 2);
    for (let i = 0; i < n; i++) {
      const [fx, fy] = tilePoint(top, 0.2 + tileRand(gx, gy, 50 + i) * 0.6, 0.2 + tileRand(gx, gy, 60 + i) * 0.6);
      const r = Math.max(1.2, span * 0.016);
      ctx.fillStyle = 'rgba(30, 70, 25, 0.5)';
      ctx.beginPath();
      ctx.arc(fx + r * 0.4, fy - span * 0.02 + r * 0.5, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(fx, fy - span * 0.02, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * Chão batido (trilha): terra alaranjada com tom contínuo e poucas pedrinhas.
 * `grassEdges` = lados (n/e/s/w) que encostam em grama no mesmo nível — ali a
 * grama avança sobre a terra numa borda ondulada ("fofa"), com sombra por baixo.
 */
function drawDirtTop(
  ctx: CanvasRenderingContext2D, corners: ScreenCorner[], elevation: number,
  gx: number, gy: number, _now: number,
  grassEdges: { n: boolean; e: boolean; s: boolean; w: boolean },
) {
  const top  = liftedCorners(corners, elevation);
  const span = tileSpan(top);

  fillTile(ctx, top);
  const tone = smoothNoise(gx / 3, gy / 3, 9);
  ctx.fillStyle = mixHex(COLORS.dirt_dark, COLORS.dirt_light, 0.3 + tone * 0.5);
  ctx.fill();
  ctx.strokeStyle = COLORS.dirt_border;
  ctx.lineWidth = 0.8;
  ctx.stroke();

  if (span < 12) return;

  // Pedrinhas/torrões (0-2), bem discretos.
  const pebbles = Math.floor(tileRand(gx, gy, 5) * 3);
  for (let i = 0; i < pebbles; i++) {
    const [px, py] = tilePoint(top, 0.2 + tileRand(gx, gy, 80 + i) * 0.6, 0.2 + tileRand(gx, gy, 90 + i) * 0.6);
    const r = span * (0.012 + tileRand(gx, gy, 100 + i) * 0.012);
    ctx.fillStyle = 'rgba(110, 66, 36, 0.55)';
    ctx.beginPath();
    ctx.ellipse(px, py, r * 1.4, r * 0.8, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Borda ondulada de grama nos lados que encostam em grama.
  const sides: Array<[boolean, number, number, (t: number) => [number, number]]> = [
    [grassEdges.n, gx, gy - 1, (t) => [t, 0]],
    [grassEdges.e, gx + 1, gy, (t) => [1, t]],
    [grassEdges.s, gx, gy + 1, (t) => [t, 1]],
    [grassEdges.w, gx - 1, gy, (t) => [0, t]],
  ];
  if (!sides.some(([on]) => on)) return;

  ctx.save();
  fillTile(ctx, top);
  ctx.clip(); // bolhas e sombra ficam só dentro da trilha

  const BUMPS = 5;
  const rx = span * 0.085;
  const ry = rx * 0.6; // achatado pela projeção isométrica
  const bumpsOf = (side: number, at: (t: number) => [number, number]) =>
    Array.from({ length: BUMPS + 1 }, (_, i) => {
      const [bx, by] = tilePoint(top, ...at(i / BUMPS));
      const k = 1 + (tileRand(gx, gy, 160 + side * 8 + i) - 0.5) * 0.5;
      return [bx, by, k] as const;
    });

  // 1ª passada: sombra (deslocada para baixo); 2ª: a grama por cima.
  for (const pass of ['shade', 'grass'] as const) {
    sides.forEach(([on, nx, ny, at], side) => {
      if (!on) return;
      ctx.fillStyle = pass === 'shade' ? COLORS.dirt_shade : grassColorAt(nx, ny);
      const dy = pass === 'shade' ? Math.max(1.5, span * 0.018) : 0;
      ctx.beginPath();
      for (const [bx, by, k] of bumpsOf(side, at)) {
        ctx.moveTo(bx + rx * k, by + dy);
        ctx.ellipse(bx, by + dy, rx * k, ry * k, 0, 0, Math.PI * 2);
      }
      ctx.fill();
    });
  }
  ctx.restore();
}

/** Piso com revestimento visível (grama/terra): nem vazio, nem água, nem rampa, nem obstáculo. */
function hasSurfaceFloor(cell: { type: string } | null | undefined): boolean {
  return !!cell && !['Full_Obstacle', 'Lake', 'water', 'Ramp'].includes(cell.type);
}

/**
 * Fundo de um Buraco/Vala (elevação negativa): a MESMA superfície plana da
 * `drawTopFace`, mas com sombra de Ambient Occlusion radial — mais escura
 * junto às paredes internas (bordas do bloco) e mais clara no centro, onde
 * menos luz é bloqueada pelas paredes ao redor. Contraste fica CONTIDO
 * dentro do próprio buraco (nunca vaza pro chão base ao redor).
 */
function drawPitFloor(
  ctx: CanvasRenderingContext2D,
  corners: ScreenCorner[],
  elevation: number,
) {
  const H = elevation * ELEV_UNIT;
  const top: ScreenCorner[] = corners.map(([x, y]) => [x, y - H]);
  const cx = (top[0][0] + top[2][0]) / 2;
  const cy = (top[0][1] + top[2][1]) / 2;

  const edgeColor   = elevationTopColor(elevation);
  const centerColor = mixHex(COLORS.walkable, edgeColor, 0.35);

  ctx.beginPath();
  ctx.moveTo(top[0][0], top[0][1]);
  ctx.lineTo(top[1][0], top[1][1]);
  ctx.lineTo(top[2][0], top[2][1]);
  ctx.lineTo(top[3][0], top[3][1]);
  ctx.closePath();

  const grad = ctx.createRadialGradient(cx, cy, 1, cx, cy, TILE_W_HALF);
  grad.addColorStop(0, centerColor);
  grad.addColorStop(1, edgeColor);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 0.8;
  ctx.stroke();
}

/**
 * Linha de Contorno de Relevo (Cliff Edge Outline) + Sombra de Borda —
 * desenhada em QUALQUER aresta onde o vizinho tem uma elevação DIFERENTE
 * (mais alta OU mais baixa que a própria célula), para que o jogador
 * identifique instantaneamente a mudança de nível (ex.: Nível 2 ao lado do
 * Nível 1, ou 0 ao lado de -1). Duas partes:
 *  1. Contorno nítido — sempre desenhado, dos dois lados da fronteira (cada
 *     célula risca a própria aresta na sua própria altura).
 *  2. Sombra de borda — uma faixa CURTA e estreita perto da aresta, só do
 *     lado mais BAIXO da fronteira (quem recebe a sombra projetada pelo
 *     vizinho mais alto). Nunca alcança o centro do bloco — mantém o topo
 *     das plataformas elevadas limpo (Zero Contraste Interno), só marca a
 *     borda em si.
 */
function drawCliffEdgeOutline(
  ctx: CanvasRenderingContext2D,
  corners: ScreenCorner[],
  edgeA: number,
  edgeB: number,
  ownElevation: number,
  neighborElevation: number,
  strength: number = 1,
) {
  const H = ownElevation * ELEV_UNIT;
  const pA: ScreenCorner = [corners[edgeA][0], corners[edgeA][1] - H];
  const pB: ScreenCorner = [corners[edgeB][0], corners[edgeB][1] - H];

  // 1. Contorno nítido — mais visível que a linha de grade padrão (18%),
  // marca claramente que esta é uma fronteira de mudança de nível.
  ctx.beginPath();
  ctx.moveTo(pA[0], pA[1]);
  ctx.lineTo(pB[0], pB[1]);
  ctx.strokeStyle = 'rgba(15,15,10,0.5)';
  ctx.lineWidth = 1.3;
  ctx.stroke();

  // 2. Sombra de borda — só no lado mais baixo, faixa curta (não passa de
  // ~22% do caminho até o centro do bloco).
  if (neighborElevation > ownElevation) {
    const cx = (corners[0][0] + corners[2][0]) / 2;
    const cy = (corners[0][1] + corners[2][1]) / 2 - H;
    const grad = ctx.createLinearGradient(pA[0], pA[1], cx, cy);
    grad.addColorStop(0,    `rgba(0,0,0,${0.30 * strength})`);
    grad.addColorStop(0.22, 'rgba(0,0,0,0)');
    grad.addColorStop(1,    'rgba(0,0,0,0)');

    ctx.beginPath();
    ctx.moveTo(pA[0], pA[1]);
    ctx.lineTo(pB[0], pB[1]);
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();
  }
}

type RampGeometry = {
  low: number;
  high: number;
  downKey: 'n' | 'e' | 's' | 'w';
  dA: number; dB: number; // índices dos cantos da aresta BAIXA
  uA: number; uB: number; // índices dos cantos da aresta ALTA
};

/**
 * Geometria compartilhada de uma célula "Ramp": detecta, a partir dos 4
 * vizinhos ortogonais, qual aresta é a "baixa" (rampa desce para lá) e qual é
 * a "alta" (encosta no platô). Usada tanto para desenhar o piso inclinado
 * quanto para fechar as paredes laterais expostas da rampa (ver loop
 * principal em drawFrame) — extraída para as duas partes ficarem sempre
 * consistentes entre si (mesma fonte de verdade para as alturas dos cantos).
 */
function computeRampGeometry(ownElevation: number, neighborElev: { n: number; e: number; s: number; w: number }): RampGeometry {
  const dirs: Array<{ key: 'n' | 'e' | 's' | 'w'; edge: [number, number] }> = [
    { key: 'n', edge: [0, 1] },
    { key: 'e', edge: [1, 2] },
    { key: 's', edge: [2, 3] },
    { key: 'w', edge: [3, 0] },
  ];
  let low = ownElevation;
  let downDir = dirs[0];
  for (const d of dirs) {
    if (neighborElev[d.key] < low) {
      low = neighborElev[d.key];
      downDir = d;
    }
  }
  const high = Math.max(ownElevation, neighborElev.n, neighborElev.e, neighborElev.s, neighborElev.w);
  const upEdgeMap: Record<string, [number, number]> = { n: [2, 3], e: [3, 0], s: [0, 1], w: [1, 2] };
  const [dA, dB] = downDir.edge;
  const [uA, uB] = upEdgeMap[downDir.key];
  return { low, high, downKey: downDir.key, dA, dB, uA, uB };
}

/** Altura (em px) do canto `idx` de uma célula Ramp, dada sua geometria já calculada. */
function rampCornerHeight(geo: RampGeometry, idx: number, lowH: number, highH: number): number {
  return (idx === geo.dA || idx === geo.dB) ? lowH : highH;
}

/**
 * Desenha uma célula "Ramp" (Rampa/Escada) como um plano inclinado ligando
 * o nível de elevação mais baixo ao mais alto entre os vizinhos ortogonais —
 * é a única transição que permite ao personagem subir/descer plataformas.
 */
function drawRampTile(
  ctx: CanvasRenderingContext2D,
  corners: ScreenCorner[],
  ownElevation: number,
  neighborElev: { n: number; e: number; s: number; w: number },
) {
  const geo = computeRampGeometry(ownElevation, neighborElev);

  if (geo.high === geo.low) {
    // Cercado por vizinhos do mesmo nível — não há inclinação a desenhar,
    // renderiza como um planalto simples (fallback seguro).
    drawTopFace(ctx, corners, ownElevation);
    return;
  }

  const lowH  = geo.low * ELEV_UNIT;
  const highH = geo.high * ELEV_UNIT;
  const heightOf = (idx: number) => rampCornerHeight(geo, idx, lowH, highH);

  const p0: ScreenCorner = [corners[0][0], corners[0][1] - heightOf(0)];
  const p1: ScreenCorner = [corners[1][0], corners[1][1] - heightOf(1)];
  const p2: ScreenCorner = [corners[2][0], corners[2][1] - heightOf(2)];
  const p3: ScreenCorner = [corners[3][0], corners[3][1] - heightOf(3)];

  ctx.beginPath();
  ctx.moveTo(p0[0], p0[1]);
  ctx.lineTo(p1[0], p1[1]);
  ctx.lineTo(p2[0], p2[1]);
  ctx.lineTo(p3[0], p3[1]);
  ctx.closePath();

  // Piso da Rampa: sólido e contínuo, EXATAMENTE o mesmo tom de grama do
  // resto do mapa (sem gradiente marrom/terra) — só um sombreado bem sutil
  // dá volume à inclinação, sem nunca deixar de "ler" como grama.
  const lowMid = lerpPt([p0, p1, p2, p3][geo.dA], [p0, p1, p2, p3][geo.dB], 0.5);
  const highMid = lerpPt([p0, p1, p2, p3][geo.uA], [p0, p1, p2, p3][geo.uB], 0.5);
  const grad = ctx.createLinearGradient(highMid[0], highMid[1], lowMid[0], lowMid[1]);
  grad.addColorStop(0, mixHex(COLORS.walkable, '#ffffff', 0.08));
  grad.addColorStop(1, mixHex(COLORS.walkable, '#000000', 0.10));
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 0.8;
  ctx.stroke();
}

// Direction row indices — clockwise from SE, matching the sprite sheet rows
const DIR_SE = 0;  // dx>0, dy=0  → screen down-right
const DIR_S  = 1;  // dx>0, dy>0  → screen down
const DIR_SW = 2;  // dx=0, dy>0  → screen down-left
const DIR_W  = 3;  // dx<0, dy>0  → screen left
const DIR_NW = 4;  // dx<0, dy=0  → screen up-left
const DIR_N  = 5;  // dx<0, dy<0  → screen up
const DIR_NE = 6;  // dx=0, dy<0  → screen up-right
const DIR_E  = 7;  // dx>0, dy<0  → screen right

// ─────────────────────────────────────────────────────────────────────────────
// Rotation & Coordinates
// ─────────────────────────────────────────────────────────────────────────────

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

// Tipos de célula que BLOQUEIAM caminhada (usado só pelo BFS de reserva do
// frontend — no combate o caminho já vem pronto do MovementEngine no backend).
const WALK_BLOCKING = new Set([
  'Full_Obstacle', 'Vision_Obstacle', 'Tree', 'tree', 'Mountain', 'Rock',
  'Lake', 'water', 'obstacle', 'npc_blacksmith', 'npc_merchant', 'npc_farmer', 'battle_point',
]);

type Cell2 = { x: number; y: number };

/**
 * Pode dar UM passo de (fx, fy) para a vizinha (tx, ty)? Mesmo nível sempre;
 * nível diferente só por Rampa legada ou Via de Subida (climb_connection) que
 * ligue as duas células — mesma regra do MovementEngine::canTraverseElevation
 * (backend, combate) e do HubController::move (Hub).
 */
export function canStepBetween(map: CombatMap, fx: number, fy: number, tx: number, ty: number): boolean {
  const a = map.cells[fy]?.[fx], b = map.cells[ty]?.[tx];
  if (getElevation(a) === getElevation(b)) return true;
  if (a?.type === 'Ramp' || b?.type === 'Ramp') return true;
  const ca = a?.climb_connection, cb = b?.climb_connection;
  return (!!ca && ca.targetX === tx && ca.targetY === ty)
      || (!!cb && cb.targetX === fx && cb.targetY === fy);
}

/**
 * BFS 4-direcional (mesma vizinhança do MovementEngine do backend) para
 * reconstruir o caminho célula-a-célula quando o servidor não mandou um
 * (ex.: clique longe no Hub). Devolve `[origem, ...passos, destino]` ou `null`.
 */
export function bfsPath(
  sx: number, sy: number, dx: number, dy: number,
  map: CombatMap,
  blockedCells: Set<string>,
): Cell2[] | null {
  if (sx === dx && sy === dy) return [{ x: sx, y: sy }];
  const w = map.width, h = map.height;
  const inb = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h;
  const passable = (x: number, y: number) => {
    if (!inb(x, y)) return false;
    const t = map.cells[y]?.[x]?.type;
    if (t === undefined) return false;
    if (WALK_BLOCKING.has(t)) return false;
    if (blockedCells.has(`${x},${y}`) && !(x === dx && y === dy)) return false;
    return true;
  };
  const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  const prev = new Map<string, string | null>();
  prev.set(`${sx},${sy}`, null);
  let frontier: Cell2[] = [{ x: sx, y: sy }];
  let found = false;
  while (frontier.length && !found) {
    const next: Cell2[] = [];
    for (const c of frontier) {
      for (const [ox, oy] of DIRS) {
        const nx = c.x + ox, ny = c.y + oy;
        const key = `${nx},${ny}`;
        if (prev.has(key) || !passable(nx, ny) || !canStepBetween(map, c.x, c.y, nx, ny)) continue;
        prev.set(key, `${c.x},${c.y}`);
        if (nx === dx && ny === dy) { found = true; break; }
        next.push({ x: nx, y: ny });
      }
      if (found) break;
    }
    frontier = next;
  }
  if (!prev.has(`${dx},${dy}`)) return null;
  const path: Cell2[] = [];
  let cur: string | null = `${dx},${dy}`;
  while (cur) {
    const [px, py] = cur.split(',').map(Number);
    path.push({ x: px, y: py });
    cur = prev.get(cur) ?? null;
  }
  return path.reverse();
}

/** Remove células repetidas consecutivas de um caminho. */
export function dedupePath(pts: Cell2[]): Cell2[] {
  const out: Cell2[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) out.push(p);
  }
  return out;
}

const HALF_PI = Math.PI / 2;
const TWO_PI  = Math.PI * 2;

/**
 * Limite de rotação da câmera no modo livre (combate real): -60°/+60°.
 * Dentro deste arco, o jogador NUNCA vê as costas de um pilar de terreno —
 * só as duas faces "da frente" (Sul/Leste) ficam expostas em qualquer
 * ângulo permitido. É essa garantia que permite ao Sistema de Pilares
 * desenhar só 2 paredes verticais por tile (ver `buildTerrainLayer`),
 * em vez de checar as 4 direções.
 */
const ROT_LIMIT = Math.PI / 3; // 60°

/** Normaliza um ângulo em radianos para o intervalo [0, 2π) */
function normalizeAngle(angle: number): number {
  let a = angle % TWO_PI;
  if (a < 0) a += TWO_PI;
  return a;
}

/** Restringe um ângulo em radianos ao arco [-ROT_LIMIT, +ROT_LIMIT]. */
function clampRotation(angle: number): number {
  return Math.max(-ROT_LIMIT, Math.min(ROT_LIMIT, angle));
}

function rotatePoint(x: number, y: number, cx: number, cy: number, angle: number): [number, number] {
  const dx = x - cx;
  const dy = y - cy;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [
    dx * cos - dy * sin,
    dx * sin + dy * cos
  ];
}

function isoProject(rx: number, ry: number, offX: number, offY: number): [number, number] {
  return [
    offX + (rx - ry) * TILE_W_HALF,
    offY + (rx + ry) * TILE_H_HALF
  ];
}

function reverseIso(sx: number, sy: number, offX: number, offY: number): [number, number] {
  const dx = sx - offX;
  const dy = sy - offY;
  return [
    (dy / TILE_H_HALF + dx / TILE_W_HALF) / 2,
    (dy / TILE_H_HALF - dx / TILE_W_HALF) / 2
  ];
}

function reverseRotate(rx: number, ry: number, cx: number, cy: number, angle: number): [number, number] {
  return rotatePoint(rx, ry, 0, 0, -angle).map((v, i) => v + (i===0?cx:cy)) as [number, number];
}

function getDirRow(dx: number, dy: number, cameraAngle: number): number {
  // Rotate the movement vector by the camera angle so the sprite faces the correct screen direction
  const [rx, ry] = rotatePoint(dx, dy, 0, 0, cameraAngle);
  const sx = Math.sign(Math.round(rx));
  const sy = Math.sign(Math.round(ry));
  if (sx ===  1 && sy ===  0) return DIR_SE;
  if (sx ===  1 && sy ===  1) return DIR_S;
  if (sx ===  0 && sy ===  1) return DIR_SW;
  if (sx === -1 && sy ===  1) return DIR_W;
  if (sx === -1 && sy ===  0) return DIR_NW;
  if (sx === -1 && sy === -1) return DIR_N;
  if (sx ===  0 && sy === -1) return DIR_NE;
  return DIR_E;
}



// ─────────────────────────────────────────────────────────────────────────────
// 3D Drawing Helpers
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────

// Escala geral da árvore. As árvores estavam menores que os personagens
// (canopy ~53px vs SPRITE_DRAW_H 64) — ajuste aqui pra deixá-las imponentes.
const TREE_SCALE = 2.0;

function draw3DTree(ctx: CanvasRenderingContext2D, cx: number, cy: number, sway: number, rot: number) {
  const baseCy = cy + TILE_H_HALF;
  const S = TREE_SCALE;

  function p3d(dx: number, dy: number, dz: number) {
    const rx = dx * Math.cos(rot) - dy * Math.sin(rot);
    const ry = dx * Math.sin(rot) + dy * Math.cos(rot);
    return {
      x: cx + (rx - ry) * TILE_W_HALF,
      y: baseCy + (rx + ry) * TILE_H_HALF - dz,
      depth: rx + ry
    };
  }

  // shadow (acompanha a copa maior)
  ctx.beginPath();
  ctx.ellipse(cx, baseCy, 14 * S, 6 * S, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fill();

  const th = 20 * S;
  const trunkW = 3 * S;
  const tb = p3d(0, 0, 0);
  const tt = p3d(0, 0, th);

  // Trunk
  ctx.fillStyle = '#3d2314';
  ctx.beginPath();
  ctx.moveTo(tb.x - trunkW, tb.y);
  ctx.lineTo(tb.x + trunkW, tb.y);
  ctx.lineTo(tt.x + trunkW * 0.66, tt.y);
  ctx.lineTo(tt.x - trunkW * 0.66, tt.y);
  ctx.fill();

  // Leaves — `dz` e `r` escalam; o espalhamento (dx/dy) fica um pouco menor
  // que a escala pra copa não virar uma mancha larga demais no tile.
  const spread = S * 0.75;
  const leaves = [
    { dx: 0, dy: 0, dz: 35 * S, c: '#1b4d1b', r: 18 * S, s: sway },
    { dx: -0.2 * spread, dy: -0.2 * spread, dz: 25 * S, c: '#143d14', r: 15 * S, s: sway * 0.8 },
    { dx: 0.2 * spread, dy: 0.2 * spread, dz: 25 * S, c: '#226622', r: 15 * S, s: sway * 1.2 },
    { dx: -0.2 * spread, dy: 0.2 * spread, dz: 25 * S, c: '#1a551a', r: 15 * S, s: sway * 0.9 },
    { dx: 0.2 * spread, dy: -0.2 * spread, dz: 25 * S, c: '#1e5e1e', r: 15 * S, s: sway * 1.1 },
  ].map(lf => {
    return { ...lf, ...p3d(lf.dx, lf.dy, lf.dz) };
  });

  leaves.sort((a, b) => a.depth - b.depth);

  for (const lf of leaves) {
    ctx.beginPath();
    ctx.arc(lf.x + lf.s, lf.y, lf.r, 0, Math.PI * 2);
    ctx.fillStyle = lf.c;
    ctx.fill();
  }
}

function draw3DMountain(ctx: CanvasRenderingContext2D, cx: number, cy: number, now: number) {
  const baseCy = cy + TILE_H_HALF;
  
  // Rounded mountain using bezier curves
  ctx.beginPath();
  ctx.moveTo(cx - 30, baseCy - 5);
  ctx.quadraticCurveTo(cx - 20, baseCy - 70, cx, baseCy - 85);
  ctx.quadraticCurveTo(cx + 20, baseCy - 70, cx + 30, baseCy - 5);
  ctx.quadraticCurveTo(cx, baseCy + 20, cx - 30, baseCy - 5);
  
  // Gradient fill
  const grad = ctx.createLinearGradient(cx - 30, baseCy - 85, cx + 30, baseCy);
  grad.addColorStop(0, '#5a5a5a');
  grad.addColorStop(1, '#2b2b2b');
  ctx.fillStyle = grad;
  ctx.fill();

  // Snow cap
  ctx.beginPath();
  ctx.moveTo(cx - 15, baseCy - 45);
  ctx.quadraticCurveTo(cx, baseCy - 85, cx + 15, baseCy - 45);
  ctx.quadraticCurveTo(cx, baseCy - 30, cx - 15, baseCy - 45);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  
  // Floating magic stones around the mountain
  const fY = Math.sin(now / 500 + cx) * 5;
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.beginPath(); ctx.ellipse(cx - 25, baseCy - 50 + fY, 6, 2, 0, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(cx + 20, baseCy - 30 - fY, 4, 1.5, 0, 0, Math.PI*2); ctx.fill();
}

function draw3DRock(ctx: CanvasRenderingContext2D, cx: number, cy: number, lx: number, ly: number) {
  const baseCy = cy + TILE_H_HALF;
  
  // seeds para a rocha
  const seed1 = Math.abs(Math.sin(lx * 12.9898 + ly * 78.233)) % 1;
  const numRocks = 1 + Math.floor(seed1 * 3); // 1, 2 ou 3 pedras
  
  const rocks = [];
  for (let i = 0; i < numRocks; i++) {
    const sX = Math.abs(Math.cos(lx * (i+1) * 11.1 + ly * 22.2)) % 1;
    const sY = Math.abs(Math.sin(lx * (i+1) * 33.3 + ly * 44.4)) % 1;
    rocks.push({
      ox: (sX - 0.5) * 16, // Mantido menor para não vazar
      oy: (sY - 0.5) * 8,  // Mantido menor para não vazar
      scale: 0.5 + sX * 0.4 // Escala máxima de 0.9 (menor que antes)
    });
  }

  // Ordena por profundidade (oy) para não sobrepor errado
  rocks.sort((a, b) => a.oy - b.oy);

  // Sombras
  rocks.forEach(r => {
    ctx.beginPath();
    ctx.ellipse(cx + r.ox, baseCy + r.oy, 16 * r.scale, 8 * r.scale, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.fill();
  });

  // Desenha as pedras
  rocks.forEach(r => {
    const rcx = cx + r.ox;
    const rcy = baseCy + r.oy;
    const scale = r.scale;

    // Base jagged rock
    ctx.beginPath();
    ctx.moveTo(rcx - 13 * scale, rcy);
    ctx.lineTo(rcx, rcy + 7 * scale);
    ctx.lineTo(rcx + 15 * scale, rcy - 2 * scale);
    ctx.lineTo(rcx + 8 * scale, rcy - 22 * scale);
    ctx.lineTo(rcx - 4 * scale, rcy - 26 * scale);
    ctx.fillStyle = '#666';
    ctx.fill();
    
    // highlight facet
    ctx.beginPath();
    ctx.moveTo(rcx - 4 * scale, rcy - 26 * scale);
    ctx.lineTo(rcx + 8 * scale, rcy - 22 * scale);
    ctx.lineTo(rcx, rcy + 7 * scale);
    ctx.fillStyle = '#7a7a7a';
    ctx.fill();
    
    // dark facet
    ctx.beginPath();
    ctx.moveTo(rcx - 13 * scale, rcy);
    ctx.lineTo(rcx, rcy + 7 * scale);
    ctx.lineTo(rcx - 4 * scale, rcy - 26 * scale);
    ctx.fillStyle = '#555';
    ctx.fill();
  });
}

type Sides = { n: boolean; e: boolean; s: boolean; w: boolean };

function isLakeCell(c: { type?: string } | null | undefined): boolean {
  return !!c && (c.type === 'Lake' || c.type === 'water');
}

/** Lados (n/e/s/w) da célula que encostam em outro lago — ali a água se emenda. */
function lakeSidesOf(cells: Array<Array<{ type?: string } | null | undefined>>, gx: number, gy: number): Sides {
  return {
    n: isLakeCell(cells[gy - 1]?.[gx]),
    e: isLakeCell(cells[gy]?.[gx + 1]),
    s: isLakeCell(cells[gy + 1]?.[gx]),
    w: isLakeCell(cells[gy]?.[gx - 1]),
  };
}

/**
 * Contorno (em uv do tile) de um retângulo recuado `inset` em cada lado, com
 * cantos arredondados só onde os DOIS lados do canto recuam (margem). Lado
 * com recuo 0 vai até a borda — é o que emenda lagos vizinhos sem costura.
 */
function addUvRoundedRect(
  ctx: CanvasRenderingContext2D, top: ScreenCorner[],
  inset: { n: number; e: number; s: number; w: number }, radius: number,
) {
  const u0 = inset.w, u1 = 1 - inset.e, v0 = inset.n, v1 = 1 - inset.s;
  const corners: Array<[number, number, number, boolean]> = [
    [u0, v0, Math.PI,       inset.w > 0 && inset.n > 0],
    [u1, v0, Math.PI * 1.5, inset.e > 0 && inset.n > 0],
    [u1, v1, 0,             inset.e > 0 && inset.s > 0],
    [u0, v1, Math.PI * 0.5, inset.w > 0 && inset.s > 0],
  ];
  const r = Math.min(radius, (u1 - u0) / 2, (v1 - v0) / 2);
  let first = true;
  const to = (u: number, v: number) => {
    const [x, y] = tilePoint(top, u, v);
    if (first) { ctx.moveTo(x, y); first = false; } else ctx.lineTo(x, y);
  };
  for (const [cu, cv, a0, round] of corners) {
    if (!round || r <= 0) { to(cu, cv); continue; }
    // Centro do arco = canto empurrado r para dentro.
    const ccu = cu + (cu === u0 ? r : -r), ccv = cv + (cv === v0 ? r : -r);
    for (let k = 0; k <= 6; k++) {
      const a = a0 + (k / 6) * (Math.PI / 2);
      to(ccu + Math.cos(a) * r, ccv + Math.sin(a) * r);
    }
  }
  ctx.closePath();
}

/**
 * Lago com a lógica da trilha de terra: lados que encostam em outro lago
 * levam a água até a borda (lagos juntos viram um lago grande e contínuo);
 * lados livres recuam numa margem arredondada de grama + barranco de terra.
 * Um lago sozinho é uma lagoa pequena. Margem "respira" e reflexos brancos
 * deslizam devagar.
 */
function drawLake(
  ctx: CanvasRenderingContext2D, corners: ScreenCorner[], elevation: number,
  gx: number, gy: number, now: number, lake: Sides,
) {
  const top  = liftedCorners(corners, elevation);
  const span = tileSpan(top);

  // Terreno em volta (grama).
  fillTile(ctx, top);
  ctx.fillStyle = grassColorAt(gx, gy);
  ctx.fill();
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 0.8;
  ctx.stroke();

  const breathe = Math.sin(now / 1400 + gx * 0.7 + gy * 0.9) * 0.012;
  const shore = 0.2 + breathe;
  const insetBy = (extra: number) => ({
    n: lake.n ? 0 : shore + extra,
    e: lake.e ? 0 : shore + extra,
    s: lake.s ? 0 : shore + extra,
    w: lake.w ? 0 : shore + extra,
  });

  // Barranco de terra → água rasa (clara) → água funda.
  ctx.beginPath();
  addUvRoundedRect(ctx, top, insetBy(-0.07), 0.3);
  ctx.fillStyle = '#8a5634';
  ctx.fill();

  ctx.beginPath();
  addUvRoundedRect(ctx, top, insetBy(0), 0.24);
  ctx.fillStyle = '#86c6ee';
  ctx.fill();

  ctx.beginPath();
  addUvRoundedRect(ctx, top, insetBy(0.07), 0.18);
  const tone = smoothNoise(gx / 3, gy / 3, 11);
  ctx.fillStyle = mixHex('#3a7fd0', '#4f97e0', tone);
  ctx.fill();

  if (span < 12) return;

  // Reflexos: traços curtos que deslizam e piscam devagar.
  const minU = lake.w ? 0.15 : shore + 0.14, maxU = lake.e ? 0.85 : 1 - shore - 0.14;
  const minV = lake.n ? 0.15 : shore + 0.14, maxV = lake.s ? 0.85 : 1 - shore - 0.14;
  if (maxU <= minU || maxV <= minV) return;
  const glints = 1 + Math.floor(tileRand(gx, gy, 200) * 2);
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(1.2, span * 0.014);
  for (let i = 0; i < glints; i++) {
    const seed = tileRand(gx, gy, 210 + i);
    const alpha = 0.25 + 0.45 * (0.5 + 0.5 * Math.sin(now / 900 + seed * 12));
    const [x, y] = tilePoint(top,
      minU + tileRand(gx, gy, 220 + i) * (maxU - minU),
      minV + tileRand(gx, gy, 230 + i) * (maxV - minV));
    const drift = Math.sin(now / 1600 + seed * 8) * span * 0.025;
    const len = span * (0.05 + seed * 0.04);
    ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
    ctx.beginPath();
    ctx.moveTo(x + drift - len / 2, y);
    ctx.lineTo(x + drift + len / 2, y);
    ctx.stroke();
  }
}

/**
 * Base da ilha flutuante: bloco de terra/rocha pendurado sob as bordas da
 * FRENTE do mapa (as viradas para a câmera, recalculadas a cada rotação).
 * Mais fundo sob o centro, raso nas pontas, fundo irregular e pontudo;
 * claro em cima e escuro embaixo, duas faces com tons diferentes e uma
 * borda de grama escorrendo sobre a rocha.
 */
function drawFloatingIslandBase(
  ctx: CanvasRenderingContext2D, mapW: number, mapH: number,
  rot: number, mapCx: number, mapCy: number, offX: number, offY: number,
) {
  const pts = ([[0, 0], [mapW, 0], [mapW, mapH], [0, mapH]] as const).map(([x, y]) => {
    const [rx, ry] = rotatePoint(x, y, mapCx, mapCy, rot);
    return isoProject(rx, ry, offX, offY);
  });
  let li = 0, ri = 0;
  pts.forEach((p, i) => { if (p[0] < pts[li][0]) li = i; if (p[0] > pts[ri][0]) ri = i; });

  // As duas cadeias de li até ri; a da frente é a de maior y médio.
  const chain = (dir: 1 | -1) => {
    const out = [pts[li]];
    for (let i = li; i !== ri;) { i = (i + dir + 4) % 4; out.push(pts[i]); }
    return out;
  };
  const a = chain(1), b = chain(-1);
  const avgY = (c: ScreenCorner[]) => c.reduce((s, p) => s + p[1], 0) / c.length;
  const front = avgY(a) >= avgY(b) ? a : b;

  const xL = pts[li][0], xR = pts[ri][0];
  const cx = (xL + xR) / 2, half = Math.max(1, (xR - xL) / 2);
  const D = Math.max(40, (mapW + mapH) * TILE_H_HALF * 0.22);
  const step = TILE_W_HALF * 0.6;

  // Amostra cada aresta da frente; profundidade afunila do centro às pontas.
  type Sample = { t: ScreenCorner; b: ScreenCorner };
  const faces: Sample[][] = [];
  let k = 0;
  for (let e = 0; e < front.length - 1; e++) {
    const [p, q] = [front[e], front[e + 1]];
    const n = Math.max(2, Math.ceil(Math.hypot(q[0] - p[0], q[1] - p[1]) / step));
    const face: Sample[] = [];
    for (let i = 0; i <= n; i++) {
      const t = lerpPt(p, q, i / n);
      const s = Math.min(1, Math.abs(t[0] - cx) / half);
      const jag = (hash01(k++) - 0.5) * D * 0.28 + (k % 2 ? D * 0.1 : 0);
      const depth = Math.max(10, D * (1 - Math.pow(s, 1.6)) + jag * (1 - s * 0.7));
      face.push({ t, b: [t[0], t[1] + depth] });
    }
    faces.push(face);
  }

  const topY = Math.min(...front.map((p) => p[1]));
  const botY = Math.max(...faces.flat().map((s) => s.b[1]));
  const palettes: Array<[string, string, string]> = [
    ['#a3623f', '#6b3a24', '#2a1710'], // face esquerda — iluminada
    ['#834b30', '#55301f', '#1f110b'], // face direita — sombra
  ];
  faces.forEach((face, fi) => {
    const [c0, c1, c2] = palettes[Math.min(fi, 1)];
    const g = ctx.createLinearGradient(0, topY, 0, botY);
    g.addColorStop(0, c0);
    g.addColorStop(0.45, c1);
    g.addColorStop(1, c2);
    ctx.beginPath();
    ctx.moveTo(face[0].t[0], face[0].t[1]);
    for (const s of face) ctx.lineTo(s.t[0], s.t[1]);
    for (let i = face.length - 1; i >= 0; i--) ctx.lineTo(face[i].b[0], face[i].b[1]);
    ctx.closePath();
    ctx.fillStyle = g;
    ctx.fill();

    // Rachaduras discretas.
    ctx.strokeStyle = 'rgba(20, 10, 6, 0.3)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 1; i < face.length - 1; i += 3) {
      const { t, b: bt } = face[i];
      const d = bt[1] - t[1];
      const j = (hash01(i * 7 + fi * 31) - 0.5) * step;
      ctx.moveTo(t[0], t[1] + d * 0.25);
      ctx.lineTo(t[0] + j, t[1] + d * 0.55);
    }
    ctx.stroke();
  });

  // Borda de grama escorrendo sobre a rocha (o piso cobre a metade de cima).
  const lip = faces.flat().map((s) => s.t);
  const rx = step * 0.55, ry = rx * 0.6;
  for (const [color, dy] of [['#3a6f2a', ry * 0.7], ['#4a9136', 0]] as const) {
    ctx.fillStyle = color;
    ctx.beginPath();
    lip.forEach(([x, y], i) => {
      const r = 1 + (hash01(i * 3 + 5) - 0.5) * 0.5;
      ctx.moveTo(x + rx * r, y + dy);
      ctx.ellipse(x, y + dy, rx * r, ry * r, 0, 0, Math.PI * 2);
    });
    ctx.fill();
  }
}

/**
 * Rampa de madeira (Via de Subida tipo RAMP), no estilo da referência:
 * laterais de pedra/terra fechando o vão sob o plano, tábuas atravessadas
 * com frestas escuras e vigas nas bordas. `p0/p1` = pé (chão do vizinho),
 * `p3/p2` = topo (borda do platô).
 */
function drawWoodRamp(
  ctx: CanvasRenderingContext2D,
  f: { p0: ScreenCorner; p1: ScreenCorner; p2: ScreenCorner; p3: ScreenCorner; sides?: Array<[ScreenCorner, ScreenCorner, ScreenCorner]>; back?: boolean },
) {
  // Laterais: base de pedra/terra.
  for (const [top, low, foot] of f.sides ?? []) {
    const g = ctx.createLinearGradient(top[0], top[1], foot[0], foot[1]);
    g.addColorStop(0, '#9c8a6c');
    g.addColorStop(1, '#5e4f3a');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.moveTo(...top); ctx.lineTo(...low); ctx.lineTo(...foot); ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = 'rgba(40, 28, 16, 0.45)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Plano das tábuas.
  const at = (u: number, v: number): ScreenCorner => lerpPt(lerpPt(f.p0, f.p1, u), lerpPt(f.p3, f.p2, u), v);
  const g = ctx.createLinearGradient(...at(0.5, 0), ...at(0.5, 1));
  g.addColorStop(0, '#7a4c28');
  g.addColorStop(1, '#a8733f');
  ctx.fillStyle = f.back ? '#a8733f' : g;
  ctx.beginPath(); ctx.moveTo(...f.p0); ctx.lineTo(...f.p1); ctx.lineTo(...f.p2); ctx.lineTo(...f.p3); ctx.closePath();
  ctx.fill();
  // Vista de costas: só uma faixa fina — tábuas claras, sem vigas escuras
  // (que ali liam como uma rachadura no chão).
  if (f.back) {
    ctx.strokeStyle = 'rgba(90, 55, 25, 0.5)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      ctx.beginPath(); ctx.moveTo(...at(0.02, i / 4)); ctx.lineTo(...at(0.98, i / 4)); ctx.stroke();
    }
    return;
  }

  // Tábuas: frestas escuras + fio de luz logo acima de cada uma.
  const planks = 7;
  for (let i = 1; i < planks; i++) {
    const v = i / planks;
    const a = at(0.04, v), b = at(0.96, v);
    ctx.strokeStyle = 'rgba(45, 25, 10, 0.75)';
    ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.moveTo(...a); ctx.lineTo(...b); ctx.stroke();
    ctx.strokeStyle = 'rgba(235, 190, 130, 0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(a[0], a[1] - 1.5); ctx.lineTo(b[0], b[1] - 1.5); ctx.stroke();
  }

  // Vigas nas bordas.
  ctx.strokeStyle = '#4e2f16';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  for (const u of [0.03, 0.97]) {
    ctx.beginPath(); ctx.moveTo(...at(u, 0)); ctx.lineTo(...at(u, 1)); ctx.stroke();
  }
}

/**
 * Detalhes do paredão de arenito (estilo das referências): colunas verticais
 * com volume (faixas claras/escuras de borda irregular, fenda escura entre
 * elas), laje saliente no topo com sombra, rachaduras curtas e uma saia de
 * grama na base. Determinístico pela `seed` da face (célula + lado).
 */
function drawCliffDetails(
  ctx: CanvasRenderingContext2D,
  f: { p0: ScreenCorner; p1: ScreenCorner; p2: ScreenCorner; p3: ScreenCorner; seed?: number; topFloor?: 'grass' | 'dirt' },
) {
  const seed = f.seed ?? 0;
  const r = (k: number) => { const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); };
  // Ponto na face: u ao longo da aresta (0..1), v da base (0) ao topo (1).
  const at = (u: number, v: number): ScreenCorner => lerpPt(lerpPt(f.p0, f.p1, u), lerpPt(f.p3, f.p2, u), v);
  const height = Math.hypot(f.p3[0] - f.p0[0], f.p3[1] - f.p0[1]);
  if (height < 4) return;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(...f.p0); ctx.lineTo(...f.p1); ctx.lineTo(...f.p2); ctx.lineTo(...f.p3); ctx.closePath();
  ctx.clip();

  // ── Colunas: fronteiras com leve ondulação vertical ──
  const cols = 3 + Math.floor(r(1) * 2);
  const bounds: number[] = [0];
  for (let i = 1; i < cols; i++) bounds.push((i + (r(10 + i) - 0.5) * 0.5) / cols);
  bounds.push(1);
  const wave = (i: number, v: number) => (i === 0 || i === cols) ? 0 : Math.sin(v * 5 + r(20 + i) * 6) * 0.025;
  const edge = (i: number) => [0, 0.3, 0.6, 0.9, 1].map((v) => at(bounds[i] + wave(i, v), v));

  for (let i = 0; i < cols; i++) {
    const left = edge(i), right = edge(i + 1).reverse();
    // Cada coluna com tom próprio (algumas salientes/claras, outras recuadas).
    const tone = r(30 + i);
    ctx.fillStyle = tone > 0.5 ? `rgba(255, 238, 200, ${(tone - 0.5) * 0.35})` : `rgba(40, 26, 12, ${(0.5 - tone) * 0.4})`;
    ctx.beginPath();
    ctx.moveTo(...left[0]);
    for (const p of left.slice(1)) ctx.lineTo(...p);
    for (const p of right) ctx.lineTo(...p);
    ctx.closePath();
    ctx.fill();
    // Luz na aresta esquerda da coluna.
    ctx.strokeStyle = 'rgba(255, 244, 215, 0.35)';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    left.forEach((p, k) => (k === 0 ? ctx.moveTo(p[0] + 1.2, p[1]) : ctx.lineTo(p[0] + 1.2, p[1])));
    ctx.stroke();
  }
  // Fendas escuras entre colunas.
  ctx.strokeStyle = 'rgba(35, 22, 10, 0.6)';
  ctx.lineWidth = 1.4;
  ctx.lineJoin = 'round';
  for (let i = 1; i < cols; i++) {
    ctx.beginPath();
    edge(i).forEach((p, k) => (k === 0 ? ctx.moveTo(...p) : ctx.lineTo(...p)));
    ctx.stroke();
  }

  // Rachaduras horizontais curtas dentro das colunas.
  ctx.strokeStyle = 'rgba(35, 22, 10, 0.35)';
  ctx.lineWidth = 1;
  for (let i = 0; i < cols; i++) {
    if (r(40 + i) < 0.4) continue;
    const u0 = bounds[i] + (bounds[i + 1] - bounds[i]) * 0.2, u1 = bounds[i] + (bounds[i + 1] - bounds[i]) * 0.75;
    const v = 0.3 + r(45 + i) * 0.45;
    const p = at(u0, v), q = at(u1, v - 0.04);
    ctx.beginPath(); ctx.moveTo(...p); ctx.lineTo(...q); ctx.stroke();
  }

  // Sombra ambiente na base (terra úmida).
  const g = ctx.createLinearGradient(...at(0.5, 0), ...at(0.5, 0.35));
  g.addColorStop(0, 'rgba(30, 20, 10, 0.4)');
  g.addColorStop(1, 'rgba(30, 20, 10, 0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(...at(0, 0)); ctx.lineTo(...at(1, 0)); ctx.lineTo(...at(1, 0.35)); ctx.lineTo(...at(0, 0.35)); ctx.closePath();
  ctx.fill();

  // ── Laje saliente no topo: faixa clara de borda irregular + sombra sob ela ──
  const lip: ScreenCorner[] = [];
  const steps = 8;
  for (let i = steps; i >= 0; i--) lip.push(at(i / steps, 0.84 - r(50 + i) * 0.09));
  ctx.strokeStyle = 'rgba(30, 20, 10, 0.45)';
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  lip.forEach((p, k) => (k === 0 ? ctx.moveTo(p[0], p[1] + 1.5) : ctx.lineTo(p[0], p[1] + 1.5)));
  ctx.stroke();
  // Saliência da borda: terra (Chão Batido) ou rocha clara sob a grama.
  ctx.fillStyle = f.topFloor === 'dirt' ? 'rgba(176, 122, 79, 0.95)' : 'rgba(232, 210, 162, 0.85)';
  ctx.beginPath();
  ctx.moveTo(...at(0, 1)); ctx.lineTo(...at(1, 1));
  for (const p of lip) ctx.lineTo(...p);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // Topo gramado: a grama escorre pela borda em "bolhas" (com sombra embaixo).
  if (f.topFloor !== 'dirt') {
    const bumps = 6;
    const rx = Math.hypot(f.p2[0] - f.p3[0], f.p2[1] - f.p3[1]) / bumps * 0.62;
    for (const [color, dy] of [['#3a6f2a', 2.2], ['#5fa843', 0]] as const) {
      ctx.fillStyle = color;
      ctx.beginPath();
      for (let i = 0; i <= bumps; i++) {
        const [bx, by] = at(i / bumps, 1);
        const k = 0.8 + r(120 + i) * 0.45;
        ctx.moveTo(bx + rx * k, by + dy);
        ctx.ellipse(bx, by + dy, rx * k, rx * k * 0.55, 0, 0, Math.PI);
      }
      ctx.fill();
    }
  }

  // ── Saia de grama na base (pode passar da face — cobre a emenda com o chão) ──
  const blades = 9;
  ctx.lineCap = 'round';
  for (let i = 0; i < blades; i++) {
    const u = (i + 0.2 + r(60 + i) * 0.6) / blades;
    const [bx, by] = at(u, 0);
    const bw = 3 + r(70 + i) * 3;
    ctx.fillStyle = r(80 + i) > 0.5 ? '#4a9136' : '#5fa843';
    ctx.beginPath();
    ctx.ellipse(bx, by + 1, bw, bw * 0.6, 0, Math.PI, Math.PI * 2);
    ctx.fill();
    // Fios de capim subindo pela rocha.
    const h = 4 + r(90 + i) * 6;
    ctx.strokeStyle = '#3f7d2e';
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.moveTo(bx - 1, by); ctx.lineTo(bx - 2.5, by - h);
    ctx.moveTo(bx + 1, by); ctx.lineTo(bx + 2, by - h * 0.8);
    ctx.stroke();
  }
  // Pedrinhas soltas.
  for (let i = 0; i < 2; i++) {
    const [px, py] = at(0.15 + r(100 + i) * 0.7, 0);
    const pr = 1.8 + r(110 + i) * 1.8;
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath(); ctx.ellipse(px + 1, py + 2, pr * 1.3, pr * 0.7, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#b39f7a';
    ctx.beginPath(); ctx.ellipse(px, py + 1 - pr * 0.4, pr * 1.1, pr * 0.75, 0, 0, Math.PI * 2); ctx.fill();
  }
}

/**
 * Obstáculo no chão (`Full_Obstacle`, nível 0): mesma grama do mapa (sem
 * "buraco" para o céu) com um amontoado de pedras que deixa claro que a
 * célula não é caminhável. Puramente visual — colisão segue `cell.type`.
 */
function drawObstacleGround(
  ctx: CanvasRenderingContext2D, corners: ScreenCorner[], gx: number, gy: number,
) {
  const top  = corners;
  const span = tileSpan(top);

  fillTile(ctx, top);
  ctx.fillStyle = grassColorAt(gx, gy);
  ctx.fill();
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // Terra remexida sob as pedras.
  const [cx, cy] = tilePoint(top, 0.5, 0.5);
  ctx.fillStyle = 'rgba(95, 70, 45, 0.55)';
  ctx.beginPath();
  ctx.ellipse(cx, cy, span * 0.3, span * 0.15, 0, 0, Math.PI * 2);
  ctx.fill();

  // 3 pedras, de trás para a frente na TELA (vale em qualquer rotação).
  const rocks = [0, 1, 2].map((i) => ({
    p: tilePoint(top, 0.3 + tileRand(gx, gy, 300 + i) * 0.4, 0.3 + tileRand(gx, gy, 310 + i) * 0.4),
    r: span * (0.07 + tileRand(gx, gy, 320 + i) * 0.05),
  })).sort((a, b) => a.p[1] - b.p[1]);

  for (const { p: [x, y], r } of rocks) {
    ctx.fillStyle = 'rgba(20, 30, 15, 0.35)';
    ctx.beginPath();
    ctx.ellipse(x + r * 0.2, y + r * 0.25, r * 1.1, r * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();

    const g = ctx.createLinearGradient(x, y - r * 1.2, x, y + r * 0.3);
    g.addColorStop(0, '#b4b9bf');
    g.addColorStop(1, '#6c7178');
    ctx.fillStyle = g;
    ctx.strokeStyle = '#4a4e54';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(x, y - r * 0.45, r, r * 0.75, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.beginPath();
    ctx.ellipse(x - r * 0.35, y - r * 0.8, r * 0.3, r * 0.16, -0.4, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Pseudo-aleatório estável (0-1) por índice. */
function hash01(i: number): number {
  const s = Math.sin(i * 91.345 + 17.17) * 43758.5453;
  return s - Math.floor(s);
}

function drawVisionObstacle(ctx: CanvasRenderingContext2D, cx: number, cy: number, now: number, lx: number, ly: number) {
  const baseCy = cy + TILE_H_HALF;
  
  ctx.save();

  // Sway and movement for the clouds (reduced to stay inside)
  const swayX = Math.sin(now / 800 + lx + ly) * 6;
  const swayY = Math.cos(now / 900 + lx * ly) * 3;

  ctx.translate(cx + swayX, baseCy - 10 + swayY);
  
  // Fog is semi-transparent light blue/white
  ctx.fillStyle = 'rgba(210, 230, 255, 0.75)'; 
  
  // Reduced ellipse sizes so it doesn't bleed out badly
  ctx.beginPath(); ctx.ellipse(0, 0, 16, 9, 0, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(-10, -8, 12, 8, 0, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(10, -6, 11, 7, 0, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(0, -15, 12, 9, 0, 0, Math.PI*2); ctx.fill();
  
  // Add slowly moving mist particles (closer)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  const p1X = Math.cos(now / 500 + lx) * 10;
  const p1Y = Math.sin(now / 400 + ly) * 10;
  ctx.beginPath(); ctx.arc(p1X, p1Y - 8, 2, 0, Math.PI*2); ctx.fill();
  
  const p2X = Math.sin(now / 600 + ly) * 12;
  const p2Y = Math.cos(now / 450 + lx) * 8;
  ctx.beginPath(); ctx.arc(p2X, p2Y - 4, 1.5, 0, Math.PI*2); ctx.fill();

  ctx.restore();
}

/**
 * Altura de desenho (px, antes do zoom) do NPC do Hub — MESMO tamanho do
 * personagem do jogador no Hub: SPRITE_DRAW_H (64) × a escala 1.2 que o
 * HubMap aplica ao player (ver `sprite_config.scale` em HubMap.tsx). A
 * "Escala Visual" do sprite no painel admin multiplica isto.
 */
const NPC_BLACKSMITH_DRAW_H = SPRITE_DRAW_H * 1.2;

/**
 * Linha FIXA do spritesheet do NPC — a direção que "encara" a câmera inicial
 * na projeção isométrica (para baixo/na direção do jogador ao entrar no Hub).
 * 1 = DIR_S. O NPC nunca gira com a câmera; se a folha usar outra ordem de
 * linhas, ajuste só este número (0..rows-1).
 */
const NPC_FACING_ROW = 1;

/**
 * Nomes exibidos ACIMA de cada NPC do Hub (traduções fixas — pt-BR).
 * A chave é o `id` do NPC (ambientNpcs.entityId / papel do backend).
 * O jogo real pode sobrepor via a prop `npcNames` (ex.: i18n com locale).
 */
const NPC_DISPLAY_NAMES: Record<string, string> = {
  blacksmith: 'Ferreiro',
  guardian: 'Guardião',
  merchant: 'Mercadora',
};

/**
 * Desenha uma imagem (NPC do Hub etc.) como billboard sobre o grid —
 * mesmo padrão visual usado para jogadores/monstros (`entity.sprite` mais
 * abaixo neste arquivo): sombra elíptica no chão + imagem ancorada pela base,
 * em vez de geometria "pseudo-3D" desenhada por polígonos.
 */
function drawPropBillboard(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  cx: number,
  groundY: number,
  drawH: number,
  label?: string,
  /** Recorte de origem — usado quando `img` é um spritesheet e só um frame deve aparecer. */
  src?: { sx: number; sy: number; sw: number; sh: number },
) {
  const iw = src ? src.sw : img.width;
  const ih = src ? src.sh : img.height;
  const drawW = Math.round(drawH * (iw / ih));

  ctx.beginPath();
  ctx.ellipse(cx, groundY + 3, drawW * 0.32, 10, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fill();

  const drawX = cx - drawW / 2;
  const drawY = groundY - drawH;
  ctx.drawImage(img, src ? src.sx : 0, src ? src.sy : 0, iw, ih, drawX, drawY, drawW, drawH);

  if (label) {
    ctx.font = '10px sans-serif';
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.strokeStyle = 'rgba(0,0,0,0.8)';
    ctx.lineWidth = 2;
    ctx.strokeText(label, cx, drawY - 4);
    ctx.fillText(label, cx, drawY - 4);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Portal de Batalha — arco de pedra medieval + vórtice sombrio + Guardião
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Vórtice SOMBRIO dentro da abertura do arco — misterioso, não "festa de luz".
 * `pts` são os 4 cantos do plano do portal já projetados na tela:
 * [baixo-esq, baixo-dir, cima-dir, cima-esq]. Tudo é recortado (clip) ao
 * quadrilátero e alinhado aos eixos dele, então acompanha a rotação da câmera.
 */
function drawPortalVortex(ctx: CanvasRenderingContext2D, pts: { x: number; y: number }[], now: number) {
  const t = now / 1000;
  const cx = (pts[0].x + pts[1].x + pts[2].x + pts[3].x) / 4;
  const cy = (pts[0].y + pts[1].y + pts[2].y + pts[3].y) / 4;
  const axX = (pts[1].x - pts[0].x) / 2, axY = (pts[1].y - pts[0].y) / 2;
  const ayX = (pts[3].x - pts[0].x) / 2, ayY = (pts[3].y - pts[0].y) / 2;
  const hw = Math.max(6, Math.hypot(axX, axY));
  const hh = Math.max(6, Math.hypot(ayX, ayY));
  const ang = Math.atan2(axY, axX);
  const cos = Math.cos(ang), sin = Math.sin(ang);
  const proj = (u: number, v: number) => ({ x: cx + u * cos - v * sin, y: cy + u * sin + v * cos });

  ctx.save();
  ctx.beginPath();
  pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
  ctx.closePath();
  ctx.clip();

  // Vazio profundo — quase preto, leve tom arroxeado no centro
  const base = ctx.createRadialGradient(cx, cy, 1, cx, cy, Math.max(hw, hh) * 1.25);
  base.addColorStop(0, '#1c1030');
  base.addColorStop(0.55, '#0c0616');
  base.addColorStop(1, '#020106');
  ctx.fillStyle = base;
  ctx.beginPath(); ctx.ellipse(cx, cy, hw * 1.3, hh * 1.3, ang, 0, Math.PI * 2); ctx.fill();

  // Bandas escuras girando lentamente (sombra líquida)
  for (let i = 0; i < 5; i++) {
    const f = i / 5;
    const spin = t * (0.18 + i * 0.05) * (i % 2 ? 1 : -1);
    const r = (1 - f) * 0.86 + 0.12;
    ctx.strokeStyle = `rgba(${8 + i * 6},${4 + i * 3},${14 + i * 8},${0.55 - f * 0.35})`;
    ctx.lineWidth = (1 - f) * 6 + 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, hw * r * (1 + Math.sin(t + i) * 0.04), hh * r, ang + spin, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Um par de fios de energia tênues (roxo/ciano frio), pouca opacidade
  ctx.globalCompositeOperation = 'lighter';
  for (let arm = 0; arm < 2; arm++) {
    ctx.strokeStyle = arm ? 'rgba(90,150,210,0.16)' : 'rgba(150,90,220,0.18)';
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    for (let s = 0; s <= 1.001; s += 0.06) {
      const th = arm * Math.PI + s * 5.5 + t * (0.5 + arm * 0.2);
      const p = proj(Math.cos(th) * s * hw * 0.92, Math.sin(th) * s * hh * 0.92);
      s === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
  }

  // Brilho interno FRACO e pulsante (não branco-quente)
  const pulse = 0.45 + Math.sin(t * 1.6) * 0.2;
  const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, hw * 0.6);
  core.addColorStop(0, `rgba(150,110,210,${(0.28 * pulse).toFixed(3)})`);
  core.addColorStop(1, 'rgba(60,30,110,0)');
  ctx.fillStyle = core;
  ctx.beginPath(); ctx.ellipse(cx, cy, hw * 0.62, hh * 0.62, ang, 0, Math.PI * 2); ctx.fill();

  // Névoa subindo de dentro do portal
  ctx.globalCompositeOperation = 'source-over';
  for (let m = 0; m < 6; m++) {
    const seed = m * 41.7;
    const life = (t * 0.22 + m * 0.17) % 1;
    const mx = (Math.sin(seed) * 0.5) * hw;
    const my = hh * 0.9 - life * hh * 1.9;
    const p = proj(mx + Math.sin(t + seed) * 4, my);
    const a = Math.sin(life * Math.PI) * 0.14;
    const rad = 6 + life * 16;
    const mg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad);
    mg.addColorStop(0, `rgba(120,105,140,${a.toFixed(3)})`);
    mg.addColorStop(1, 'rgba(120,105,140,0)');
    ctx.fillStyle = mg;
    ctx.beginPath(); ctx.arc(p.x, p.y, rad, 0, Math.PI * 2); ctx.fill();
  }

  // Glints raros
  ctx.globalCompositeOperation = 'lighter';
  for (let p = 0; p < 5; p++) {
    const seed = p * 91.3;
    const orbit = (t * 0.25 + p * 0.2) % 1;
    const rad = 0.2 + orbit * 0.7;
    const th = seed + t * 0.8;
    const pt = proj(Math.cos(th) * rad * hw * 0.85, Math.sin(th) * rad * hh * 0.85);
    const tw = Math.max(0, Math.sin((t + seed) * 3));
    ctx.fillStyle = `rgba(200,190,230,${(tw * 0.5).toFixed(3)})`;
    ctx.beginPath(); ctx.arc(pt.x, pt.y, tw * 1.3 + 0.3, 0, Math.PI * 2); ctx.fill();
  }

  ctx.restore();

  // Halo externo discreto
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const bp = 0.8 + Math.sin(t * 1.4) * 0.2;
  const bloom = ctx.createRadialGradient(cx, cy, 2, cx, cy, hw * 1.9);
  bloom.addColorStop(0, `rgba(110,70,180,${(0.14 * bp).toFixed(3)})`);
  bloom.addColorStop(0.5, `rgba(80,45,140,${(0.05 * bp).toFixed(3)})`);
  bloom.addColorStop(1, 'rgba(50,20,100,0)');
  ctx.fillStyle = bloom;
  ctx.beginPath(); ctx.ellipse(cx, cy, hw * 1.9, hh * 1.6, ang, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

/** Tocha medieval acesa presa a um pilar — chama tremeluzente + poça de luz. */
function drawTorch(ctx: CanvasRenderingContext2D, x: number, y: number, t: number, seed: number) {
  const flick = Math.sin(t * 11 + seed) * 0.5 + Math.sin(t * 23 + seed * 2) * 0.3;
  // suporte de ferro
  ctx.strokeStyle = '#2b2b2b';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(x, y + 6);
  ctx.lineTo(x, y - 4);
  ctx.stroke();
  // poça de luz quente
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const glow = ctx.createRadialGradient(x, y - 6, 0, x, y - 6, 22 + flick * 3);
  glow.addColorStop(0, 'rgba(255,170,70,0.42)');
  glow.addColorStop(0.5, 'rgba(255,120,40,0.15)');
  glow.addColorStop(1, 'rgba(255,90,20,0)');
  ctx.fillStyle = glow;
  ctx.beginPath(); ctx.arc(x, y - 6, 22 + flick * 3, 0, Math.PI * 2); ctx.fill();
  // chama
  const fh = 11 + flick * 2.5;
  const fg = ctx.createLinearGradient(x, y - 4, x, y - 4 - fh);
  fg.addColorStop(0, 'rgba(255,90,20,0.9)');
  fg.addColorStop(0.5, 'rgba(255,170,60,0.95)');
  fg.addColorStop(1, 'rgba(255,240,170,0.2)');
  ctx.fillStyle = fg;
  ctx.beginPath();
  ctx.moveTo(x - 3.2, y - 3);
  ctx.quadraticCurveTo(x - 4 + flick, y - 4 - fh * 0.6, x + flick * 1.5, y - 4 - fh);
  ctx.quadraticCurveTo(x + 4 + flick, y - 4 - fh * 0.6, x + 3.2, y - 3);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * Efeito visual da AÇÃO de um NPC data-driven (systems/movement) enquanto ela
 * toca num waypoint. `cx`/`groundY` = base do sprite na tela; `drawW`/`drawH` =
 * dimensões desenhadas do sprite. O tipo da ação vem do JSON (`actionName`).
 */
function drawNpcActionFx(
  ctx: CanvasRenderingContext2D,
  action: string | null,
  cx: number,
  groundY: number,
  drawW: number,
  drawH: number,
  now: number,
) {
  const pulse = 0.5 + 0.5 * Math.sin(now / 90);

  if (action === 'forge') {
    // fagulhas alaranjadas na altura da bigorna, à frente do ferreiro
    const gx = cx + drawW * 0.28;
    const gy = groundY - drawH * 0.34;
    const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, 22);
    grad.addColorStop(0, `rgba(255,170,60,${0.55 * pulse})`);
    grad.addColorStop(1, 'rgba(255,120,0,0)');
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(gx, gy, 22, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = `rgba(255,220,150,${0.9 * pulse})`;
    for (let s = 0; s < 3; s++) {
      const a = now / 120 + s * 2.1;
      ctx.fillRect(gx + Math.cos(a) * (6 + s * 3), gy + Math.sin(a * 1.7) * 5 - s * 2, 2, 2);
    }
    return;
  }

  if (action === 'channel') {
    // energia arcana subindo do chão + halo roxo no topo do cajado
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const hy = groundY - drawH * 0.95;
    const halo = ctx.createRadialGradient(cx, hy, 0, cx, hy, 16 + pulse * 6);
    halo.addColorStop(0, `rgba(170,120,255,${0.5 * pulse + 0.15})`);
    halo.addColorStop(1, 'rgba(120,60,220,0)');
    ctx.fillStyle = halo;
    ctx.beginPath(); ctx.arc(cx, hy, 16 + pulse * 6, 0, Math.PI * 2); ctx.fill();
    for (let s = 0; s < 5; s++) {
      const p = ((now / 900) + s / 5) % 1;
      const py = groundY - p * drawH * 0.9;
      const px = cx + Math.sin((p * 6) + s) * drawW * 0.22;
      ctx.fillStyle = `rgba(160,120,255,${(1 - p) * 0.6})`;
      ctx.fillRect(px - 1.5, py - 1.5, 3, 3);
    }
    ctx.restore();
    return;
  }

  if (action === 'trade' || action === 'farm') {
    // brilho dourado de moedas sobre a bancada, à frente da mercadora
    const gx = cx + drawW * 0.24;
    const gy = groundY - drawH * 0.30;
    const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, 16);
    grad.addColorStop(0, `rgba(255,214,110,${0.5 * pulse})`);
    grad.addColorStop(1, 'rgba(230,170,40,0)');
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(gx, gy, 16, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = `rgba(255,235,170,${0.9 * pulse})`;
    for (let s = 0; s < 3; s++) {
      const a = now / 260 + s * 2.1;
      ctx.beginPath();
      ctx.arc(gx + Math.cos(a) * 6, gy + Math.sin(a) * 3 - s * 2, 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
    return;
  }
}

/**
 * Rótulo com o nome do NPC, flutuando logo acima da cabeça — pequeno, com
 * contorno escuro pra ler sobre qualquer fundo. Não gira com a câmera.
 */
function drawNpcNameTag(ctx: CanvasRenderingContext2D, name: string, cx: number, topY: number) {
  if (!name) return;
  ctx.save();
  ctx.font = '600 11px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.strokeText(name, cx, topY);
  ctx.fillStyle = '#f4e9c9';
  ctx.fillText(name, cx, topY);
  ctx.restore();
}

function draw3DPortal(ctx: CanvasRenderingContext2D, cx: number, cy: number, now: number, rot: number, label = '⚔ BATALHA ⚔') {
  const t = now / 1000;

  // `cy` chega como o ponto SUPERIOR do losango do tile (isoProject usa
  // offsetY - TILE_H_HALF). O "chão" onde as entidades pisam é cy + TILE_H_HALF
  // — os doodads (árvore/pedra) somam isso internamente. O portal precisa fazer
  // o mesmo, senão fica flutuando ~16px acima da própria célula.
  const baseCy = cy + TILE_H_HALF;
  const p3d = (dx: number, dy: number, dz: number) => {
    const rx = dx * Math.cos(rot) - dy * Math.sin(rot);
    const ry = dx * Math.sin(rot) + dy * Math.cos(rot);
    return { x: cx + (rx - ry) * TILE_W_HALF, y: baseCy + (rx + ry) * TILE_H_HALF - dz, depth: rx + ry, rx, ry };
  };

  const pw = 0.17;
  const ph = 46;
  const px1 = -0.42, px2 = 0.42;
  const lh = 13;

  // ── Névoa rasteira no chão ──────────────────────────────────────────────
  const gp = p3d(0, 0, 0);
  for (let i = 0; i < 4; i++) {
    const seed = i * 33.3;
    const drift = Math.sin(t * 0.5 + seed) * 10;
    const fg = ctx.createRadialGradient(gp.x + drift, gp.y + 6, 0, gp.x + drift, gp.y + 6, 34);
    fg.addColorStop(0, 'rgba(70,60,90,0.12)');
    fg.addColorStop(1, 'rgba(70,60,90,0)');
    ctx.fillStyle = fg;
    ctx.beginPath(); ctx.ellipse(gp.x + drift, gp.y + 6, 34, 12, 0, 0, Math.PI * 2); ctx.fill();
  }

  // ── Luz sombria projetada no chão ───────────────────────────────────────
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const gGlow = 1.0 + Math.sin(t * 1.6) * 0.1;
  const gr = ctx.createRadialGradient(gp.x, gp.y + 4, 2, gp.x, gp.y + 4, 40 * gGlow);
  gr.addColorStop(0, 'rgba(110,70,180,0.22)');
  gr.addColorStop(1, 'rgba(70,30,140,0)');
  ctx.fillStyle = gr;
  ctx.beginPath(); ctx.ellipse(gp.x, gp.y + 4, 40 * gGlow, 17 * gGlow, 0, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // ── Arco de pedra antiga (faces pseudo-3D, back-face cull, depth sort) ──
  const STONE = { front: '#4a4650', side: '#332f3a', dark: '#211e27', top: '#565060', moss: '#41502f' };
  type QFace = { pts: { x: number; y: number; z: number }[]; color: string; nx: number; ny: number };
  const faces: QFace[] = [];
  const face = (pts: { x: number; y: number; z: number }[], color: string, nx: number, ny: number) =>
    faces.push({ pts, color, nx, ny });

  const pillar = (bx: number) => {
    face([{ x: bx - pw, y: pw, z: 0 }, { x: bx + pw, y: pw, z: 0 }, { x: bx + pw, y: pw, z: ph }, { x: bx - pw, y: pw, z: ph }], STONE.front, 0, 1);
    face([{ x: bx + pw, y: -pw, z: 0 }, { x: bx - pw, y: -pw, z: 0 }, { x: bx - pw, y: -pw, z: ph }, { x: bx + pw, y: -pw, z: ph }], STONE.dark, 0, -1);
    face([{ x: bx + pw, y: pw, z: 0 }, { x: bx + pw, y: -pw, z: 0 }, { x: bx + pw, y: -pw, z: ph }, { x: bx + pw, y: pw, z: ph }], STONE.side, 1, 0);
    face([{ x: bx - pw, y: -pw, z: 0 }, { x: bx - pw, y: pw, z: 0 }, { x: bx - pw, y: pw, z: ph }, { x: bx - pw, y: -pw, z: ph }], STONE.dark, -1, 0);
    // base musgosa
    face([{ x: bx - pw, y: pw, z: 0 }, { x: bx + pw, y: pw, z: 0 }, { x: bx + pw, y: pw, z: 6 }, { x: bx - pw, y: pw, z: 6 }], STONE.moss, 0, 1);
  };
  pillar(px1);
  pillar(px2);

  face([{ x: px1 - pw, y: pw, z: ph }, { x: px2 + pw, y: pw, z: ph }, { x: px2 + pw, y: pw, z: ph + lh }, { x: px1 - pw, y: pw, z: ph + lh }], STONE.front, 0, 1);
  face([{ x: px2 + pw, y: -pw, z: ph }, { x: px1 - pw, y: -pw, z: ph }, { x: px1 - pw, y: -pw, z: ph + lh }, { x: px2 + pw, y: -pw, z: ph + lh }], STONE.dark, 0, -1);
  face([{ x: px2 + pw, y: pw, z: ph }, { x: px2 + pw, y: -pw, z: ph }, { x: px2 + pw, y: -pw, z: ph + lh }, { x: px2 + pw, y: pw, z: ph + lh }], STONE.side, 1, 0);
  face([{ x: px1 - pw, y: -pw, z: ph }, { x: px1 - pw, y: pw, z: ph }, { x: px1 - pw, y: pw, z: ph + lh }, { x: px1 - pw, y: -pw, z: ph + lh }], STONE.dark, -1, 0);
  face([{ x: px1 - pw, y: pw, z: ph + lh }, { x: px2 + pw, y: pw, z: ph + lh }, { x: px2 + pw, y: -pw, z: ph + lh }, { x: px1 - pw, y: -pw, z: ph + lh }], STONE.top, 0, 0);

  const rendered: { pts: { x: number; y: number; depth: number }[]; color?: string; depth: number; portal?: boolean }[] = [];
  for (const f of faces) {
    if (f.nx !== 0 || f.ny !== 0) {
      const rnx = f.nx * Math.cos(rot) - f.ny * Math.sin(rot);
      const rny = f.nx * Math.sin(rot) + f.ny * Math.cos(rot);
      if (rnx + rny < -0.001) continue;
    }
    const pp = f.pts.map(p => p3d(p.x, p.y, p.z));
    rendered.push({ pts: pp, color: f.color, depth: pp.reduce((s, p) => s + p.depth, 0) / pp.length });
  }

  const portalPts = [p3d(px1 + pw, 0, 2), p3d(px2 - pw, 0, 2), p3d(px2 - pw, 0, ph), p3d(px1 + pw, 0, ph)];
  rendered.push({ pts: portalPts, depth: portalPts.reduce((s, p) => s + p.depth, 0) / 4 - 0.01, portal: true });

  rendered.sort((a, b) => a.depth - b.depth);

  for (const f of rendered) {
    if (f.portal) { drawPortalVortex(ctx, f.pts, now); continue; }
    ctx.beginPath();
    f.pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = f.color!;
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // ── Runas fracas nos pilares ────────────────────────────────────────────
  for (const bx of [px1, px2]) {
    for (let r = 0; r < 3; r++) {
      const rp = p3d(bx, pw, ph * (0.35 + r * 0.22));
      const a = 0.12 + Math.max(0, Math.sin(t * 1.3 + r + bx * 5)) * 0.22;
      ctx.fillStyle = `rgba(150,110,220,${a.toFixed(3)})`;
      ctx.fillRect(rp.x - 1.5, rp.y - 3, 3, 6);
    }
  }

  // ── Tochas nos pilares (frente) ────────────────────────────────────────
  const tL = p3d(px1 - pw, pw, ph * 0.72);
  const tR = p3d(px2 + pw, pw, ph * 0.72);
  drawTorch(ctx, tL.x, tL.y, t, 1.7);
  drawTorch(ctx, tR.x, tR.y, t, 4.1);

  // ── Runa da chave do arco ──────────────────────────────────────────────
  const ks = p3d(0, pw, ph + lh * 0.5);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const kg = ctx.createRadialGradient(ks.x, ks.y, 0, ks.x, ks.y, 8);
  kg.addColorStop(0, 'rgba(170,120,240,0.7)');
  kg.addColorStop(1, 'rgba(120,60,220,0)');
  ctx.fillStyle = kg;
  ctx.beginPath(); ctx.arc(ks.x, ks.y, 8, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // ── Guardião: agora é um NPC data-driven (systems/movement) — registrado
  // pelo HubMap na célula battle_point e desenhado pelo ramo 'mover'. ──────

  // ── Placa "BATALHA" (madeira envelhecida, balanço suave) ───────────────
  const signPt = p3d(0, 0, ph + lh + 22);
  ctx.save();
  ctx.translate(signPt.x, signPt.y);
  ctx.rotate(Math.sin(t * 1.1) * 0.035);
  ctx.font = 'bold 10px "Palatino Linotype","Book Antiqua",Palatino,serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const text = label;
  const tw = ctx.measureText(text).width + 18;
  const th = 18;
  const pg = ctx.createLinearGradient(0, -th / 2, 0, th / 2);
  pg.addColorStop(0, '#4c3120');
  pg.addColorStop(0.5, '#3c2718');
  pg.addColorStop(1, '#2c1c11');
  ctx.fillStyle = pg;
  ctx.beginPath(); ctx.roundRect(-tw / 2, -th / 2, tw, th, 2); ctx.fill();
  ctx.strokeStyle = '#7a6a4a';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.roundRect(-tw / 2, -th / 2, tw, th, 2); ctx.stroke();
  // cantoneiras de ferro
  ctx.fillStyle = '#20202a';
  for (const sx of [-1, 1]) ctx.fillRect(sx * (tw / 2) - (sx < 0 ? 0 : 4), -th / 2, 4, th);
  ctx.fillStyle = '#d9b866';
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

// ─────────────────────────────────────────────────────────────────────────────
// Portal de Missão — visual próprio (sem arco de pedra): círculo de runas no
// chão girando em sentidos opostos, feixe de luz, vórtice ciano/magenta em
// espiral, cristais em órbita e partículas subindo. `ageMs` = tempo desde que
// o portal surgiu (Infinity = sem animação de abertura, ex.: Hub): raio do céu,
// clarão, duas ondas de choque, estilhaços e o portal crescendo do zero.
// ─────────────────────────────────────────────────────────────────────────────
const MP_SPAWN_MS = 1800;

function drawMissionPortal(ctx: CanvasRenderingContext2D, cx: number, cy: number, now: number, label: string, ageMs: number) {
  const t = now / 1000;
  const gx = cx;
  const gy = cy + TILE_H_HALF;          // chão da célula (mesma regra do draw3DPortal)
  const spawning = ageMs < MP_SPAWN_MS;
  const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
  const easeOutBack = (v: number) => { const c1 = 1.70158, c3 = c1 + 1; return 1 + c3 * Math.pow(v - 1, 3) + c1 * Math.pow(v - 1, 2); };
  // 0 → 1 depois do raio (300 ms), com leve "estouro" no fim.
  const grow = spawning ? Math.max(0, easeOutBack(clamp01((ageMs - 300) / 1200))) : 1;

  const vy = gy - 38;                   // centro do vórtice (flutua acima do chão)

  // ── Abertura: raio do céu + clarão + ondas de choque + estilhaços ─────────
  if (spawning) {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    if (ageMs < 750) {
      const a = 1 - ageMs / 750;
      const w = 26 * a + 4;
      const beam = ctx.createLinearGradient(gx - w, 0, gx + w, 0);
      beam.addColorStop(0, 'rgba(120,240,255,0)');
      beam.addColorStop(0.5, `rgba(235,255,255,${(0.95 * a).toFixed(3)})`);
      beam.addColorStop(1, 'rgba(255,120,240,0)');
      ctx.fillStyle = beam;
      ctx.fillRect(gx - w, gy - 520, w * 2, 520);
      const flash = ctx.createRadialGradient(gx, gy, 0, gx, gy, 90);
      flash.addColorStop(0, `rgba(255,255,255,${(0.9 * a).toFixed(3)})`);
      flash.addColorStop(1, 'rgba(140,220,255,0)');
      ctx.fillStyle = flash;
      ctx.beginPath(); ctx.ellipse(gx, gy, 90, 42, 0, 0, Math.PI * 2); ctx.fill();
    }
    for (const [start, color] of [[200, '120,240,255'], [420, '255,130,240']] as const) {
      const p = clamp01((ageMs - start) / 900);
      if (p <= 0 || p >= 1) continue;
      const r = 8 + p * 100;
      ctx.strokeStyle = `rgba(${color},${(0.85 * (1 - p)).toFixed(3)})`;
      ctx.lineWidth = 3 * (1 - p) + 1;
      ctx.beginPath(); ctx.ellipse(gx, gy, r, r * 0.45, 0, 0, Math.PI * 2); ctx.stroke();
    }
    const sp = clamp01((ageMs - 250) / 800);
    if (sp > 0 && sp < 1) {
      for (let i = 0; i < 12; i++) {
        const ang = (i / 12) * Math.PI * 2 + 0.3;
        const d = 10 + sp * 70;
        const sx = gx + Math.cos(ang) * d;
        const sy = gy + Math.sin(ang) * d * 0.45 - Math.sin(sp * Math.PI) * 26;
        ctx.fillStyle = i % 2 ? `rgba(130,240,255,${(1 - sp).toFixed(3)})` : `rgba(255,150,240,${(1 - sp).toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(sx, sy - 4); ctx.lineTo(sx + 2, sy); ctx.lineTo(sx, sy + 4); ctx.lineTo(sx - 2, sy);
        ctx.closePath(); ctx.fill();
      }
    }
    ctx.restore();
  }
  if (grow <= 0.01) return;

  ctx.save();
  // ── Círculo mágico no chão: dois anéis de runas girando em sentidos opostos ─
  const R = 34 * grow;
  ctx.globalCompositeOperation = 'lighter';
  const halo = ctx.createRadialGradient(gx, gy, 0, gx, gy, R * 1.3);
  halo.addColorStop(0, 'rgba(255,120,235,0.30)');
  halo.addColorStop(0.6, 'rgba(90,220,255,0.16)');
  halo.addColorStop(1, 'rgba(90,220,255,0)');
  ctx.fillStyle = halo;
  ctx.beginPath(); ctx.ellipse(gx, gy, R * 1.3, R * 0.6, 0, 0, Math.PI * 2); ctx.fill();

  const ring = (radius: number, color: string, ticks: number, speed: number) => {
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = 'rgba(30,10,60,0.55)';
    ctx.lineWidth = 3.5;
    ctx.beginPath(); ctx.ellipse(gx, gy, radius, radius * 0.45, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.ellipse(gx, gy, radius, radius * 0.45, 0, 0, Math.PI * 2); ctx.stroke();
    for (let i = 0; i < ticks; i++) {
      const a = (i / ticks) * Math.PI * 2 + t * speed;
      const x = gx + Math.cos(a) * radius, y = gy + Math.sin(a) * radius * 0.45;
      ctx.fillStyle = color;
      // "runa": tracinho em cruz
      ctx.fillRect(x - 2.5, y - 0.75, 5, 1.5);
      ctx.fillRect(x - 0.75, y - 2, 1.5, 4);
    }
  };
  ring(R, 'rgba(90,230,255,0.95)', 12, 0.6);
  ring(R * 0.66, 'rgba(255,90,220,0.95)', 8, -0.9);

  // ── Feixe de luz pulsando para cima ──────────────────────────────────────
  const pulse = 0.22 + Math.sin(t * 2.4) * 0.08;
  const bw = 15 * grow;
  const bg = ctx.createLinearGradient(0, gy, 0, gy - 95);
  bg.addColorStop(0, `rgba(170,240,255,${pulse.toFixed(3)})`);
  bg.addColorStop(1, 'rgba(170,240,255,0)');
  ctx.fillStyle = bg;
  ctx.fillRect(gx - bw / 2, gy - 95, bw, 95);

  // ── Cristais em órbita (os de trás antes do vórtice) ─────────────────────
  const crystals = Array.from({ length: 6 }, (_, i) => {
    const a = t * 0.9 + (i / 6) * Math.PI * 2;
    return { a, x: gx + Math.cos(a) * 27 * grow, y: vy + Math.sin(a) * 9 * grow + Math.sin(t * 2 + i) * 2, front: Math.sin(a) > 0 };
  });
  const crystal = (c: { x: number; y: number; front: boolean }) => {
    const s = (c.front ? 1 : 0.75) * grow;
    const cg = ctx.createLinearGradient(c.x, c.y - 8 * s, c.x, c.y + 8 * s);
    cg.addColorStop(0, 'rgba(210,255,255,0.95)');
    cg.addColorStop(1, 'rgba(170,90,255,0.85)');
    ctx.fillStyle = cg;
    ctx.beginPath();
    ctx.moveTo(c.x, c.y - 8 * s); ctx.lineTo(c.x + 3.5 * s, c.y); ctx.lineTo(c.x, c.y + 8 * s); ctx.lineTo(c.x - 3.5 * s, c.y);
    ctx.closePath(); ctx.fill();
  };
  ctx.globalCompositeOperation = 'source-over';
  crystals.filter(c => !c.front).forEach(crystal);

  // ── Vórtice: fundo escuro (contraste em dia claro) + braços em espiral ────
  const rx = 18 * grow, ry = 28 * grow;
  ctx.globalCompositeOperation = 'source-over';
  const deep = ctx.createRadialGradient(gx, vy, 0, gx, vy, ry);
  deep.addColorStop(0, 'rgba(70,20,120,0.95)');
  deep.addColorStop(0.7, 'rgba(36,10,80,0.92)');
  deep.addColorStop(1, 'rgba(20,6,50,0.85)');
  ctx.fillStyle = deep;
  ctx.beginPath(); ctx.ellipse(gx, vy, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
  ctx.globalCompositeOperation = 'lighter';
  const core = ctx.createRadialGradient(gx, vy, 0, gx, vy, ry * 0.55);
  core.addColorStop(0, 'rgba(255,255,255,0.9)');
  core.addColorStop(0.5, 'rgba(120,235,255,0.5)');
  core.addColorStop(1, 'rgba(120,235,255,0)');
  ctx.fillStyle = core;
  ctx.beginPath(); ctx.ellipse(gx, vy, rx * 0.55, ry * 0.55, 0, 0, Math.PI * 2); ctx.fill();
  for (let arm = 0; arm < 4; arm++) {
    ctx.strokeStyle = arm % 2 ? 'rgba(255,90,220,0.95)' : 'rgba(90,230,255,0.95)';
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    for (let k = 0; k <= 18; k++) {
      const f = k / 18;
      const a = arm * (Math.PI / 2) + f * 4.2 - t * 3.2;
      const x = gx + Math.cos(a) * rx * f, y = vy + Math.sin(a) * ry * f;
      if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.globalCompositeOperation = 'source-over';
  ctx.strokeStyle = 'rgba(150,240,255,0.95)';
  ctx.lineWidth = 2.2;
  ctx.shadowColor = 'rgba(255,90,220,0.9)';
  ctx.shadowBlur = 10;
  ctx.beginPath(); ctx.ellipse(gx, vy, rx, ry, 0, 0, Math.PI * 2); ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'lighter';

  // ── Partículas subindo em espiral ────────────────────────────────────────
  for (let i = 0; i < 14; i++) {
    const ph = (t * 0.45 + i / 14) % 1;
    const a = i * 2.4 + t * 1.5;
    const px = gx + Math.cos(a) * (10 + ph * 16) * grow;
    const py = gy - ph * 90;
    ctx.fillStyle = i % 2 ? `rgba(130,240,255,${(0.9 * (1 - ph)).toFixed(3)})` : `rgba(255,160,240,${(0.9 * (1 - ph)).toFixed(3)})`;
    ctx.beginPath(); ctx.arc(px, py, 1.6, 0, Math.PI * 2); ctx.fill();
  }

  ctx.globalCompositeOperation = 'source-over';
  crystals.filter(c => c.front).forEach(crystal);

  // ── Placa flutuante ──────────────────────────────────────────────────────
  if (grow >= 0.6) {
    ctx.globalAlpha = clamp01((grow - 0.6) / 0.4);
    const sy = gy - 82 + Math.sin(t * 1.6) * 2;
    ctx.font = 'bold 10px "Palatino Linotype","Book Antiqua",Palatino,serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const tw = ctx.measureText(label).width + 18, th = 18;
    ctx.fillStyle = 'rgba(14,10,36,0.88)';
    ctx.beginPath(); ctx.roundRect(gx - tw / 2, sy - th / 2, tw, th, 9); ctx.fill();
    ctx.strokeStyle = 'rgba(130,235,255,0.9)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = '#e8fbff';
    ctx.fillText(label, gx, sy + 0.5);
  }
  ctx.restore();
}


// ─────────────────────────────────────────────────────────────────────────────
// Animation types
// ─────────────────────────────────────────────────────────────────────────────

interface AnimPos    { x: number; y: number; bob: number }
/**
 * Animação de caminhada casa-a-casa. `pts[0]` = célula de origem, `pts[último]`
 * = destino; cada segmento entre dois pontos é um passo de `stepMs`. Total =
 * `(pts.length - 1) * stepMs`. `t0` pode ser futuro (fila de inimigos).
 */
interface AnimTarget { pts: Array<{ x: number; y: number }>; t0: number; stepMs: number }

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface CombatGridProps {
  map:          CombatMap;
  entities:     Record<string, CombatEntity>;
  /**
   * Caminhos percorridos na última ação (do servidor — MovementEngine BFS).
   * Uma entrada por entidade que se moveu, `path` = TODAS as células na ordem.
   * O grid anima o sprite andando casa a casa por essa sequência. Sem entrada
   * (ou sem a prop), reconstrói o caminho com BFS local — nunca desliza reto.
   */
  movements?: Array<{ entity_id: string; path: Array<{ x: number; y: number }> }>;
  myEntityId:   string | null;
  highlights:   HighlightedCell[];
  cellHover:    { x: number; y: number } | null;
  onCellClick:  (x: number, y: number) => void;
  onCellHover:  (x: number, y: number) => void;
  onMouseLeave: () => void;
  turnSecondsLeft?: number | null;
  /**
   * Modo de rotação da câmera:
   *  - 'free'   (padrão): arraste contínuo com o botão direito, ângulo livre.
   *             Usado no combate tático real — mantém a liberdade total já
   *             documentada em PROJECT.md.
   *  - 'snap90': a câmera só assume os 4 ângulos fixos (0°/90°/180°/270°).
   *             Arrastar acumula um "passo" a cada limiar de pixels e pula
   *             direto para o próximo múltiplo de 90°, sem ângulos intermediários.
   *             Usado no Mapa Hub.
   */
  rotationMode?: 'free' | 'snap90';
  /**
   * Tema do Plano de Fundo Medieval Dinâmico (Parallax Ambiance). Se omitido,
   * usa `map.background_theme` (vindo do MapTemplate/Combat) e por fim o
   * padrão 'medieval_mountains_day'.
   */
  backgroundTheme?: MapBackgroundTheme;
  /**
   * Tema de Clima/Horário (Sistema de Iluminação e Clima Variável). Se
   * omitido, usa `map.weather_type` (vindo do MapTemplate/Combat) e por fim
   * o padrão 'SUNNY'. Controla o overlay renderizado por <CanvasWeatherEffect/>
   * e ajustes sutis no próprio desenho do grid (sombras mais longas ao
   * entardecer, sombras suavizadas em dias nublados/chuvosos).
   */
  weatherType?: MapWeatherType;
  /**
   * Tempestade (missões): casas onde o raio cai na próxima rodada (⚡ pulsando)
   * e onde caiu na última — uma lista nova dispara a animação do raio.
   */
  stormMarks?: Array<{ x: number; y: number }>;
  stormStrikes?: Array<{ x: number; y: number; turn: number }>;
  /**
   * Entrada no portal de missão: a escuridão se fecha sobre a célula (x,y)
   * desde t0 (performance.now) por durationMs, até cobrir tudo.
   */
  portalWarp?: { x: number; y: number; t0: number; durationMs: number } | null;
  /**
   * Toggle "Exibir Fundo (Céu)". Se omitido, usa `map.background_enabled`
   * (vindo do MapTemplate/Combat) e por fim `true`. Desligado, o Canvas de
   * fundo não desenha o cenário parallax animado — só um preenchimento
   * sólido (mesma cor de base do chão), evitando qualquer sobrecarga visual
   * quando o admin preferir um fundo neutro.
   */
  backgroundEnabled?: boolean;
  /** Lado do mapa que é o Norte (0=topo, 90=direita, 180=baixo, 270=esquerda). Se omitido, usa `map.north` e por fim 0. */
  north?: number;
  /**
   * Sprites de NPC do Hub, resolvidos pelo backend (HubController::getState).
   * Chave = id do NPC (ex.: 'blacksmith'); valor = arquivo servido em
   * `/sprites/<sprite>` mais os ajustes finos do painel de Sprites
   * (escala / offset). O NPC é desenhado como billboard dessa imagem; sem
   * vínculo / enquanto a imagem não carrega, mostra só um marcador mínimo.
   */
  npcSprites?: Record<string, { sprite: string; cols?: number; rows?: number; scale?: number; offset_x?: number; offset_y?: number; frame_overrides?: Record<string, { scale?: number; offsetX?: number; offsetY?: number }> }>;
  /**
   * Nome exibido acima de cada NPC do Hub, por id/papel ('blacksmith',
   * 'guardian', 'merchant'). Se omitido, usa os nomes fixos pt-BR de
   * `NPC_DISPLAY_NAMES`. Passe para respeitar o locale (i18n).
   */
  npcNames?: Record<string, string>;
  /**
   * Fauna ambiental data-driven (ver `src/systems/movement`). Nome do spawner
   * de pássaros em `ambient_birds.json`; `null` desliga. Default: `'hub_birds'`.
   */
  birdSpawner?: string | null;
  /**
   * NPCs de patrulha data-driven (perfis em `npc_behavior.json`). O movimento
   * roda 100% no cliente (client-authoritative); o servidor só sincroniza
   * jogador + ações globais.
   */
  ambientNpcs?: AmbientNpcSpec[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function CombatGrid({
  map,
  entities,
  movements,
  myEntityId,
  highlights,
  cellHover,
  onCellClick,
  onCellHover,
  onMouseLeave,
  turnSecondsLeft,
  rotationMode = 'free',
  backgroundTheme,
  weatherType,
  stormMarks,
  stormStrikes,
  portalWarp,
  backgroundEnabled,
  north,
  npcSprites,
  npcNames,
  birdSpawner = 'hub_birds',
  ambientNpcs,
  }: CombatGridProps) {
  const resolvedBackgroundTheme: MapBackgroundTheme = backgroundTheme ?? map.background_theme ?? 'medieval_mountains_day';
  const resolvedWeatherType: MapWeatherType = weatherType ?? map.weather_type ?? 'SUNNY';
  const resolvedBackgroundEnabled: boolean = backgroundEnabled ?? map.background_enabled ?? true;
  const resolvedNorth: number = north ?? map.north ?? 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Zoom & Rotation ────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(1.0);
  const [rotation, setRotation] = useState(0); // Angle in radians
  const zoomRef = useRef(1.0);
  const rotRef = useRef(0);
  zoomRef.current = zoom;
  rotRef.current = rotation;

  // ── Map offsets and canvas dimensions ─────────────────────────────────────
  // Ensure bounding box covers rotation
  const mapCx = map.width / 2;
  const mapCy = map.height / 2;
  
  const baseW   = (map.width + map.height) * TILE_W_HALF * 1.5;
  const baseH   = (map.width + map.height) * TILE_H_HALF * 1.5;
  const offsetX = baseW / 2;
  // No modo 'free' (combate) a grade gira em ângulos arbitrários e sua caixa
  // envolvente cresce ~√2 — o deslocamento para cima reserva a folga inferior
  // pra isso. No 'snap90' (Hub) a grade só assume 0/90/180/270°, cuja caixa
  // envolvente é idêntica à de 0°, então a grade fica CENTRADA no canvas
  // (senão o mapa aparece "colado no topo" da tela do jogador).
  const offsetY = rotationMode === 'snap90'
    ? baseH / 2
    : baseH / 2 - (map.width + map.height) * TILE_H_HALF / 4;
  const canvasW = Math.round(baseW * zoom);
  const canvasH = Math.round(baseH * zoom);

  // ── Fauna ambiental data-driven (pássaros + NPCs de patrulha) ─────────────
  // Movimento/spawn/despawn 100% no cliente, a partir dos JSON em
  // `public/data/movement/`. Ref estável — lido nos callbacks de render (deps []).
  const ambient = useAmbientMovement({
    birdSpawners: birdSpawner === null ? [] : [birdSpawner],
    npcs: ambientNpcs ?? [],
  });
  const ambientRef = ambient.ref;

  // Ajuste da animação de caminhada de personagens/monstros — via JSON
  // (character_movement.json). Ref estável, lido no loop RAF.
  const moveCfgRef = useCharacterMovementConfig();

  // ── "Latest props" ref — avoids stale closures inside RAF ─────────────────
  const L = useRef({ map, entities, myEntityId, highlights, cellHover, offsetX, offsetY, mapCx, mapCy, backgroundTheme: resolvedBackgroundTheme, weatherType: resolvedWeatherType, backgroundEnabled: resolvedBackgroundEnabled, npcSprites, npcNames, stormMarks, stormStrikes, portalWarp });
  L.current = { map, entities, myEntityId, highlights, cellHover, offsetX, offsetY, mapCx, mapCy, backgroundTheme: resolvedBackgroundTheme, weatherType: resolvedWeatherType, backgroundEnabled: resolvedBackgroundEnabled, npcSprites, npcNames, stormMarks, stormStrikes, portalWarp };
  // Animação do raio: reinicia quando chega uma lista nova de impactos.
  const strikeAnim = useRef<{ key: string | null; t0: number }>({ key: null, t0: -Infinity });

  // ── Walk / position animation state ───────────────────────────────────────
  const animPos    = useRef<Map<string, AnimPos>>(new Map());
  const animTarget = useRef<Map<string, AnimTarget>>(new Map());
  const knownPos   = useRef<Map<string, { x: number; y: number }>>(new Map());
  const rafId      = useRef(0);

  // ── Câmera que acompanha o jogador (follow-cam com zona morta) ─────────────
  // Enquanto o jogador está na região central do monitor, a câmera não mexe.
  // Ao chegar perto da borda, ela desliza até recolocá-lo no centro — no Hub
  // e no combate. Suspensa enquanto o jogador arrasta a câmera (pan manual).
  const followActive     = useRef(false);   // recentragem em andamento
  const playerHasMoved   = useRef(false);   // só segue depois do 1º passo (respeita o enquadramento inicial da batalha)
  const followSuspendTo  = useRef(0);       // timestamp até quando o follow fica pausado (após pan manual)
  // Alvo de scroll da câmera. Toda movimentação AUTOMÁTICA (follow-cam,
  // recentragem ao girar) escreve aqui; o loop RAF desliza o scroll até esse
  // alvo com ease-out + teto de velocidade — nunca um salto seco. `null` = o
  // jogador está no controle (arrastando) ou não há alvo.
  const camTarget        = useRef<{ x: number; y: number } | null>(null);
  // Portal de missão recém-aberto: quando surgiu (por célula) e foco temporário
  // da câmera nele (volta ao enquadramento anterior ao fim).
  const portalSpawnAt    = useRef(new Map<string, number>());
  const portalFocus      = useRef<{ x: number; y: number; until: number; back: { x: number; y: number } | null } | null>(null);
  const bgFrame          = useRef(0);       // contador p/ throttle do fundo parallax
  const bgDirty          = useRef(true);    // força redesenho do fundo (rotação / zoom / mount)
  const terrainCache     = useRef<HTMLCanvasElement | null>(null);  // piso pré-renderizado (mapas planos)
  const terrainCacheKey  = useRef<string>('');

  // ── Sprite state ───────────────────────────────────────────────────────────
  const spriteCache    = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const loadingSet     = useRef<Set<string>>(new Set());
  const entityDir      = useRef<Map<string, {dx: number, dy: number}>>(new Map());

  // ── NPC image state (Hub: sprite enviado pelo admin p/ o Ferreiro etc.) ────
  // Diferente de spriteCache (personagens): imagem única estática, servida em
  // /sprites/<arquivo>, sem spritesheet de direções e sem chroma-key (a imagem
  // já traz canal alfa real). Sem fallback procedural — só um marcador mínimo
  // enquanto não carrega.
  const npcImageCache = useRef<Map<string, HTMLImageElement>>(new Map());
  const npcLoadingSet = useRef<Set<string>>(new Set());

  // ── Core draw function ─────────────────────────────────────────────────────
  const drawFrame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { entities, myEntityId, highlights, cellHover, offsetX, offsetY, weatherType } = L.current;
    // Render usa o mapa sem a Rampa antiga (tipo 'Ramp'), que abria vãos na
    // geometria — ela é desenhada como terreno comum no mesmo nível.
    const map = withoutLegacyRamps(L.current.map);
    const zoom = zoomRef.current;
    const rot = rotRef.current;
    const now  = performance.now();

    /** Projeta os 4 cantos (em tela) de QUALQUER célula do grid (gx,gy) — usado tanto para a própria célula quanto para a vizinha ao montar o plano inclinado de uma Rampa (ver Via de Subida). */
    const cellCorners = (gx: number, gy: number): ScreenCorner[] =>
      [[gx, gy], [gx + 1, gy], [gx + 1, gy + 1], [gx, gy + 1]].map(([px, py]) => {
        const [rx, ry] = rotatePoint(px, py, L.current.mapCx, L.current.mapCy, rot);
        return isoProject(rx, ry, offsetX, offsetY);
      });

    // Sistema de Iluminação e Clima Variável: Nublado/Chuvoso → luz difusa,
    // sombras de contato bem mais suaves; Pôr do Sol → sombras longas e
    // deslocadas (luz rasante). 'SUNNY'/'NIGHT' usam a força padrão.
    const softShadows = weatherType === 'CLOUDY' || weatherType === 'RAIN' || weatherType === 'FOG' || weatherType === 'STORM';
    const shadowStrength = softShadows ? 0.35 : 1;
    const isSunsetLight = weatherType === 'SUNSET';

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(zoom, zoom);

    const hlMap = new Map<string, HighlightedCell['type']>();
    for (const h of highlights) hlMap.set(`${h.x},${h.y}`, h.type);

    // ── Sistema de Renderização em Camadas (3 Layers rígidas) ─────────────────
    //
    // Layer 1+2 (piso): para CADA célula, o topo (chão padrão OU plataforma
    // elevada Nível 1/2) é SEMPRE desenhado sólido, cobrindo 100% da área do
    // bloco — corrige o antigo bug do "buraco oco": qualquer célula que não
    // seja um vazio genuíno (Full_Obstacle no chão, Nível 0) ganha piso sólido,
    // mesmo que seu tipo decorativo original fosse Mountain/Tree/Rock/
    // Vision_Obstacle/Full_Obstacle — pintar elevação > 0 sobre qualquer tipo
    // sempre "solidifica" o bloco como uma montanha/plataforma caminhável.
    //
    // Layer 3 (profundidade): paredes verticais de elevação NÃO são mais
    // desenhadas junto ao piso — elas entram no mesmo vetor ordenado por
    // profundidade (junto com personagens e obstáculos decorativos) para que
    // um personagem atrás de uma montanha fique oculto por ela, e um
    // personagem em cima da montanha sempre desenhe por cima da parede.
    type WallFace = {
      p0: ScreenCorner; p1: ScreenCorner; p2: ScreenCorner; p3: ScreenCorner; shade: string; isPit: boolean;
      /** Semente estável (célula + lado) para o desenho das colunas de rocha. */
      seed?: number;
      /** Piso do topo (grama/Chão Batido) — define a saliência na borda do paredão. */
      topFloor?: 'grass' | 'dirt';
      /** Rampa: triângulos laterais (entre o plano inclinado e o chão) virados para a câmera. */
      sides?: Array<[ScreenCorner, ScreenCorner, ScreenCorner]>;
      /** Rampa no lado de trás do platô (vista de costas, só uma faixa fina). */
      back?: boolean;
      /** Via de Subida (Ponto A → Ponto B) definida no admin — troca o muro por um conector caminhável. */
      connector?: 'RAMP' | 'STAIRS' | 'ROPE';
    };
    type DepthObj =
      | { type: 'entity'; id: string; lx: number; ly: number; vx: number; vy: number; elevation: number; data: CombatEntity }
      | { type: 'mover'; id: string; lx: number; ly: number; vx: number; vy: number; elevation: number }
      | { type: 'obstacle'; kind: 'Tree'|'Mountain'|'Rock'|'Vision_Obstacle'|'npc_blacksmith'|'npc_merchant'|'npc_farmer'|'tree'|'battle_point'; lx: number; ly: number; vx: number; vy: number; elevation: number }
      | { type: 'wall'; vx: number; vy: number; elevation: number; face: WallFace }
      | { type: 'floor'; vx: number; vy: number; elevation: number; render: (fctx: CanvasRenderingContext2D) => void; live?: boolean };

    const depthObjects: DepthObj[] = [];

    // Deslocamento (em coordenadas de grid, antes da rotação) do ponto médio
    // de cada uma das 4 arestas de uma célula — usado para posicionar a
    // parede no vetor de profundidade na mesma "linha" da aresta compartilhada.
    const EDGE_MID: Array<[number, number]> = [[0.5, 0], [1, 0.5], [0.5, 1], [0, 0.5]]; // N, E, S, W

    // ── Garantia de 360° (Zero Buracos nas Laterais) ───────────────────────
    // Cada parede de elevação é um único quad plano, não um volume 3D real —
    // ao contrário de um cubo sólido, não existe "outro lado" que a oclua
    // naturalmente. Ocultar uma face por "back-face culling" nesse modelo
    // deixa um vão genuíno (nada mais preenche aquele degrau de altura entre
    // o topo e o vizinho mais baixo), exatamente o bug relatado ao girar a
    // câmera. Por isso TODA aresta exposta (vizinho com Z diferente) sempre
    // desenha sua parede, em qualquer ângulo — a oclusão correta entre
    // paredes/plataformas/personagens continua garantida pelo Painter's
    // Algorithm por profundidade (ver depthObjects.sort abaixo).

    // ── Prisma Isométrico Maciço: Piso + Paredes num ÚNICO Painter's Algorithm ──
    // Abandonado o esquema antigo de duas camadas fixas (piso sempre desenhado
    // primeiro, paredes/personagens sempre por cima). Naquele esquema, uma
    // parede QUALQUER (mesmo de um tile distante) sempre vencia visualmente
    // qualquer piso, já que era desenhada numa passada posterior e separada —
    // isso podia cobrir incorretamente o chão de um platô elevado que, pela
    // profundidade real da câmera, deveria estar NA FRENTE daquela parede
    // (o bug de "chão sumindo"/"parede oca flutuando"). Agora o piso de cada
    // tile (chão/rampa/lago + decal + contorno + destaque) entra no MESMO
    // vetor `depthObjects` que paredes/personagens/obstáculos, como um
    // registro 'floor' com uma função de desenho adiada (`render`) — só
    // roda depois do único `depthObjects.sort` abaixo, na ordem correta de
    // profundidade (X+Y) e desempate por altura (Z). Cada tile continua
    // sendo um bloco 3D maciço e opaco: face do topo SEMPRE desenhada
    // (nenhum piso é omitido em nenhuma elevação) + paredes verticais
    // fechando 100% da lacuna exposta para os vizinhos mais baixos.
    //
    // ── Isolamento: Lógica do Terreno em Função Própria ─────────────────────
    // Todo o cálculo de geometria sólida (piso/rampa/lago + paredes/
    // conectores de Subida) fica CONTIDO nesta função — `buildTerrainLayer`
    // só lê `map`/`corners`/elevação e empurra registros para `depthObjects`.
    // Ela não desenha nem conhece entidades/personagens/sprites; isso é
    // responsabilidade exclusiva do loop de entidades logo abaixo (código
    // de sprite 100% intocado). As duas listas convergem no MESMO
    // `depthObjects.sort` para a oclusão 3D ficar correta em qualquer
    // ângulo — mas a CONSTRUÇÃO de cada uma é uma função/bloco separado.
    const buildTerrainLayer = () => {
    for (let gy = 0; gy < map.height; gy++) {
      for (let gx = 0; gx < map.width; gx++) {
        const cell = map.cells[gy]?.[gx];
        if (!cell) continue;

        const corners = cellCorners(gx, gy);

        // Centro da célula (chave de profundidade)
        const [rcx, rcy] = rotatePoint(gx + 0.5, gy + 0.5, L.current.mapCx, L.current.mapCy, rot);

        // Elevação de RENDER (não de dados/colisão): Montanha/Árvore/Rocha/
        // Vegetação são sempre Billboard, então valem nível 0 aqui — ver
        // `getVisualElevation`. Isso vale tanto para a própria célula quanto
        // para os vizinhos, senão uma plataforma real ao lado de uma
        // Montanha ainda tentaria "ficar no mesmo nível" e deixaria de gerar
        // a parede que fecha visualmente contra o chão plano da Montanha.
        const elev = getVisualElevation(cell);
        const neighborCells = {
          n: map.cells[gy - 1]?.[gx],
          e: map.cells[gy]?.[gx + 1],
          s: map.cells[gy + 1]?.[gx],
          w: map.cells[gy]?.[gx - 1],
        };
        const neighborElev = {
          n: getVisualElevation(neighborCells.n),
          e: getVisualElevation(neighborCells.e),
          s: getVisualElevation(neighborCells.s),
          w: getVisualElevation(neighborCells.w),
        };

        // Vazio genuíno: só existe ao nível do chão (elevação 0). Qualquer
        // elevação > 0 sempre "solidifica" a célula num piso caminhável —
        // é assim que uma "Montanha" pintada pelo admin vira terreno sólido
        // em vez de um buraco, mesmo que o tipo decorativo original bloqueie
        // no nível do chão.
        const isVoid = cell.type === 'Full_Obstacle' && elev === 0;

        // Highlight/Hover desta célula — calculado aqui, mas só DESENHADO
        // dentro do closure `renderFloor` abaixo, para ficar corretamente
        // depth-sorted junto com o piso (senão poderia vazar por cima/baixo
        // de paredes de outros tiles com profundidade diferente).
        const hl = hlMap.get(`${gx},${gy}`);
        const isHover = cellHover?.x === gx && cellHover?.y === gy;

        // ── Face do Topo (Piso) — SEMPRE desenhada, em QUALQUER elevação ────
        // Fórmula equivalente a `screenY = baseScreenY - heightLevel * TILE_HEIGHT_PIXELS`:
        // os `corners` acima já estão no plano Z=0 da tela; cada helper de
        // desenho (drawTopFace/drawPitFloor/drawRampTile/drawLake) desloca
        // os vértices para cima em `elevation * ELEV_UNIT` pixels antes de
        // preencher — é ali, no topo já deslocado, que o personagem pisa.
        const renderFloor = (fctx: CanvasRenderingContext2D) => {
          if (isVoid) {
            // Obstáculo total no chão: grama + amontoado de pedras (não caminhável).
            drawObstacleGround(fctx, corners, gx, gy);
          } else if (cell.type === 'Lake') {
            drawLake(fctx, corners, elev, gx, gy, now, lakeSidesOf(map.cells, gx, gy));
          } else if (cell.type === 'Ramp') {
            drawRampTile(fctx, corners, elev, neighborElev);
          } else if (elev < 0) {
            drawPitFloor(fctx, corners, elev);
          } else if (cell.floor === 'dirt') {
            // Capim só invade a borda vizinha de grama no MESMO nível.
            const isGrassAt = (c: typeof cell | undefined, e: number) =>
              hasSurfaceFloor(c) && c!.floor !== 'dirt' && e === elev;
            drawDirtTop(fctx, corners, elev, gx, gy, now, {
              n: isGrassAt(neighborCells.n, neighborElev.n),
              e: isGrassAt(neighborCells.e, neighborElev.e),
              s: isGrassAt(neighborCells.s, neighborElev.s),
              w: isGrassAt(neighborCells.w, neighborElev.w),
            });
          } else {
            // Topo do relevo segue o piso da célula (grama aqui; Chão Batido
            // no ramo acima) — a montanha "se adapta" ao terreno caminhável.
            drawGrassDetails(fctx, corners, elev, gx, gy, now);
          }

          if (!isVoid && cell.type === 'battle_point') {
            // Draw magic circle on ground (decal plano — o corpo 3D do
            // Portal é desenhado à parte, junto ao vetor ordenado por
            // profundidade, para não ser cortado/sobreposto incorretamente
            // ao girar a câmera — ver depthObjects abaixo).
            const z = elev * ELEV_UNIT;
            fctx.fillStyle = `rgba(180, 80, 255, ${0.1 + Math.sin(now / 500) * 0.1})`;
            fctx.beginPath();
            fctx.moveTo(corners[0][0], corners[0][1] - z);
            fctx.lineTo(corners[1][0], corners[1][1] - z);
            fctx.lineTo(corners[2][0], corners[2][1] - z);
            fctx.lineTo(corners[3][0], corners[3][1] - z);
            fctx.fill();
            fctx.strokeStyle = 'rgba(150, 50, 200, 0.6)';
            fctx.lineWidth   = 1.5;
            fctx.stroke();
          }

          // ── Linhas de Limite entre Níveis (Cliff Edge Outlines) ──────────
          // Toda fronteira com diferença de altura ganha um contorno nítido
          // para identificação instantânea, mais uma faixa CURTA de sombra
          // do lado mais baixo — EXCETO na fronteira de uma Via de Subida
          // (Rampa/Escada/Corda), que já comunica "isto é acessível" com seu
          // próprio conector e não deve parecer um limite intransponível.
          if (!isVoid) {
            if (neighborElev.n !== elev && !findClimbConnectorType(cell, gx, gy, neighborCells.n, gx, gy - 1)) drawCliffEdgeOutline(fctx, corners, 0, 1, elev, neighborElev.n, shadowStrength);
            if (neighborElev.e !== elev && !findClimbConnectorType(cell, gx, gy, neighborCells.e, gx + 1, gy)) drawCliffEdgeOutline(fctx, corners, 1, 2, elev, neighborElev.e, shadowStrength);
            if (neighborElev.s !== elev && !findClimbConnectorType(cell, gx, gy, neighborCells.s, gx, gy + 1)) drawCliffEdgeOutline(fctx, corners, 2, 3, elev, neighborElev.s, shadowStrength);
            if (neighborElev.w !== elev && !findClimbConnectorType(cell, gx, gy, neighborCells.w, gx - 1, gy)) drawCliffEdgeOutline(fctx, corners, 3, 0, elev, neighborElev.w, shadowStrength);
          }

          // Highlight and Hover overlay
          if (hl || isHover) {
            const z0 = elev * ELEV_UNIT;

            fctx.beginPath();
            fctx.moveTo(corners[0][0], corners[0][1] - z0);
            fctx.lineTo(corners[1][0], corners[1][1] - z0);
            fctx.lineTo(corners[2][0], corners[2][1] - z0);
            fctx.lineTo(corners[3][0], corners[3][1] - z0);
            fctx.closePath();

            if (hl) {
              fctx.fillStyle =
                hl === 'movement'    ? COLORS.highlight_mov   :
                hl === 'spell_range' ? COLORS.highlight_spell :
                                       COLORS.highlight_aoe;
              fctx.fill();
            }
            if (isHover) {
              fctx.fillStyle = COLORS.hover;
              fctx.fill();
              // Limpeza Visual do Grid: como as linhas de grade agora ficam
              // quase invisíveis (18% opacidade) por padrão, a célula sob o
              // mouse ganha um contorno forte e bem visível — o "indicador de
              // grade" só aparece onde o jogador/admin está de fato interagindo.
              fctx.strokeStyle = COLORS.borderHover;
              fctx.lineWidth = 1.8;
              fctx.stroke();
            }
          }
        };
        // Lago é animado: fica fora do cache do piso estático (redesenhado a cada frame).
        depthObjects.push({ type: 'floor', vx: rcx, vy: rcy, elevation: elev, render: renderFloor, live: isLakeCell(cell) });

        // ── Paredes Verticais (Laterais Expostas) ────────────────────────────
        // Só fecham a lacuna entre o topo do tile atual e o chão do vizinho
        // MAIS BAIXO — nenhuma parede é desenhada para vizinho da MESMA
        // altura (`nb === elev`), e nenhuma parede "traseira" é criada: só
        // existe UMA parede por fronteira exposta (sempre gerada pelo lado
        // mais alto), nunca duas paredes espelhadas na mesma aresta. Como a
        // câmera gira livremente em 360° (não apenas 2 lados fixos), as 4
        // direções (N/E/S/W) são verificadas, não só esquerda/direita.
        if (!isVoid && cell.type === 'Ramp') {
          // ── Sistema de Pilares: só as 2 faces da FRENTE (Leste/Sul) ─────────
          // Com a câmera travada em -60°/+60° (ROT_LIMIT), as arestas Norte/
          // Oeste são as "costas do pilar" — nunca ficam visíveis em nenhum
          // ângulo permitido. Removê-las da geometria elimina de vez a classe
          // inteira de bugs de "buraco atrás do conector"/paredes fantasmas
          // que vinham da tentativa de fechar 4 lados com vizinhos-espelho.
          {
            const rampGeo = computeRampGeometry(elev, neighborElev);
            const rLowH  = rampGeo.low * ELEV_UNIT;
            const rHighH = rampGeo.high * ELEV_UNIT;
            const rHeightAt = (idx: number) => rampCornerHeight(rampGeo, idx, rLowH, rHighH);

            const rampFaces: Array<{ dir: number; a: number; b: number; nb: number; nbIsRamp: boolean; shadeUp: string; shadeDown: string; mid: [number, number] }> = [
              { dir: 1, a: 1, b: 2, nb: neighborElev.e, nbIsRamp: neighborCells.e?.type === 'Ramp', shadeUp: COLORS.high_ground_E, shadeDown: COLORS.pit_wall_E, mid: EDGE_MID[1] },
              { dir: 2, a: 2, b: 3, nb: neighborElev.s, nbIsRamp: neighborCells.s?.type === 'Ramp', shadeUp: COLORS.high_ground_S, shadeDown: COLORS.pit_wall_S, mid: EDGE_MID[2] },
            ];
            for (const rf of rampFaces) {
              if (rf.nbIsRamp) continue; // vizinho também é rampa — continuação da trilha, sem parede
              const hA = rHeightAt(rf.a);
              const hB = rHeightAt(rf.b);
              const nH = rf.nb * ELEV_UNIT;
              if (nH >= Math.min(hA, hB)) continue; // vizinho cobre esta aresta inteira — nada exposto
              const isPit = rf.nb < 0;
              const pA = corners[rf.a];
              const pB = corners[rf.b];
              const rFace: WallFace = {
                p0: [pA[0], pA[1] - nH],
                p1: [pB[0], pB[1] - nH],
                p2: [pB[0], pB[1] - hB],
                p3: [pA[0], pA[1] - hA],
                shade: isPit ? rf.shadeDown : rf.shadeUp,
                isPit,
              };
              const [rwvx, rwvy] = rotatePoint(gx + rf.mid[0], gy + rf.mid[1], L.current.mapCx, L.current.mapCy, rot);
              depthObjects.push({ type: 'wall', vx: rwvx, vy: rwvy, elevation: elev, face: rFace });
            }
          }
        } else if (!isVoid && cell.type !== 'Lake') {
          // Chão padrão OU plataforma elevada (Nível 0/1/2) OU Buraco/Vala —
          // o piso já foi empurrado para `renderFloor` acima; aqui só
          // fecham-se as paredes verticais (Laterais Expostas) que ligam o
          // topo deste tile ao chão dos vizinhos mais baixos.
          // Paredes nos 4 lados, mas só as viradas para a câmera no ângulo
          // atual (normal da face apontando para baixo na tela). Antes só
          // Leste/Sul existiam — no Hub (giro de 90°) Norte/Oeste ficavam de
          // frente sem parede e o céu aparecia pelo buraco; e a face de trás
          // desenhada sobrava como uma "faixa" solta acima do platô.
          const faces: Array<{ dir: number; a: number; b: number; nb: number; nx: number; ny: number; nbIsRamp: boolean; mid: [number, number]; n: [number, number] }> = [
            { dir: 0, a: 0, b: 1, nb: neighborElev.n, nx: gx,   ny: gy-1, nbIsRamp: neighborCells.n?.type === 'Ramp', mid: EDGE_MID[0], n: [0, -1] },
            { dir: 1, a: 1, b: 2, nb: neighborElev.e, nx: gx+1, ny: gy,   nbIsRamp: neighborCells.e?.type === 'Ramp', mid: EDGE_MID[1], n: [1, 0] },
            { dir: 2, a: 2, b: 3, nb: neighborElev.s, nx: gx,   ny: gy+1, nbIsRamp: neighborCells.s?.type === 'Ramp', mid: EDGE_MID[2], n: [0, 1] },
            { dir: 3, a: 3, b: 0, nb: neighborElev.w, nx: gx-1, ny: gy,   nbIsRamp: neighborCells.w?.type === 'Ramp', mid: EDGE_MID[3], n: [-1, 0] },
          ];
          for (const f of faces) {
            if (f.nb >= elev) continue; // mesmo nível ou mais alto — NENHUMA parede (nem "traseira")
            // Normal da face na tela: y > 0 = de frente para a câmera; x < 0 = virada para a luz (esquerda).
            const [nrx, nry] = rotatePoint(f.n[0], f.n[1], 0, 0, rot);
            const neighborAtDir = f.dir === 0 ? neighborCells.n : f.dir === 1 ? neighborCells.e : f.dir === 2 ? neighborCells.s : neighborCells.w;
            const isRampLink = findClimbConnectorType(cell, gx, gy, neighborAtDir, f.nx, f.ny) === 'RAMP';
            // Face de trás fica escondida pelo próprio topo — exceto a Rampa, que
            // deita sobre o chão do vizinho e continua visível do outro lado.
            if (nrx + nry <= 0.01 && !isRampLink) continue;
            const litLeft = nrx - nry < 0;
            // O vizinho mais baixo é uma Rampa/Trilha — ela já faz a transição
            // suave entre os dois níveis; não desenha um paredão reto aqui,
            // ou cortaria visualmente a inclinação da rampa.
            if (f.nbIsRamp) continue;
            // Parede de "Buraco/Vala": o vizinho mais baixo está abaixo do
            // nível 0 (subterrâneo) — usa tons escuros de sombra interna em
            // vez do cliff externo de montanha.
            const isPit = f.nb < 0;
            const nH = f.nb * ELEV_UNIT;
            const H  = elev * ELEV_UNIT;
            const pA = corners[f.a];
            const pB = corners[f.b];
            // Via de Subida (Ponto A → Ponto B): se esta fronteira tiver uma
            // climb_connection definida no admin, troca o muro sólido por um
            // conector caminhável (Rampa/Escada/Corda) — ver draw unificado abaixo.
            const neighborCellAtDir = f.dir === 0 ? neighborCells.n : f.dir === 1 ? neighborCells.e : f.dir === 2 ? neighborCells.s : neighborCells.w;
            const connector = findClimbConnectorType(cell, gx, gy, neighborCellAtDir, f.nx, f.ny);

            // Direcionamento Automático da Rampa: NÃO é mais um quad vertical
            // na própria aresta (isso é o que lia como "desenhado de lado").
            // O plano se ESTENDE para dentro da célula vizinha mais baixa,
            // até a borda distante DELA, criando um chão inclinado de
            // verdade que segue o eixo exato N/S/E/W da conexão — a borda
            // baixa (p0/p1) fica na ponta B (chão do vizinho), a borda alta
            // (p2/p3) fica na ponta A (chão do platô), nunca vazando lateral.
            const [lowA, lowB] = connector === 'RAMP'
              ? [cellCorners(f.nx, f.ny)[f.a], cellCorners(f.nx, f.ny)[f.b]]
              : [pA, pB];
            // Laterais da rampa: triângulo (topo na borda do platô, ponta baixa
            // na borda distante do vizinho, pé no chão sob o topo) — fecha o
            // vão por onde antes aparecia o céu. Só as viradas para a câmera.
            const CG: Array<[number, number]> = [[0, 0], [1, 0], [1, 1], [0, 1]];
            const rampSides = (ia: number, ib: number) => {
              const d: [number, number] = [CG[ib][0] - CG[ia][0], CG[ib][1] - CG[ia][1]];
              const out: Array<[ScreenCorner, ScreenCorner, ScreenCorner]> = [];
              for (const [idx, low, nrm] of [[ia, lowA, [-d[0], -d[1]]], [ib, lowB, d]] as const) {
                const [sx, sy] = rotatePoint(nrm[0], nrm[1], 0, 0, rot);
                if (sx + sy <= 0.01) continue;
                const p = corners[idx];
                out.push([[p[0], p[1] - H], [low[0], low[1] - nH], [p[0], p[1] - nH]]);
              }
              return out;
            };

            const face: WallFace = {
              p0: [lowA[0], lowA[1] - nH],
              p1: [lowB[0], lowB[1] - nH],
              p2: [pB[0], pB[1] - H],
              p3: [pA[0], pA[1] - H],
              shade: isPit
                ? (litLeft ? COLORS.pit_wall_L : COLORS.pit_wall_E)
                : (litLeft ? COLORS.high_ground_L : COLORS.high_ground_E),
              isPit,
              connector,
              seed: gx * 73 + gy * 151 + f.dir * 17,
              topFloor: cell.floor === 'dirt' ? 'dirt' : 'grass',
              sides: connector === 'RAMP' ? rampSides(f.a, f.b) : undefined,
              back: nrx + nry <= 0.01,
            };
            const [wvx, wvy] = rotatePoint(gx + f.mid[0], gy + f.mid[1], L.current.mapCx, L.current.mapCy, rot);
            if (connector === 'RAMP') {
              // A rampa deita SOBRE a célula vizinha: entra na fila logo depois
              // do chão dela (senão aquele chão pintava por cima da metade de
              // baixo da rampa) e antes de quem está naquela célula.
              const [rvx, rvy] = rotatePoint(f.nx + 0.5, f.ny + 0.5, L.current.mapCx, L.current.mapCy, rot);
              depthObjects.push({ type: 'wall', vx: rvx, vy: rvy, elevation: f.nb + 0.5, face });
            } else {
              depthObjects.push({ type: 'wall', vx: wvx, vy: wvy, elevation: elev, face });
            }
          }
        }
      }
    }
    };

    // Piso ESTÁTICO (Hub): mapa plano + sem destaques/hover → o piso não muda
    // por frame. Só reconstrói a lista de piso quando o cache precisa ser
    // regerado (ângulo/zoom/tamanho novos); nos demais frames pula o loop de
    // ~centenas de células inteiro.
    const flatStatic = isFlatMap(map) && highlights.length === 0 && !cellHover;
    const terrainKey = flatStatic
      ? `m${mapId(map)}|${canvas.width}x${canvas.height}|${rot.toFixed(4)}|${zoom.toFixed(3)}`
      : '';
    const terrainReady = flatStatic && terrainKey === terrainCacheKey.current && !!terrainCache.current;
    if (!terrainReady) buildTerrainLayer();

    // ── Personagens, Objetos e Paredes/Pisos de Elevação (Depth Sorted) ──────
    // A partir daqui é só o loop ORIGINAL de entidades/obstáculos (sprites,
    // círculo de fallback, barra de HP, doodads 3D) — nenhuma linha deste
    // trecho foi alterada pelo trabalho de terreno acima.
    // Ordenação ÚNICA e obrigatória, aplicada a TODO o conteúdo do mapa
    // (pisos + paredes + obstáculos + personagens) junto: 1) profundidade
    // isométrica (X+Y projetado, `vx+vy`); 2) desempate por altura (Z ×0.9).
    // Só depois disso qualquer coisa é de fato desenhada — nenhum piso ou
    // parede tem prioridade fixa sobre o resto, então um tile mais próximo
    // da câmera sempre cobre corretamente o que está atrás dele, em
    // qualquer ângulo de rotação.
    const alive = Object.values(entities).filter(e => e.hp > 0);
    for (const entity of alive) {
      const pos = animPos.current.get(entity.id) ?? { x: entity.position_x, y: entity.position_y, bob: 0 };
      // Chave de profundidade no CENTRO da célula (cellX+0.5, cellY+0.5) —
      // mesma convenção do piso (`buildTerrainLayer` usa gx+0.5/gy+0.5).
      // Usar a quina crua (sem +0.5), como antes, deixava a entidade com uma
      // soma X+Y sistematicamente MENOR que a do piso da própria célula,
      // então ela ordenava "atrás" do próprio chão e o piso pintava por
      // cima dela — exatamente o bug de "chão sobrepondo entidade" relatado
      // ao girar a câmera (o desalinhamento de meio tile muda de sinal
      // conforme o ângulo de rotação).
      //
      // Durante a CAMINHADA, `pos` é fracionário (interpolado entre a célula
      // de origem e a de destino). Se a chave de profundidade usasse esse
      // valor fracionário direto, a entidade — no meio do passo — ficaria com
      // soma X+Y MENOR que a do piso da célula de destino e o chão pintaria
      // por cima do personagem enquanto ele anda ("o chão fica em cima do
      // personagem"). Ancorar a chave na célula mais próxima (a que a entidade
      // de fato ocupa naquele instante) mantém o personagem sempre à frente
      // do próprio piso. O DESENHO continua usando `pos` interpolado (linha do
      // loop de render abaixo) — só a ordenação é "quantizada" para o tile.
      const cellX = Math.round(pos.x);
      const cellY = Math.round(pos.y);
      const [rx, ry] = rotatePoint(cellX + 0.5, cellY + 0.5, L.current.mapCx, L.current.mapCy, rot);
      const elevation = getElevation(map.cells[cellY]?.[cellX]);
      depthObjects.push({ type: 'entity', id: entity.id, lx: pos.x, ly: pos.y, vx: rx, vy: ry, elevation, data: entity });
    }

    // NPCs de patrulha data-driven (systems/movement) — só existem no Hub e
    // apenas se `ambientNpcs` foi passado. Mesma quantização de profundidade
    // das entidades (célula mais próxima) para não brigar com o piso.
    const mvSys = ambientRef.current;
    if (mvSys) {
      for (const npc of mvSys.npcs()) {
        const p = npc.position;
        const cX = Math.round(p.x);
        const cY = Math.round(p.y);
        const [mrx, mry] = rotatePoint(cX + 0.5, cY + 0.5, L.current.mapCx, L.current.mapCy, rot);
        const mElev = getElevation(map.cells[cY]?.[cX]);
        depthObjects.push({ type: 'mover', id: npc.id, lx: p.x, ly: p.y, vx: mrx, vy: mry, elevation: mElev });
      }
    }

    for (let gy = 0; gy < map.height; gy++) {
      for (let gx = 0; gx < map.width; gx++) {
        const cell = map.cells[gy]?.[gx];
        const t = cell?.type;
        const elevation = getElevation(cell);
        // Billboarding: Montanha/Árvore/Rocha/Vegetação mágica SEMPRE
        // aparecem como "prop" 2D reto sobre piso plano — independente de
        // `elevation` guardado na célula (ex.: Montanha herda nível 2 do
        // pincel do admin, mas isso não vira mais bloco/prisma — só o doodad
        // fica maior/visualmente imponente). Usa a elevação VISUAL (sempre 0
        // para estes tipos) na chave de profundidade, batendo com o piso
        // plano que `buildTerrainLayer` desenhou para a mesma célula.
        if (BILLBOARD_PROP_TYPES.has(t ?? '')) {
          // Mesma correção da chave de profundidade: centro da célula
          // (gx+0.5, gy+0.5), igual ao piso — evita o doodad "sortear atrás"
          // do próprio chão e ser coberto por ele.
          const [rx, ry] = rotatePoint(gx + 0.5, gy + 0.5, L.current.mapCx, L.current.mapCy, rot);
          depthObjects.push({ type: 'obstacle', kind: t as any, lx: gx, ly: gy, vx: rx, vy: ry, elevation: 0 });
        } else if (t === 'npc_blacksmith' || t === 'npc_merchant' || t === 'npc_farmer' || t === 'battle_point') {
          const [rx, ry] = rotatePoint(gx + 0.5, gy + 0.5, L.current.mapCx, L.current.mapCy, rot);
          depthObjects.push({ type: 'obstacle', kind: t, lx: gx, ly: gy, vx: rx, vy: ry, elevation });
        }
      }
    }

    // Ordenação de profundidade (Painter's Algorithm ajustado para altura):
    // Y-Sorting Absoluto: a chave de profundidade é o screenY REAL projetado
    // na tela — não mais uma soma heurística de coordenadas de grade. Todo
    // elemento (piso, parede de elevação, obstáculo, entidade) usa a MESMA
    // fórmula do isoProject para sua posição-base: offY + (vx+vy)*TILE_H_HALF,
    // menos o deslocamento vertical real que a elevação aplica na tela
    // (elevation * ELEV_UNIT px, igual ao usado em todo o resto do desenho).
    // offY é constante para todos os objetos no frame, então pode ser
    // omitido do comparador sem alterar a ordenação relativa. Isso garante
    // que a ordem de desenho corresponda exatamente à posição vertical final
    // de cada elemento na tela, em QUALQUER ângulo de câmera — sem depender
    // de pesos arbitrários para desempate.
    //
    // CORREÇÃO: a altura NÃO pode subtrair da chave. Com `- elevation *
    // ELEV_UNIT`, um platô era desenhado ANTES do chão que fica até ~2
    // fileiras atrás dele, e esse chão pintava por cima do topo elevado
    // (sobrava só uma "faixa" do platô). A ordem certa é a profundidade no
    // chão (vx+vy) e, só no empate, o nível mais baixo primeiro — assim o que
    // é elevado sempre sobrepõe o que está mais baixo atrás/ao lado.
    // Personagens/objetos ganham +0.08: ficam por cima do piso e da rampa
    // (elevation + 0.5 → +0.05) da própria célula.
    const screenDepthKey = (obj: DepthObj): number =>
      (obj.vx + obj.vy) * TILE_H_HALF + obj.elevation * 0.1
      + (obj.type === 'entity' || obj.type === 'mover' || obj.type === 'obstacle' ? 0.08 : 0);
    depthObjects.sort((a, b) => screenDepthKey(a) - screenDepthKey(b));

    // ── Base da ilha flutuante — atrás de tudo, antes do piso ───────────────
    drawFloatingIslandBase(ctx, map.width, map.height, rot, L.current.mapCx, L.current.mapCy, offsetX, offsetY);

    // ── Cache do piso (grande ganho de performance no Hub) ──────────────────
    // Mapa PLANO + sem destaques → o piso é estático por ângulo/zoom. Renderiza
    // uma vez num canvas fora de tela; nos frames seguintes só faz `drawImage`
    // e nem monta a lista de piso. Os billboards (personagens/NPCs/árvores/
    // portal) desenham por cima — idêntico em terreno plano. Combate (com
    // elevação) nunca entra aqui.
    if (flatStatic) {
      if (!terrainReady) {
        let tc = terrainCache.current;
        if (!tc) { tc = document.createElement('canvas'); terrainCache.current = tc; }
        if (tc.width !== canvas.width || tc.height !== canvas.height) {
          tc.width = canvas.width; tc.height = canvas.height;
        }
        const tctx = tc.getContext('2d')!;
        tctx.clearRect(0, 0, tc.width, tc.height);
        tctx.save();
        tctx.scale(zoom, zoom);
        for (const obj of depthObjects) if (obj.type === 'floor' && !obj.live) obj.render(tctx);
        tctx.restore();
        terrainCacheKey.current = terrainKey;
      }
      ctx.drawImage(terrainCache.current!, 0, 0, canvas.width, canvas.height,
                    0, 0, canvas.width / zoom, canvas.height / zoom);
      // Lagos ficaram fora do cache — desenhados ao vivo (animação da água).
      for (let gy = 0; gy < map.height; gy++) {
        for (let gx = 0; gx < map.width; gx++) {
          const c = map.cells[gy]?.[gx];
          if (!isLakeCell(c)) continue;
          drawLake(ctx, cellCorners(gx, gy), getVisualElevation(c), gx, gy, now, lakeSidesOf(map.cells, gx, gy));
        }
      }
    }

    for (const obj of depthObjects) {
      if (obj.type === 'floor') {
        if (flatStatic) continue;   // já colado do cache
        // Piso (chão/rampa/lago + decal/contorno/destaque) — adiado até
        // aqui para respeitar a MESMA ordem de profundidade de paredes,
        // obstáculos e personagens (ver comentário acima do `.sort`).
        obj.render(ctx);
        continue;
      }
      if (obj.type === 'wall') {
        // Parede vertical de elevação (Auto-Tiling), desenhada aqui — dentro
        // do vetor ordenado por profundidade — para que um personagem no
        // chão base atrás da montanha fique oculto por ela, e um personagem
        // em cima da montanha sempre desenhe por cima da parede.
        // Cliff Face / Paredão de Pedra — bloco vertical RETO, 100% sólido e
        // opaco (Tiles Isométricos em Camadas / 2.5D Layering). Abandonamos
        // a deformação de malha (silhueta "jagged") do padrão anterior: o
        // caminho de vértices irregular podia se autointersectar
        // (bowtie/self-intersecting polygon), o que o Canvas 2D preenche com
        // "buracos" via regra par-ímpar — essa era a causa real dos
        // polígonos rasgados/transparentes/desproporcionais relatados. O
        // quadrilátero agora segue estritamente o perímetro p0→p1→p2→p3 sem
        // nenhum desvio de vértice.
        const f = obj.face;
        if (f.connector === 'RAMP') { drawWoodRamp(ctx, f); continue; }
        ctx.beginPath();
        ctx.moveTo(f.p0[0], f.p0[1]);
        ctx.lineTo(f.p1[0], f.p1[1]);
        ctx.lineTo(f.p2[0], f.p2[1]);
        ctx.lineTo(f.p3[0], f.p3[1]);
        ctx.closePath();

        if (f.isPit) {
          // Sombra interna do Buraco/Vala (Ambient Occlusion): `f.p0`/`f.p1`
          // ficam no nível do FUNDO (mais profundo, menos luz — mais
          // escuro); `f.p3`/`f.p2` ficam no nível da BORDA/rima, junto à
          // superfície (mais luz ambiente entra por ali — mais claro). A
          // versão anterior tinha essa direção INVERTIDA (borda mais escura
          // que o fundo), o que lia como profundidade ao contrário.
          const grad = ctx.createLinearGradient(f.p0[0], f.p0[1], f.p3[0], f.p3[1]);
          grad.addColorStop(0, '#050403');
          grad.addColorStop(1, mixHex(f.shade, '#ffffff', 0.14));
          ctx.fillStyle = grad;
        } else {
          // Paredão de Pedra/Falésia — volume 3D via gradiente vertical
          // (claro no topo, escuro na base), tom terroso/pedra bem definido,
          // sempre 100% opaco, sem clipping nem geometria extra. Também usado
          // como base estrutural por trás dos conectores Escada/Corda — a
          // subida é algo construído/fixado NA rocha, não a substitui.
          const topMid = lerpPt(f.p3, f.p2, 0.5);
          const botMid = lerpPt(f.p0, f.p1, 0.5);
          const grad = ctx.createLinearGradient(topMid[0], topMid[1], botMid[0], botMid[1]);
          grad.addColorStop(0,   mixHex(f.shade, '#ffffff', 0.22));
          grad.addColorStop(0.5, f.shade);
          grad.addColorStop(1,   mixHex(f.shade, '#000000', 0.4));
          ctx.fillStyle = grad;
        }
        ctx.fill();
        ctx.strokeStyle = COLORS.border;
        ctx.lineWidth = 0.7;
        ctx.stroke();

        if (f.connector === 'STAIRS') {
          // Via de Subida — Escada de Madeira: NÃO é mais um preenchimento
          // achatado cobrindo a parede inteira — é um objeto centralizado
          // (32%–68% da largura) sobre a rocha: duas hastes verticais +
          // degraus horizontais, deixando a falésia visível nas laterais.
          const RAIL_L = 0.32, RAIL_R = 0.68;
          const leftTop  = lerpPt(f.p3, f.p2, RAIL_L), leftBot  = lerpPt(f.p0, f.p1, RAIL_L);
          const rightTop = lerpPt(f.p3, f.p2, RAIL_R), rightBot = lerpPt(f.p0, f.p1, RAIL_R);

          // Hastes (madeira escura)
          ctx.strokeStyle = COLORS.stairs_riser;
          ctx.lineWidth = 2.4;
          ctx.beginPath(); ctx.moveTo(leftTop[0], leftTop[1]);   ctx.lineTo(leftBot[0], leftBot[1]);   ctx.stroke();
          ctx.beginPath(); ctx.moveTo(rightTop[0], rightTop[1]); ctx.lineTo(rightBot[0], rightBot[1]); ctx.stroke();

          // Degraus (madeira clara), amarrados entre as duas hastes
          const RUNGS = 4;
          ctx.strokeStyle = COLORS.stairs_tread;
          ctx.lineWidth = 2;
          for (let i = 0; i < RUNGS; i++) {
            const t = (i + 0.5) / RUNGS;
            const rl = lerpPt(leftBot, leftTop, t);
            const rr = lerpPt(rightBot, rightTop, t);
            ctx.beginPath();
            ctx.moveTo(rl[0], rl[1]);
            ctx.lineTo(rr[0], rr[1]);
            ctx.stroke();
          }
        } else if (f.connector === 'ROPE') {
          // Via de Subida — Corda de Escalada: decal "pendurado", não papel
          // de parede — pino de madeira ancorado na borda do platô (Ponto B,
          // topo) e a corda desce com uma leve curvatura (balanço natural)
          // até o chão do vizinho mais baixo (Ponto A), ocupando só o
          // centro da parede — a rocha segue visível ao redor.
          const topC = lerpPt(f.p3, f.p2, 0.5);
          const botC = lerpPt(f.p0, f.p1, 0.5);
          // Seed determinístico (por posição de tela) para o leve balanço —
          // evita todas as cordas do mapa ficarem idênticas/estáticas.
          const sway = (Math.sin(topC[0] * 0.13 + topC[1] * 0.27) ) * 5;
          const ctrl: ScreenCorner = [(topC[0] + botC[0]) / 2 + sway, (topC[1] + botC[1]) / 2];

          // Pino/suporte de madeira ancorado no topo
          ctx.fillStyle = COLORS.rope_dark;
          ctx.fillRect(topC[0] - 3, topC[1] - 2, 6, 4);

          ctx.strokeStyle = COLORS.rope;
          ctx.lineWidth = 2.2;
          ctx.beginPath();
          ctx.moveTo(topC[0], topC[1]);
          ctx.quadraticCurveTo(ctrl[0], ctrl[1], botC[0], botC[1]);
          ctx.stroke();

          // Nós ao longo da curva (De Casteljau simplificado)
          const KNOTS = 4;
          for (let i = 1; i < KNOTS; i++) {
            const t = i / KNOTS;
            const px = (1 - t) * (1 - t) * topC[0] + 2 * (1 - t) * t * ctrl[0] + t * t * botC[0];
            const py = (1 - t) * (1 - t) * topC[1] + 2 * (1 - t) * t * ctrl[1] + t * t * botC[1];
            ctx.beginPath();
            ctx.arc(px, py, 2, 0, Math.PI * 2);
            ctx.fillStyle = COLORS.rope_dark;
            ctx.fill();
          }
        } else if (!f.isPit && !f.connector) {
          drawCliffDetails(ctx, f);
        }
        continue;
      }

      if (obj.type === 'obstacle') {
        const [rx, ry] = rotatePoint(obj.lx + 0.5, obj.ly + 0.5, L.current.mapCx, L.current.mapCy, rot);
        const [sx, sy] = isoProject(rx, ry, offsetX, offsetY - TILE_H_HALF);

        if (obj.kind === 'Tree' || obj.kind === 'tree') {
          draw3DTree(ctx, sx, sy, Math.sin(now / 900 + obj.lx * 0.5 + obj.ly * 0.5) * 4, rot);
        } else if (obj.kind === 'Mountain') {
          draw3DMountain(ctx, sx, sy, now);
        } else if (obj.kind === 'Rock') {
          draw3DRock(ctx, sx, sy, obj.lx, obj.ly);
        } else if (obj.kind === 'Vision_Obstacle') {
          drawVisionObstacle(ctx, sx, sy, now, obj.lx, obj.ly);
        } else if (obj.kind === 'npc_blacksmith' || obj.kind === 'npc_merchant' || obj.kind === 'npc_farmer') {
          // Ferreiro / Mercadora são NPCs data-driven (systems/movement): andam,
          // trabalham e GIRAM junto com a câmera. Quando o sistema ambiente tem
          // esse NPC, o ramo 'mover' abaixo desenha tudo — aqui não fazemos nada
          // (evita render duplicado).
          const role = (obj.kind === 'npc_merchant' || obj.kind === 'npc_farmer') ? 'merchant' : 'blacksmith';
          const label = (L.current.npcNames?.[role] ?? NPC_DISPLAY_NAMES[role] ?? '').toUpperCase();
          if (ambientRef.current?.getNpc(role)) continue;
          // Fallback (Preview Isométrico do admin, sistema não carregado):
          // billboard do sprite escolhido, ou um marcador de texto mínimo.
          const npcCfg = L.current.npcSprites?.[role];
          const npcImg = npcCfg?.sprite ? npcImageCache.current.get(npcCfg.sprite) : undefined;
          const groundY = sy + TILE_H_HALF;
          if (npcImg) {
            // A imagem enviada é um spritesheet (colunas × linhas do painel).
            // Coluna 0 (parado) + linha FIXA NPC_FACING_ROW — mesmo frame em
            // qualquer ângulo de rotação do mapa.
            const cols = Math.max(1, npcCfg?.cols ?? 1);
            const rows = Math.max(1, npcCfg?.rows ?? 1);
            const fw = npcImg.width / cols;
            const fh = npcImg.height / rows;
            const npcRow = Math.min(NPC_FACING_ROW, rows - 1);
            drawPropBillboard(
              ctx,
              npcImg,
              sx + (npcCfg?.offset_x ?? 0),
              groundY + (npcCfg?.offset_y ?? 0),
              NPC_BLACKSMITH_DRAW_H * (npcCfg?.scale ?? 1),
              label,
              { sx: 0, sy: npcRow * fh, sw: fw, sh: fh },
            );
          } else {
            ctx.beginPath();
            ctx.ellipse(sx, groundY + 2, 16, 6, 0, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(0,0,0,0.3)';
            ctx.fill();
          }
          drawNpcNameTag(ctx, L.current.npcNames?.[role] ?? NPC_DISPLAY_NAMES[role] ?? '', sx, groundY - NPC_BLACKSMITH_DRAW_H * (npcImg ? (npcCfg?.scale ?? 1) : 0.5) - 8);
        } else if (obj.kind === 'battle_point') {
          // Corpo 3D do Portal — agora participa do mesmo vetor ordenado por
          // profundidade (vx+vy) que entidades e demais obstáculos, então
          // fica corretamente na frente/atrás do jogador/NPC em qualquer
          // um dos 4 ângulos de câmera, sem cortes.
          // Portal com rótulo próprio (ex.: o portal de Missão aberto após a
          // vitória) troca a placa e não tem Guardião.
          const portalCell  = L.current.map.cells?.[obj.ly]?.[obj.lx];
          const portalLabel = portalCell?.portal_label;
          if (portalCell?.portal_style === 'mission') {
            // Portal de missão: visual próprio. Recém-aberto (nível vencido) →
            // animação de surgimento + panorâmica rápida da câmera até ele.
            let age = Infinity;
            if (portalCell.portal_spawn) {
              const key = `${obj.lx},${obj.ly}`;
              if (!portalSpawnAt.current.has(key)) {
                portalSpawnAt.current.set(key, now);
                portalFocus.current = { x: obj.lx, y: obj.ly, until: now + 1900, back: null };
              }
              age = now - portalSpawnAt.current.get(key)!;
            }
            drawMissionPortal(ctx, sx, sy, now, portalLabel ?? '', age);
          } else {
            draw3DPortal(ctx, sx, sy, now, rot, portalLabel ?? undefined);
          }

          // Guardião do Portal — só no Preview Isométrico do admin (o sistema
          // ambiente não está carregado ali). No jogo real, o ramo 'mover'
          // desenha esse NPC animado.
          if (!portalLabel && !ambientRef.current?.getNpc('guardian')) {
            const gCfg = L.current.npcSprites?.guardian;
            const gImg = gCfg?.sprite ? npcImageCache.current.get(gCfg.sprite) : undefined;
            if (gImg) {
              const gcols = Math.max(1, gCfg?.cols ?? 1);
              const grows = Math.max(1, gCfg?.rows ?? 1);
              const gfw = gImg.width / gcols;
              const gfh = gImg.height / grows;
              const grow = Math.min(NPC_FACING_ROW, grows - 1);
              drawPropBillboard(
                ctx,
                gImg,
                sx + (gCfg?.offset_x ?? 0),
                sy + TILE_H_HALF + (gCfg?.offset_y ?? 0),
                NPC_BLACKSMITH_DRAW_H * (gCfg?.scale ?? 1),
                (L.current.npcNames?.guardian ?? NPC_DISPLAY_NAMES.guardian ?? '').toUpperCase(),
                { sx: 0, sy: grow * gfh, sw: gfw, sh: gfh },
              );
            }
            drawNpcNameTag(
              ctx,
              L.current.npcNames?.guardian ?? NPC_DISPLAY_NAMES.guardian ?? '',
              sx,
              sy + TILE_H_HALF - NPC_BLACKSMITH_DRAW_H * (gImg ? (gCfg?.scale ?? 1) : 0.7) - 8,
            );
          }
        }
        continue;
      }

      if (obj.type === 'mover') {
        // NPC data-driven (systems/movement). Billboard sem barra de HP; o
        // frame vem de MovementComponent.getFrame(rot) — a direção gira junto
        // com a câmera igual ao jogador.
        const npc = ambientRef.current?.getNpc(obj.id);
        const [mrx, mry] = rotatePoint(obj.lx + 0.5, obj.ly + 0.5, L.current.mapCx, L.current.mapCy, rot);
        const [msx, msy] = isoProject(mrx, mry, offsetX, offsetY - TILE_H_HALF);
        const mGroundY = msy + TILE_H_HALF - obj.elevation * ELEV_UNIT;

        ctx.beginPath();
        ctx.ellipse(msx, mGroundY + 3, 18, 7, 0, 0, Math.PI * 2);
        ctx.fillStyle = softShadows ? 'rgba(0,0,0,0.24)' : COLORS.shadow;
        ctx.fill();

        // Imagem: SÓ o sprite escolhido/enviado pelo admin p/ este NPC
        // (npcSprites). Sem escolha explícita, NUNCA cai numa folha de classe
        // genérica — vai direto pro marcador mínimo no branch `else` abaixo.
        const npcCfg = L.current.npcSprites?.[obj.id];
        const uploaded = npcCfg?.sprite ? npcImageCache.current.get(npcCfg.sprite) : undefined;
        const img: HTMLImageElement | HTMLCanvasElement | undefined = uploaded;
        const cols = Math.max(1, npcCfg?.cols ?? 1);
        const rows = Math.max(1, npcCfg?.rows ?? 1);

        const fr = npc?.getFrame(rot);
        // Ajuste fino por célula (mesma convenção do combate): "r{lin}c{col}".
        const ov = fr ? npcCfg?.frame_overrides?.[`r${Math.min(fr.row, rows - 1)}c${Math.min(fr.col, cols - 1)}`] : undefined;
        const drawScale = ov?.scale ?? npcCfg?.scale ?? 1;
        const offX = ov?.offsetX ?? npcCfg?.offset_x ?? 0;
        const offY = ov?.offsetY ?? npcCfg?.offset_y ?? 0;

        if (img && fr) {
          const fw = img.width / cols;
          const fh = img.height / rows;
          const sxFrame = Math.min(fr.col, cols - 1) * fw;
          const syFrame = Math.min(fr.row, rows - 1) * fh;
          const drawH = NPC_BLACKSMITH_DRAW_H * drawScale;
          const drawW = drawH * (fw / fh);
          // efeito da ação em andamento (forja / conjuração / colheita)
          if (npc?.acting) {
            drawNpcActionFx(
              ctx,
              npc.actionName,
              msx + offX,
              mGroundY + offY,
              drawW,
              drawH,
              now,
            );
          }
          ctx.drawImage(img, sxFrame, syFrame, fw, fh, msx - drawW / 2 + offX, mGroundY - drawH + offY, drawW, drawH);
          drawNpcNameTag(ctx, L.current.npcNames?.[obj.id] ?? NPC_DISPLAY_NAMES[obj.id] ?? '', msx, mGroundY - drawH + offY - 6);
        } else {
          ctx.beginPath();
          ctx.arc(msx, mGroundY - 14, 7, 0, Math.PI * 2);
          ctx.fillStyle = '#7a5cff';
          ctx.fill();
          drawNpcNameTag(ctx, L.current.npcNames?.[obj.id] ?? NPC_DISPLAY_NAMES[obj.id] ?? '', msx, mGroundY - 28);
        }
        continue;
      }

      const entity = obj.data;
      const pos = animPos.current.get(entity.id) ?? { x: entity.position_x, y: entity.position_y, bob: 0 };
      const [rx, ry] = rotatePoint(pos.x + 0.5, pos.y + 0.5, L.current.mapCx, L.current.mapCy, rot);
      const [sx, sy] = isoProject(rx, ry, offsetX, offsetY - TILE_H_HALF);
      
      const elev = obj.elevation * ELEV_UNIT;
      
      const cx      = sx;
      const groundY = sy + TILE_H_HALF - elev;
      const bob     = pos.bob ?? 0;

      // Shadow — Sistema de Clima: Pôr do Sol projeta uma sombra bem mais
      // longa e deslocada (luz rasante/baixa no horizonte); dias
      // Nublados/Chuvosos suavizam bastante o contato (luz difusa, sem sol
      // direto para projetar contorno forte).
      ctx.beginPath();
      if (isSunsetLight) {
        ctx.ellipse(cx + 14, groundY + 3, 40, 7, 0.55, 0, Math.PI * 2);
      } else {
        ctx.ellipse(cx, groundY + 3, 22, 8, 0, 0, Math.PI * 2);
      }
      ctx.fillStyle = softShadows ? 'rgba(0,0,0,0.28)' : COLORS.shadow;
      ctx.fill();

      const sc = entity.sprite ? spriteCache.current.get(entity.sprite) : undefined;

      if (sc) {
        const spriteCfg: SpriteSheetCfg = {
          cols:    entity.sprite_config?.cols || SPRITE_COLS,
          rows:    entity.sprite_config?.rows || SPRITE_ROWS,
          scale:   entity.sprite_config?.scale || 1.0,
          offsetX: entity.sprite_config?.offsetX || 0,
          offsetY: entity.sprite_config?.offsetY || 0,
          rowOrder:       entity.sprite_config?.rowOrder ?? null,
          frameOverrides: entity.sprite_config?.frameOverrides ?? null,
        };

        // Caminhando? Enquanto houver um AnimTarget ativo (já começou e ainda
        // não terminou). `walkElapsed` mede desde o início do deslocamento —
        // usado só pra ciclar as colunas de passada do spritesheet.
        const at          = animTarget.current.get(entity.id);
        const atTotal     = at ? (at.pts.length - 1) * at.stepMs : 0;
        const walkElapsed = at ? (now - at.t0) : Infinity;
        const isWalking   = at !== undefined && walkElapsed >= 0 && walkElapsed < atTotal;

        // Logical delta -> Visual Row
        let ldelta = entityDir.current.get(entity.id) ?? { dx: 1, dy: 0 };

        if (!isWalking) {
          let closestDist = Infinity;
          let closestEnemy: CombatEntity | null = null;
          for (const other of alive) {
            if (other.team !== entity.team && other.hp > 0) {
              const dist = Math.abs(other.position_x - entity.position_x) + Math.abs(other.position_y - entity.position_y);
              if (dist < closestDist) {
                closestDist = dist;
                closestEnemy = other;
              }
            }
          }
          if (closestEnemy) {
            ldelta = {
              dx: closestEnemy.position_x - entity.position_x,
              dy: closestEnemy.position_y - entity.position_y
            };
            if (ldelta.dx === 0 && ldelta.dy === 0) ldelta = { dx: 1, dy: 0 };
          }
        }

        const dirIndex = getDirRow(ldelta.dx, ldelta.dy, rot);

        let col = 0;
        if (isWalking && spriteCfg.cols > 1) {
            const frameMs = moveCfgRef.current.walkFrameMs;
            if (spriteCfg.cols >= 3) {
               col = (Math.floor(walkElapsed / frameMs) % 2) + 1;
            } else {
               col = (Math.floor(walkElapsed / frameMs) % 2);
            }
        }

        // Desenho do frame — mesma rotina que o preview do painel admin usa.
        const { drawW, drawY } = drawSpriteFrame(ctx, sc, spriteCfg, {
          dirIndex, col, groundX: cx, groundY, bob, baseDrawH: SPRITE_DRAW_H,
        });

        const ringColor = entity.id === myEntityId ? '#ffff00'
                        : entity.team === 'player'  ? '#4488ff'
                        :                             '#ff4444';
        ctx.beginPath();
        ctx.ellipse(cx, groundY + 5, drawW * 0.38, 7, 0, 0, Math.PI * 2);
        ctx.strokeStyle = ringColor;
        ctx.lineWidth   = entity.id === myEntityId ? 2.5 : 1.5;
        ctx.stroke();

        // HP bar
        const barW  = TILE_W - 4;
        const barH  = 5;
        const barX  = cx - barW / 2;
        const barY  = drawY - 6;

        // Name text
        ctx.font = '10px sans-serif';
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        // Draw shadow/outline for readability
        ctx.strokeStyle = 'rgba(0,0,0,0.8)';
        ctx.lineWidth = 2;
        ctx.strokeText(entity.name, cx, barY - 4);
        ctx.fillText(entity.name, cx, barY - 4);

        // Barra de vida — escondida quando hp_max <= 0 (jogador no Hub: não há
        // combate; a energia fica só na barra do topo do mapa).
        if (entity.hp_max > 0) {
          const hpPct = Math.max(0, entity.hp / entity.hp_max);
          ctx.fillStyle = '#330000';
          ctx.fillRect(barX, barY, barW, barH);
          ctx.fillStyle =
            hpPct > 0.5  ? '#44ff44' :
            hpPct > 0.25 ? '#ffaa00' :
                           '#ff2222';
          ctx.fillRect(barX, barY, Math.max(0, Math.floor(barW * hpPct)), barH);
          ctx.strokeStyle = 'rgba(0,0,0,0.45)';
          ctx.lineWidth   = 0.5;
          ctx.strokeRect(barX, barY, barW, barH);
        }

      } else {
        const r  = 20;
        const cy = groundY - r - bob;

        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = entity.team === 'player' ? COLORS.player : COLORS.monster;
        ctx.fill();

        if (entity.id === myEntityId) {
          ctx.strokeStyle = '#ffff00';
          ctx.lineWidth   = 2.5;
          ctx.stroke();
        }

        // HP bar above circle
        const barW  = TILE_W - 6;
        const barH  = 5;
        const barX  = cx - barW / 2;
        const barY  = cy - r - 8;

        // Name text
        ctx.font = '10px sans-serif';
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        // Draw shadow/outline for readability
        ctx.strokeStyle = 'rgba(0,0,0,0.8)';
        ctx.lineWidth = 2;
        ctx.strokeText(entity.name, cx, barY - 4);
        ctx.fillText(entity.name, cx, barY - 4);

        if (entity.hp_max > 0) {
          const hpPct = Math.max(0, entity.hp / entity.hp_max);
          ctx.fillStyle = '#330000';
          ctx.fillRect(barX, barY, barW, barH);
          ctx.fillStyle =
            hpPct > 0.5  ? '#44ff44' :
            hpPct > 0.25 ? '#ffaa00' :
                           '#ff2222';
          ctx.fillRect(barX, barY, Math.max(0, Math.floor(barW * hpPct)), barH);
          ctx.strokeStyle = 'rgba(0,0,0,0.45)';
          ctx.lineWidth   = 0.5;
          ctx.strokeRect(barX, barY, barW, barH);
        }

        // Name initial
        ctx.fillStyle    = COLORS.text;
        ctx.font         = 'bold 11px sans-serif';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(entity.name.charAt(0).toUpperCase(), cx, cy);
      }
    }

    // ── Fauna ambiental data-driven (src/systems/movement + ambient_birds.json)
    // Simulação (spawn/voo/despawn) roda no `tick`; aqui só DESENHAMOS,
    // traduzindo do espaço do viewport visível para o canvas (escalado).
    // ── Tempestade (missões): casas marcadas ⚡ e o raio caindo ───────────────
    const { stormMarks: marks, stormStrikes: strikes } = L.current;
    if (marks?.length || strikes?.length) {
      const tNow = performance.now();
      const cellTop = (x: number, y: number) => {
        const cs = cellCorners(x, y);
        const z = getVisualElevation(map.cells[y]?.[x]) * ELEV_UNIT;
        return { cs, z, cx: (cs[0][0] + cs[2][0]) / 2, cy: (cs[0][1] + cs[2][1]) / 2 - z };
      };
      const pulse = 0.45 + 0.2 * Math.sin(tNow / 170);
      for (const m of marks ?? []) {
        const { cs, z, cx, cy } = cellTop(m.x, m.y);
        ctx.beginPath();
        cs.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py - z) : ctx.lineTo(px, py - z)));
        ctx.closePath();
        ctx.fillStyle = `rgba(255, 200, 40, ${pulse})`;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 245, 170, 0.95)';
        ctx.lineWidth = 2.2;
        ctx.stroke();
        // Raiozinho vetorial (emoji varia por fonte/navegador)
        ctx.beginPath();
        ctx.moveTo(cx + 2, cy - 11);
        ctx.lineTo(cx - 5, cy + 1);
        ctx.lineTo(cx, cy + 1);
        ctx.lineTo(cx - 3, cy + 11);
        ctx.lineTo(cx + 6, cy - 2);
        ctx.lineTo(cx + 1, cy - 2);
        ctx.closePath();
        ctx.fillStyle = '#fff6b8';
        ctx.fill();
        ctx.strokeStyle = 'rgba(120, 80, 0, 0.85)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      const key = JSON.stringify(strikes ?? []);
      if (strikeAnim.current.key === null) strikeAnim.current = { key, t0: -Infinity }; // não reanima ao abrir a tela
      else if (key !== strikeAnim.current.key) strikeAnim.current = { key, t0: tNow };
      const age = tNow - strikeAnim.current.t0;
      const DUR = 900;
      if (strikes?.length && age < DUR) {
        const a = 1 - age / DUR;
        for (const st of strikes) {
          const { cx, cy } = cellTop(st.x, st.y);
          ctx.save();
          ctx.strokeStyle = `rgba(255, 255, 220, ${a})`;
          ctx.shadowColor = '#fff6a0';
          ctx.shadowBlur = 18;
          ctx.lineWidth = 3;
          ctx.beginPath();
          const top = cy - 280, SEG = 7;
          ctx.moveTo(cx, top);
          for (let i = 1; i <= SEG; i++) {
            const jitter = i === SEG ? 0 : (((st.x * 31 + st.y * 17 + i * 13) % 2) ? 1 : -1) * (8 + ((i * 7) % 10));
            ctx.lineTo(cx + jitter, top + ((cy - top) * i) / SEG);
          }
          ctx.stroke();
          const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 34);
          g.addColorStop(0, `rgba(255, 250, 200, ${0.9 * a})`);
          g.addColorStop(1, 'rgba(255, 220, 90, 0)');
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(cx, cy, 34, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }
    }

    const amb = ambientRef.current;
    if (amb) {
      const sa = scrollRef.current;
      const visX = (sa?.scrollLeft ?? 0) / zoom;
      const visY = (sa?.scrollTop ?? 0) / zoom;
      // Sombra só sobre célula caminhável: desfaz a projeção isométrica e a
      // rotação para achar a célula sob o ponto — fora do mapa (céu/nuvens)
      // ou sobre obstáculo/lago, o pássaro voa sem sombra.
      const unrotCos = Math.cos(-rot), unrotSin = Math.sin(-rot);
      const hasGroundAt = (x: number, y: number) => {
        const a = (x + visX - offsetX) / TILE_W_HALF;
        const b = (y + visY - offsetY) / TILE_H_HALF;
        const rx = (a + b) / 2, ry = (b - a) / 2;
        const gx = Math.floor(L.current.mapCx + rx * unrotCos - ry * unrotSin);
        const gy = Math.floor(L.current.mapCy + rx * unrotSin + ry * unrotCos);
        const c = map.cells[gy]?.[gx];
        return !!c && !WALK_BLOCKING.has(c.type);
      };
      ctx.save();
      ctx.translate(visX, visY);
      for (const bird of amb.birds()) drawBird(ctx, bird, hasGroundAt);
      for (const eagle of amb.eagles()) drawEagle(ctx, eagle, hasGroundAt);
      ctx.restore();
    }

    // ── Entrada no portal de missão: escuridão fechando sobre o portal ─────
    // Um "buraco" de luz centrado no portal encolhe até sumir; o brilho do
    // portal é o último a apagar. (O App cobre a tela inteira no final.)
    const warp = L.current.portalWarp;
    if (warp) {
      const p  = Math.min(1, (performance.now() - warp.t0) / warp.durationMs);
      const e  = p * p; // ease-in: começa devagar, fecha rápido no fim
      const cs = cellCorners(warp.x, warp.y);
      const wz = getVisualElevation(map.cells[warp.y]?.[warp.x]) * ELEV_UNIT;
      const px = (cs[0][0] + cs[2][0]) / 2;
      const py = (cs[0][1] + cs[2][1]) / 2 - wz - 30;
      const W  = ctx.canvas.width / zoom, H = ctx.canvas.height / zoom;
      const r  = Math.hypot(W, H) * (1 - e) + 8;
      const dark = ctx.createRadialGradient(px, py, r * 0.3, px, py, r);
      dark.addColorStop(0, 'rgba(0,0,0,0)');
      dark.addColorStop(1, `rgba(0,0,0,${(0.6 + 0.4 * e).toFixed(3)})`);
      ctx.fillStyle = dark;
      ctx.fillRect(-W, -H, W * 3, H * 3);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const gr = 34 + 26 * e;
      const glow = ctx.createRadialGradient(px, py, 0, px, py, gr);
      glow.addColorStop(0, `rgba(210,160,255,${(0.55 * (1 - 0.7 * p)).toFixed(3)})`);
      glow.addColorStop(1, 'rgba(120,60,220,0)');
      ctx.fillStyle = glow;
      ctx.beginPath(); ctx.arc(px, py, gr, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }

    ctx.restore();
  }, []);

  // ── Plano de Fundo Medieval Dinâmico (Parallax) ────────────────────────────
  // Camada independente, redesenhada a cada frame do RAF, atrás da grade
  // isométrica. O deslocamento (`shiftPx`) combina a rotação contínua da
  // câmera com o pan/scroll do mapa, e cada camada interna do tema aplica
  // sua própria fração de velocidade — efeito Parallax sem interferir nos
  // blocos/objetos reais do jogo (canvas totalmente separado, sem clique).
  const drawBackground = useCallback(() => {
    const bgCanvas  = bgCanvasRef.current;
    const wrapperEl = wrapperRef.current;
    if (!bgCanvas || !wrapperEl) return;

    const cw = wrapperEl.clientWidth;
    const ch = wrapperEl.clientHeight;
    if (cw <= 0 || ch <= 0) return;

    if (bgCanvas.width !== cw || bgCanvas.height !== ch) {
      bgCanvas.width  = cw;
      bgCanvas.height = ch;
    }

    const ctx = bgCanvas.getContext('2d');
    if (!ctx) return;

    // Toggle "Exibir Fundo (Céu)": desligado, nada de cenário parallax
    // animado — só um preenchimento sólido neutro atrás da grade.
    if (!L.current.backgroundEnabled) {
      ctx.clearRect(0, 0, cw, ch);
      ctx.fillStyle = '#6fa3ee';
      ctx.fillRect(0, 0, cw, ch);
      return;
    }

    const now     = performance.now();
    const rot     = rotRef.current;
    const scrollX = scrollRef.current?.scrollLeft ?? 0;
    const scrollY = scrollRef.current?.scrollTop  ?? 0;
    // Rotação (0–2π) pesa mais forte (gira o "horizonte"); pan pesa mais leve.
    const shiftPx = rot * 260 + scrollX * 0.6 + scrollY * 0.15;

    // ── Horizonte Dinâmico (Alinhamento com o Ponto de Fuga Real) ───────────
    // Em vez de uma fração fixa arbitrária, mede a posição REAL do canvas do
    // grid isométrico dentro do wrapper — o topo do canvas é onde a célula
    // mais "de trás"/distante da matriz de tiles projeta — e alinha a linha
    // do horizonte do cenário exatamente ali. Reage a zoom, scroll e
    // redimensionamento da janela automaticamente.
    let horizonY = 0.56;
    const gridCanvas = canvasRef.current;
    if (gridCanvas) {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const gridRect = gridCanvas.getBoundingClientRect();
      if (wrapperRect.height > 0) {
        const topFrac = (gridRect.top - wrapperRect.top) / wrapperRect.height;
        // A paisagem distante (montanhas/árvores) deve ficar LOGO ACIMA do
        // topo real da grade, nunca empurrada para dentro dela — um pequeno
        // deslocamento negativo garante que o horizonte pare um pouco antes
        // do vértice mais distante da matriz de tiles, e a extensão de chão
        // (mesma cor do piso) cubra o resto, incluindo toda a área por trás
        // da própria grade.
        horizonY = topFrac - 0.03;
      }
    }

    drawParallaxBackground(ctx, cw, ch, now, shiftPx, L.current.backgroundTheme, horizonY);
  }, []);

  // ── Follow-cam + deslize suave da câmera ──────────────────────────────────
  // 1) Zona morta: o jogador anda livre pela região central do monitor (35%
  //    em cada eixo). Ao passar dessa margem, a follow-cam MIRA o centro no
  //    jogador (histerese em 4%). Só liga depois do 1º passo — respeita o
  //    enquadramento inicial (mapa inteiro / cena da batalha).
  // 2) O scroll então DESLIZA até o alvo com ease-out (fração baixa por
  //    frame) + teto de velocidade — movimento calmo, nunca um salto seco,
  //    em qualquer mapa (hub ou batalha).
  const followCam = useCallback((now: number) => {
    const sa = scrollRef.current;
    if (!sa) return;

    // Arrastando a câmera à mão → o jogador está no controle, sem alvo.
    if (isRightDragging.current || isLeftDragging.current) {
      camTarget.current = null;
      followActive.current = false;
      return;
    }

    const viewW = sa.clientWidth, viewH = sa.clientHeight;
    if (viewW === 0 || viewH === 0) return;

    // ── (0) portal de missão recém-aberto: panorâmica até ele e volta ─────
    const pf = portalFocus.current;
    if (pf) {
      if (pf.back === null) pf.back = { x: sa.scrollLeft, y: sa.scrollTop };
      if (now < pf.until) {
        const z = zoomRef.current || 1;
        const [rx, ry] = rotatePoint(pf.x + 0.5, pf.y + 0.5, L.current.mapCx, L.current.mapCy, rotRef.current);
        const [psx, psy] = isoProject(rx, ry, L.current.offsetX, L.current.offsetY - TILE_H_HALF);
        camTarget.current = { x: psx * z - viewW / 2, y: (psy - 30) * z - viewH / 2 };
        followActive.current = false;
      } else {
        camTarget.current = pf.back;
        portalFocus.current = null;
      }
    }

    // ── (1) follow-cam decide o alvo ──────────────────────────────────────
    const followEnabled = !portalFocus.current && playerHasMoved.current && now >= followSuspendTo.current;
    if (followEnabled) {
      const pid = L.current.myEntityId || '';
      const player = L.current.entities[pid];
      if (player && player.hp > 0) {
        const z = zoomRef.current || 1;
        const p = animPos.current.get(pid) ?? { x: player.position_x, y: player.position_y };
        const [rx, ry] = rotatePoint(p.x + 0.5, p.y + 0.5, L.current.mapCx, L.current.mapCy, rotRef.current);
        const [psx, psy] = isoProject(rx, ry, L.current.offsetX, L.current.offsetY - TILE_H_HALF);

        const dxc = (psx * z) - (sa.scrollLeft + viewW / 2);
        const dyc = (psy * z) - (sa.scrollTop + viewH / 2);

        if (Math.abs(dxc) > viewW * 0.35 || Math.abs(dyc) > viewH * 0.35) {
          followActive.current = true;
        }
        if (followActive.current) {
          // mira o jogador ao vivo (a câmera trilha um pouco atrás — natural)
          camTarget.current = { x: psx * z - viewW / 2, y: psy * z - viewH / 2 };
          if (Math.abs(dxc) < viewW * 0.04 && Math.abs(dyc) < viewH * 0.04) {
            followActive.current = false;
          }
        }
      }
    }

    // ── (2) desliza o scroll até camTarget — SUAVE ────────────────────────
    const tgt = camTarget.current;
    if (!tgt) return;

    const maxX = Math.max(0, sa.scrollWidth  - viewW);
    const maxY = Math.max(0, sa.scrollHeight - viewH);
    const gx = Math.max(0, Math.min(maxX, tgt.x));
    const gy = Math.max(0, Math.min(maxY, tgt.y));

    const dx = gx - sa.scrollLeft;
    const dy = gy - sa.scrollTop;
    const dist = Math.hypot(dx, dy);

    if (dist < 0.6) {
      sa.scrollLeft = gx;
      sa.scrollTop  = gy;
      if (!followActive.current) camTarget.current = null;   // chegou e não está mais seguindo
      return;
    }

    const EASE = 0.055;   // fração da distância por frame → ease-out gentil
    const MAX_STEP = 11;  // px por frame (~660 px/s a 60fps) → nunca "rápido"
    let sx = dx * EASE;
    let sy = dy * EASE;
    const step = Math.hypot(sx, sy);
    if (step > MAX_STEP) { sx *= MAX_STEP / step; sy *= MAX_STEP / step; }
    sa.scrollLeft += sx;
    sa.scrollTop  += sy;
    bgDirty.current = true;   // a câmera panou → parallax acompanha
  }, []);

  // ── Animation tick ─────────────────────────────────────────────────────────
  const tick = useCallback((now: number) => {
    const mcfg = moveCfgRef.current;   // JSON: stepMs / easing / bobHeightPx / ...
    for (const [id, anim] of animTarget.current.entries()) {
      const segCount = anim.pts.length - 1;
      const totalMs  = segCount * anim.stepMs;
      const elapsed  = now - anim.t0;

      if (elapsed <= 0) {
        // ainda na fila (inimigos entram em cascata) — segura na origem
        const p0 = anim.pts[0];
        animPos.current.set(id, { x: p0.x, y: p0.y, bob: 0 });
        continue;
      }

      // Progresso ao longo do CAMINHO INTEIRO — o easing (partida/chegada
      // suaves) é aplicado UMA vez sobre o trajeto todo, não por célula.
      // Assim a velocidade fica constante no meio do percurso: o personagem
      // ANDA, em vez de acelerar-e-parar em cada casa (o "pulinho").
      const tPath = Math.min(elapsed / totalMs, 1);       // 0..1
      const eased = mcfg.easing === 'linear' ? tPath : smoothstep(tPath);
      const dist  = eased * segCount;                     // 0..segCount (em células)
      const seg   = Math.min(Math.floor(dist), segCount - 1);
      const frac  = dist - seg;                           // linear dentro do segmento
      const a = anim.pts[seg];
      const b = anim.pts[seg + 1];

      animPos.current.set(id, {
        x: a.x + (b.x - a.x) * frac,
        y: a.y + (b.y - a.y) * frac,
        // gingado contínuo de caminhada (2 toques por célula, bem sutil).
        // bobHeightPx = 0 → sem oscilação vertical (estilo 16-bit puro).
        bob: mcfg.bobHeightPx > 0 && tPath < 1
          ? Math.abs(Math.sin(dist * Math.PI * 2)) * mcfg.bobHeightPx
          : 0,
      });

      // encara a direção do passo atual (vira nas quinas do caminho)
      if (b.x !== a.x || b.y !== a.y) {
        entityDir.current.set(id, { dx: b.x - a.x, dy: b.y - a.y });
      }

      if (elapsed >= totalMs) {
        const last = anim.pts[anim.pts.length - 1];
        animPos.current.set(id, { x: last.x, y: last.y, bob: 0 });
        animTarget.current.delete(id);
      }
    }

    // Fauna ambiental data-driven — passo de simulação. Viewport = área
    // REALMENTE visível (não o canvas oversized), em coordenadas lógicas.
    const amb = ambientRef.current;
    if (amb) {
      const sa = scrollRef.current;
      const z = zoomRef.current || 1;
      const vw = (sa?.clientWidth ?? canvasRef.current?.width ?? 0) / z;
      const vh = (sa?.clientHeight ?? canvasRef.current?.height ?? 0) / z;
      amb.update(now, { width: vw, height: vh }, rotRef.current);
    }

    // ── Follow-cam: mantém o jogador enquadrado ──────────────────────────────
    followCam(now);

    // O plano de fundo parallax (gradientes + nuvens à deriva) é caro e muda
    // MUITO devagar — redesenha a ~20fps em vez de 60. Sempre redesenha logo
    // após girar/mover a câmera (bgDirty), pra não "arrastar".
    bgFrame.current++;
    if (bgDirty.current || bgFrame.current % 3 === 0) {
      drawBackground();
      bgDirty.current = false;
    }
    drawFrame();

    // Loop infinito para as animações rodarem constantemente a 60fps
    rafId.current = requestAnimationFrame(tick);
  }, [drawFrame, drawBackground, followCam]);

  // ── Per-sprite lazy loader ─────────────────────────────────────────────────
  const loadSprite = useCallback((name: string) => {
    if (spriteCache.current.has(name) || loadingSet.current.has(name)) return;
    loadingSet.current.add(name);

    const img = new Image();
    img.onload = () => {
      const oc = keyOutWhite(img);
      spriteCache.current.set(name, oc);
      loadingSet.current.delete(name);

      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(tick);
    };
    img.onerror = () => {
      loadingSet.current.delete(name);
      console.warn(`[Sprite] /sprites/${name}.png not found — falling back to circle.`);
    };
    // Cache buster: Forces the browser to load the latest sprite (resolves issues where updated sprites don't appear in combat).
    // Uses a daily hash (or session hash) in production, but here Date.now() per mount is fine since it's cached in spriteCache.current.
    img.src = `/sprites/${name}.png?v=${Date.now()}`;
  }, [tick]);

  // ── Per-NPC lazy loader (Hub) ───────────────────────────────────────────────
  // Arquivo já é o filename final servido em /sprites/<arquivo> (com extensão),
  // resolvido pelo backend a partir do painel de Sprites. Alfa real, sem
  // chroma-key. Sem fallback procedural — só um marcador mínimo enquanto não
  // carrega / se o vínculo não existir.
  const loadNpcImage = useCallback((file: string) => {
    if (!file || npcImageCache.current.has(file) || npcLoadingSet.current.has(file)) return;
    npcLoadingSet.current.add(file);

    const img = new Image();
    img.onload = () => {
      npcImageCache.current.set(file, img);
      npcLoadingSet.current.delete(file);
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(tick);
    };
    img.onerror = () => {
      npcLoadingSet.current.delete(file);
      console.warn(`[NPC] /sprites/${file} não encontrado — usando marcador mínimo.`);
    };
    img.src = `/sprites/${file}`;
  }, [tick]);

  useEffect(() => {
    for (const entity of Object.values(L.current.entities)) {
      if (entity.sprite) loadSprite(entity.sprite);
    }
  }, [entities, loadSprite]);

  // Carrega as imagens dos NPCs do Hub (Ferreiro etc.) — o filename vem do
  // backend em `npcSprites`, então depende dos dados do mapa/hub.
  useEffect(() => {
    for (const npc of Object.values(npcSprites ?? {})) {
      if (npc?.sprite) loadNpcImage(npc.sprite);
    }
  }, [npcSprites, loadNpcImage]);

  // ── Detect entity position changes → schedule walk animations ─────────────
  // Cada entidade que mudou de posição anda CASA A CASA pelo caminho: o do
  // servidor (`movements` — MovementEngine BFS) quando existe, senão um BFS
  // local. Nunca desliza direto da origem pro destino.
  useEffect(() => {
    const now = performance.now();
    const mcfg = moveCfgRef.current;   // JSON: stepMs / enemyCascade / fallback

    const pathById = new Map<string, Array<{ x: number; y: number }>>();
    for (const m of movements ?? []) {
      if (Array.isArray(m.path) && m.path.length) pathById.set(m.entity_id, m.path);
    }

    // células ocupadas por entidades vivas — o BFS local desvia delas
    const occupied = new Set<string>();
    for (const e of Object.values(entities)) {
      if (e.hp > 0) occupied.add(`${e.position_x},${e.position_y}`);
    }

    type MoveRec = { id: string; pts: Array<{ x: number; y: number }> };
    const playerMoves: MoveRec[] = [];
    const enemyMoves:  MoveRec[] = [];

    for (const [id, entity] of Object.entries(entities)) {
      const known = knownPos.current.get(id);
      const tx    = entity.position_x;
      const ty    = entity.position_y;

      if (!known) {
        animPos.current.set(id, { x: tx, y: ty, bob: 0 });
        knownPos.current.set(id, { x: tx, y: ty });
        continue;
      }
      if (known.x === tx && known.y === ty) continue;

      const cur = animPos.current.get(id) ?? { x: known.x, y: known.y };
      knownPos.current.set(id, { x: tx, y: ty });

      const sx = Math.round(cur.x);
      const sy = Math.round(cur.y);

      // 1) caminho do servidor · 2) BFS local · 3) reta (degrada com elegância)
      let pts: Array<{ x: number; y: number }>;
      const serverPath = pathById.get(id);
      if (serverPath) {
        pts = dedupePath([{ x: sx, y: sy }, ...serverPath]);
      } else if (mcfg.clientPathfindingFallback) {
        const own = `${entity.position_x},${entity.position_y}`;
        const blocked = new Set(occupied); blocked.delete(own); blocked.delete(`${sx},${sy}`);
        pts = bfsPath(sx, sy, tx, ty, map, blocked) ?? [{ x: sx, y: sy }, { x: tx, y: ty }];
      } else {
        pts = [{ x: sx, y: sy }, { x: tx, y: ty }];
      }
      const end = pts[pts.length - 1];
      if (end.x !== tx || end.y !== ty) pts.push({ x: tx, y: ty });
      if (pts.length < 2) continue;

      entityDir.current.set(id, { dx: pts[1].x - pts[0].x, dy: pts[1].y - pts[0].y });
      (entity.team === 'player' ? playerMoves : enemyMoves).push({ id, pts });

      // O jogador (myEntity) andou → libera a follow-cam a partir daqui.
      if (id === (L.current.myEntityId || '')) playerHasMoved.current = true;
    }

    const step = mcfg.stepMs;
    for (const { id, pts } of playerMoves) {
      animTarget.current.set(id, { pts, t0: now, stepMs: step });
    }

    // inimigos entram em cascata: um depois do outro, depois do jogador
    enemyMoves.sort((a, b) => a.id.localeCompare(b.id));
    let cursor = now + (playerMoves.length
      ? Math.max(...playerMoves.map(m => (m.pts.length - 1) * step)) + mcfg.enemyCascade.afterPlayerMs
      : 0);
    for (const { id, pts } of enemyMoves) {
      animTarget.current.set(id, { pts, t0: cursor, stepMs: step });
      cursor += (pts.length - 1) * step + mcfg.enemyCascade.betweenEnemiesMs;
    }

    for (const id of [...animPos.current.keys()]) {
      if (!entities[id]) {
        animPos.current.delete(id);
        animTarget.current.delete(id);
        knownPos.current.delete(id);
        entityDir.current.delete(id);
      }
    }

    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(tick);
  }, [entities, movements, map, tick]);

  // ── Redraw when props or rotation change ───────────────────────────────────
  useEffect(() => {
    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(tick);
  }, [map, highlights, cellHover, myEntityId, rotation, tick]);

  // ── Restart RAF when zoom changes ──────────────────────────────────────────
  useEffect(() => {
    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(tick);
  }, [zoom, tick]);

  // ── Zoom exclusivamente pelos botões da UI ────────────────────────────────
  // Zoom livre por scroll do mouse ou gestos de pinça foi desativado por
  // completo (quebrava a proporção visual do mapa). `zoomAnchorRef` ainda é
  // usado por `handleZoom` (botões +/-) para manter o centro da tela como
  // ponto de ancoragem ao aplicar o novo zoom — ver efeito abaixo.
  const zoomAnchorRef = useRef<{logicX: number, logicY: number, clientOffsetX: number, clientOffsetY: number} | null>(null);

  // ── Apply zoom anchor scrolling ────────────────────────────────────────────
  useLayoutEffect(() => {
    if (!scrollRef.current || !zoomAnchorRef.current) return;
    const { logicX, logicY, clientOffsetX, clientOffsetY } = zoomAnchorRef.current;
    
    const newMouseX = logicX * zoom;
    const newMouseY = logicY * zoom;

    scrollRef.current.scrollLeft = newMouseX - clientOffsetX;
    scrollRef.current.scrollTop = newMouseY - clientOffsetY;

    // Clear the anchor so it doesn't trigger unexpectedly
    zoomAnchorRef.current = null;
  }, [zoom]);

  // Identidade do mapa atual — muda quando entra num combate/mapa novo. Enquanto
  // for o mesmo mapa, os recentros (ao girar) seguem o jogador; no PRIMEIRO
  // enquadramento de um mapa novo (início da batalha) centraliza o MAPA INTEIRO
  // no monitor, não o jogador (que costuma nascer num canto).
  const centeredMapKey = useRef<string>('');
  const centeredRotRef = useRef<number>(NaN);

  // ── Centralizar scroll (mapa ao iniciar a batalha, jogador ao girar) ───────
  useEffect(() => {
    if (!scrollRef.current) return;

    // Defer para o próximo frame para garantir layout CSS e dimensões exatas na montagem
    const timer = setTimeout(() => {
      const scrollArea = scrollRef.current;
      if (!scrollArea) return;

      const viewW = scrollArea.clientWidth;
      const viewH = scrollArea.clientHeight;

      const mapKey = `${L.current.map.width}x${L.current.map.height}`;
      const firstFrameOfMap = centeredMapKey.current !== mapKey;
      const rotationChanged = centeredRotRef.current !== rotRef.current;
      centeredMapKey.current = mapKey;
      centeredRotRef.current = rotRef.current;

      // Mapa novo → volta ao enquadramento inicial e desliga a follow-cam até
      // o jogador dar o 1º passo.
      if (firstFrameOfMap) {
        playerHasMoved.current = false;
        followActive.current = false;
      }

      // Efeito dispara também quando `myEntityId` passa de null→id (jogador
      // carregou): nesse caso NÃO reposiciona — evita "puxar" a câmera logo
      // depois de enquadrar o mapa.
      if (!firstFrameOfMap && !rotationChanged) return;

      // No COMBATE (rotationMode 'free'), o 1º enquadramento centraliza a CENA
      // DA BATALHA — o centro de massa de TODOS os combatentes (jogador +
      // inimigos) — pra os dois lados aparecerem no monitor. (Centrar só no
      // "centro geométrico do mapa" jogava os inimigos, que nascem no topo,
      // pra fora da tela em mapas altos.) No Hub ('snap90') segue o jogador.
      const centerOnBattle = firstFrameOfMap && rotationMode !== 'snap90';

      // Pivô de Rotação: o centro REAL do mapa (grid) sempre projeta no
      // ponto fixo (offsetX, offsetY - TILE_H_HALF) na tela, em QUALQUER
      // ângulo. Fallback quando não há combatentes: centro geométrico do mapa.
      let targetX = L.current.offsetX * zoomRef.current;
      let targetY = (L.current.offsetY - TILE_H_HALF) * zoomRef.current;

      const projectEntity = (e: CombatEntity) => {
        const p = animPos.current.get(e.id) ?? { x: e.position_x, y: e.position_y };
        const [erx, ery] = rotatePoint(p.x + 0.5, p.y + 0.5, L.current.mapCx, L.current.mapCy, rotRef.current);
        return isoProject(erx, ery, L.current.offsetX, L.current.offsetY - TILE_H_HALF);
      };

      const playerEntity = L.current.entities[L.current.myEntityId || ''];
      const noEntities = Object.keys(L.current.entities).length === 0;

      if (noEntities) {
        // Preview Isométrico (admin) — sem combatentes: enquadra o MAPA INTEIRO,
        // centralizando na bounding box dos 4 cantos projetados (o pivô de
        // rotação sozinho fica ACIMA do terreno em mapas altos → tela "vazia").
        const mw = L.current.map.width, mh = L.current.map.height;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const [cxg, cyg] of [[0, 0], [mw, 0], [mw, mh], [0, mh]] as const) {
          const [rx, ry] = rotatePoint(cxg, cyg, L.current.mapCx, L.current.mapCy, rotRef.current);
          const [px, py] = isoProject(rx, ry, L.current.offsetX, L.current.offsetY - TILE_H_HALF);
          minX = Math.min(minX, px); maxX = Math.max(maxX, px);
          minY = Math.min(minY, py); maxY = Math.max(maxY, py);
        }
        targetX = ((minX + maxX) / 2) * zoomRef.current;
        targetY = ((minY + maxY) / 2) * zoomRef.current;
      } else if (centerOnBattle) {
        const combatants = Object.values(L.current.entities).filter(e => e.hp > 0);
        if (combatants.length) {
          let sx = 0, sy = 0;
          for (const e of combatants) { const [ex, ey] = projectEntity(e); sx += ex; sy += ey; }
          targetX = (sx / combatants.length) * zoomRef.current;
          targetY = (sy / combatants.length) * zoomRef.current;
        }
      } else if (playerEntity) {
        // Recentros posteriores (ao girar) e 1º frame do Hub: segue o jogador.
        const [px, py] = projectEntity(playerEntity);
        targetX = px * zoomRef.current;
        targetY = py * zoomRef.current;
      }

      const goalX = targetX - viewW / 2;
      const goalY = targetY - viewH / 2;

      if (firstFrameOfMap) {
        // Abertura de um mapa: corte seco (não há "slide" visível — a tela
        // ainda nem foi vista). Zera qualquer alvo pendente.
        scrollArea.scrollLeft = goalX;
        scrollArea.scrollTop = goalY;
        camTarget.current = null;
      } else {
        // Recentro ao GIRAR a câmera: desliza suave até o novo enquadramento
        // (o loop RAF cuida do ease-out).
        camTarget.current = { x: goalX, y: goalY };
      }
    }, 10);
    
    return () => clearTimeout(timer);
  }, [rotation, myEntityId, map.width, map.height]);

  // Girar / dar zoom muda o parallax de imediato — força um redesenho do fundo.
  useEffect(() => { bgDirty.current = true; }, [rotation, zoom]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => () => { cancelAnimationFrame(rafId.current); }, []);

  // ── Mouse handlers ─────────────────────────────────────────────────────────

  const isRightDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartRot = useRef(0);

  const isLeftDragging = useRef(false);
  const panStartX = useRef(0);
  const panStartY = useRef(0);
  const scrollStartX = useRef(0);
  const scrollStartY = useRef(0);
  const hasDragged = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (e.button === 2) {
      isRightDragging.current = true;
      dragStartX.current = e.clientX;
      dragStartRot.current = rotRef.current;
      e.preventDefault();
    } else if (e.button === 0 || e.button === 1) {
      isLeftDragging.current = true;
      panStartX.current = e.clientX;
      panStartY.current = e.clientY;
      scrollStartX.current = scrollRef.current?.scrollLeft || 0;
      scrollStartY.current = scrollRef.current?.scrollTop || 0;
      hasDragged.current = false;
      camTarget.current = null;   // o jogador assumiu a câmera
    }
  }, []);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (e.button === 2) {
      isRightDragging.current = false;
    } else if (e.button === 0 || e.button === 1) {
      isLeftDragging.current = false;
      // Se o jogador acabou de arrastar a câmera manualmente, segura a
      // follow-cam por ~1.4s pra não "puxar" de volta na cara dele.
      if (hasDragged.current) followSuspendTo.current = performance.now() + 1400;
    }
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (e.button !== 0) return; // Only left click for actions
      if (hasDragged.current) return; // Ignora o clique se o mapa foi arrastado
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const z = zoomRef.current;
      const rot = rotRef.current;
      
      const mouseX = (e.clientX - rect.left) / z;
      const mouseY = (e.clientY - rect.top)  / z;

      // ── Hit-test de alvos "altos" (billboard/arco) ANTES da projeção no chão ─
      // NPC e Portal são desenhados bem ACIMA do tile; um clique no corpo deles,
      // projetado direto no plano do chão, cai várias linhas atrás — e o
      // jogador "nunca clica no Ferreiro / no Portal". Testa o clique contra o
      // retângulo aproximado de cada um e dispara o clique naquele tile.
      const bsScale = L.current.npcSprites?.blacksmith?.scale ?? 1;
      const mcScale = L.current.npcSprites?.merchant?.scale ?? 1;
      const tallHalfW = TILE_W * 0.6;
      const tallBoxH: Record<string, number> = {
        npc_blacksmith: NPC_BLACKSMITH_DRAW_H * bsScale + 20,
        npc_merchant: NPC_BLACKSMITH_DRAW_H * mcScale + 20,
        npc_farmer: NPC_BLACKSMITH_DRAW_H * mcScale + 20,
        battle_point: 92, // arco (ph+lh) + placa
      };
      for (let ny = 0; ny < map.height; ny++) {
        for (let nx = 0; nx < map.width; nx++) {
          const boxH = tallBoxH[map.cells[ny]?.[nx]?.type ?? ''];
          if (!boxH) continue;
          const [nrx, nry] = rotatePoint(nx + 0.5, ny + 0.5, map.width / 2, map.height / 2, rot);
          const [nsx, nGroundY] = isoProject(nrx, nry, offsetX, offsetY);
          if (
            mouseX >= nsx - tallHalfW && mouseX <= nsx + tallHalfW &&
            mouseY >= nGroundY - boxH && mouseY <= nGroundY + 22
          ) {
            onCellClick(nx, ny);
            return;
          }
        }
      }

      // Reverse projection and rotation
      const [rx, ry] = reverseIso(mouseX, mouseY, offsetX, offsetY);
      const [lx, ly] = reverseRotate(rx, ry, map.width / 2, map.height / 2, rot);

      const gx = Math.floor(lx);
      const gy = Math.floor(ly);

      // Bloqueio Rigoroso de Clique: além dos limites da matriz, exige que a
      // célula realmente exista no array (grids esparsos podem ter buracos)
      // antes de disparar qualquer comando de movimento — clique no fundo
      // decorativo ou fora da grade jogável é sempre ignorado.
      const clickedCell = map.cells[gy]?.[gx];
      if (gx >= 0 && gx < map.width && gy >= 0 && gy < map.height && clickedCell !== undefined) {
        onCellClick(gx, gy);
      }
    },
    [offsetX, offsetY, map.width, map.height, map.cells, onCellClick],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isRightDragging.current) {
        const dx = e.clientX - dragStartX.current;

        if (rotationMode === 'snap90') {
          // A câmera só pode ocupar os 4 ângulos fixos (0°/90°/180°/270°).
          // Cada vez que o arrasto cruza o limiar de pixels, pulamos direto
          // para o próximo múltiplo de 90° — nunca exibindo um ângulo
          // intermediário — e reancoramos o ponto de partida para permitir
          // múltiplos passos num único arrasto contínuo.
          const STEP_PX = 70;
          if (Math.abs(dx) >= STEP_PX) {
            const steps = dx > 0 ? -1 : 1;
            const nextRot = normalizeAngle(dragStartRot.current + steps * HALF_PI);
            dragStartRot.current = nextRot;
            dragStartX.current   = e.clientX;
            setRotation(nextRot);
          }
          return;
        }

        // Modo livre (combate real): rotação contínua e suave, restrita ao
        // arco de -60°/+60° (ROT_LIMIT) — a câmera nunca chega a mostrar as
        // costas de um pilar de terreno, então o Sistema de Pilares só
        // precisa desenhar as 2 paredes da frente (ver `buildTerrainLayer`).
        // 100 pixels ≈ PI/2 radianos (90 graus)
        let newRot = dragStartRot.current - (dx / 100) * HALF_PI;
        newRot = clampRotation(newRot);
        setRotation(newRot);
        return;
      }

      if (isLeftDragging.current) {
        const dx = e.clientX - panStartX.current;
        const dy = e.clientY - panStartY.current;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
          hasDragged.current = true;
        }
        if (scrollRef.current) {
          scrollRef.current.scrollLeft = scrollStartX.current - dx;
          scrollRef.current.scrollTop = scrollStartY.current - dy;
          bgDirty.current = true;
        }
        return; // não processa hover durante o arrasto
      }
      
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const z = zoomRef.current;
      const rot = rotRef.current;
      
      const mouseX = (e.clientX - rect.left) / z;
      const mouseY = (e.clientY - rect.top)  / z;

      // Reverse projection and rotation
      const [rx, ry] = reverseIso(mouseX, mouseY, offsetX, offsetY);
      const [lx, ly] = reverseRotate(rx, ry, map.width / 2, map.height / 2, rot);
      
      const gx = Math.floor(lx);
      const gy = Math.floor(ly);

      const hoveredCell = map.cells[gy]?.[gx];
      if (gx >= 0 && gx < map.width && gy >= 0 && gy < map.height && hoveredCell !== undefined) {
        onCellHover(gx, gy);
      }
    },
    [offsetX, offsetY, map.width, map.height, map.cells, onCellHover, rotationMode],
  );

  const handleZoom = useCallback((delta: number) => {
    const scrollArea = scrollRef.current;
    if (!scrollArea) return;
    const oldZ = zoomRef.current;
    const newZ = parseFloat(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZ + delta)).toFixed(2));
    if (newZ !== oldZ) {
      const rect = scrollArea.getBoundingClientRect();
      // Use center of the scroll area as the anchor
      const clientOffsetX = rect.width / 2;
      const clientOffsetY = rect.height / 2;
      const mouseX = scrollArea.scrollLeft + clientOffsetX;
      const mouseY = scrollArea.scrollTop + clientOffsetY;
      
      zoomAnchorRef.current = {
        logicX: mouseX / oldZ,
        logicY: mouseY / oldZ,
        clientOffsetX,
        clientOffsetY
      };
      camTarget.current = null;   // scroll é reposicionado pelo âncora do zoom
      followActive.current = false;
      setZoom(newZ);
    }
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <canvas ref={bgCanvasRef} className={styles.bgCanvas} aria-hidden="true" />
      <div className={styles.groundingShadow} aria-hidden="true" />
      <div className={styles.scrollArea} ref={scrollRef}>
        <canvas
          ref={canvasRef}
          width={canvasW}
          height={canvasH}
          className={styles.canvas}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onContextMenu={handleContextMenu}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => {
             isRightDragging.current = false;
             isLeftDragging.current = false;
             onMouseLeave();
          }}
        />
      </div>

      {/* Sistema de Iluminação e Clima Variável — overlay decorativo, sempre
          pointer-events:none, nunca intercepta cliques/hover da grade real. */}
      <CanvasWeatherEffect weatherType={resolvedWeatherType} />

      {/* Bússola N/L/S/O — gira junto com a câmera */}
      <Compass rotation={rotation} north={resolvedNorth} />

      {/* Zoom and Rotate controls — floating overlay, bottom-right */}
      <div className={styles.zoomControls}>
        <button
          type="button"
          className={styles.zoomBtn}
          onClick={() => setRotation(r => rotationMode === 'snap90' ? normalizeAngle(r + HALF_PI) : clampRotation(r + HALF_PI))}
          title="Rotate Right 90°"
        >↻</button>
        <button
          type="button"
          className={styles.zoomBtn}
          onClick={() => setRotation(r => rotationMode === 'snap90' ? normalizeAngle(r - HALF_PI) : clampRotation(r - HALF_PI))}
          title="Rotate Left 90°"
        >↺</button>
        <div style={{width: 12, display: 'inline-block'}} />

        <button
          type="button"
          className={styles.zoomBtn}
          onClick={() => handleZoom(ZOOM_STEP)}
          disabled={zoom >= MAX_ZOOM}
          title="Zoom in"
        >＋</button>
        <span className={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          className={styles.zoomBtn}
          onClick={() => handleZoom(-ZOOM_STEP)}
          disabled={zoom <= MIN_ZOOM}
          title="Zoom out"
        >－</button>
      </div>

      {/* Turn timer overlaid inside the map wrapper */}
      {turnSecondsLeft !== undefined && turnSecondsLeft !== null && (
        <div className={`${styles.turnTimer} ${turnSecondsLeft <= 10 ? styles.turnTimerUrgent : ''}`}>
          ⏱ {turnSecondsLeft}s
        </div>
      )}
    </div>
  );
}
